export type ARWorkflowEventFamily = "business" | "platform";

export type ARWorkflowCallEventType =
  | "CallAttempted"
  | "CallCompleted"
  | "CallFailed"
  | "CallVoicemailLeft"
  | "CallOutcomeUpdated";

export type ARWorkflowNoteEventType =
  | "NoteCreated"
  | "NoteUpdated"
  | "CollectorNoteCreated"
  | "ManagerNoteCreated"
  | "CustomerNoteCreated";

export type ARWorkflowPromiseEventType =
  | "PromiseCreated"
  | "PromiseUpdated"
  | "PromiseCompleted"
  | "PromiseBroken"
  | "PromiseCancelled"
  | "PromiseEscalated"
  | "PromisePaymentReceived";

export type ARWorkflowTicklerEventType =
  | "TicklerCreated"
  | "TicklerUpdated"
  | "TicklerCompleted"
  | "TicklerRescheduled"
  | "TicklerCancelled"
  | "TicklerSnoozed"
  | "TicklerEscalated";

export type ARWorkflowDocumentEventType =
  | "LetterGenerated"
  | "LetterPreviewed"
  | "LetterPrinted"
  | "LetterDownloaded"
  | "LetterSent"
  | "LetterDelivered"
  | "LetterFailed"
  | "LetterArchived"
  | "StatementGenerated"
  | "StatementPreviewed"
  | "StatementPrinted"
  | "StatementDownloaded"
  | "StatementSent"
  | "StatementDelivered"
  | "StatementFailed"
  | "StatementArchived";

export type ARWorkflowEscalationEventType =
  | "EscalationCreated"
  | "EscalationUpdated"
  | "EscalationAssigned"
  | "EscalationResolved"
  | "EscalationClosed"
  | "EscalationReopened";

export type ARWorkflowPaymentEventType =
  | "PaymentRecorded"
  | "PaymentUpdated"
  | "PaymentVoided"
  | "PaymentAppliedToPromise"
  | "PaymentAppliedToAccount";

export type ARWorkflowReportEventType =
  | "ReportViewed"
  | "ReportFiltered"
  | "ReportExported"
  | "ReportDrillDownOpened";

export type ARWorkflowPortfolioIntakeEventType =
  | "ImportStarted"
  | "PortfolioCreated"
  | "ImportValidated"
  | "ImportFailed"
  | "ImportExecuting"
  | "ValidationCompleted"
  | "ValidationWarningDetected"
  | "ValidationErrorDetected"
  | "ImportMapped"
  | "MappingProfileCreated"
  | "MappingProfileUpdated"
  | "MappingWarningDetected"
  | "MappingErrorDetected"
  | "ApprovalCompleted"
  | "ApprovalRequired"
  | "RiskLevelCalculated"
  | "ImportApproved"
  | "ImportRejected"
  | "ImportCompleted"
  | "ImportRolledBack"
  | "PortfolioAssigned"
  | "PortfolioAssignmentUpdated"
  | "PortfolioAssignmentRemoved"
  | "PortfolioReadyForActivation"
  | "PortfolioActivationRequested"
  | "PortfolioActivationValidated"
  | "PortfolioActivated"
  | "PortfolioActivationFailed"
  | "PortfolioDeactivated"
  | "PortfolioActivationBlocked"
  | "PortfolioArchived";

export type ARWorkflowCommunicationEventType =
  | "EmailDrafted"
  | "EmailSent"
  | "EmailDelivered"
  | "EmailFailed"
  | "SMSSent"
  | "SMSDelivered"
  | "SMSFailed"
  | "PortalNotification"
  | "MicrosoftTeamsNotification"
  | "OutlookReminder"
  | "PhoneCallStarted"
  | "PhoneCallCompleted";

export type ARWorkflowTaskEventType =
  | "TaskCreated"
  | "TaskAssigned"
  | "TaskReassigned"
  | "TaskCompleted"
  | "TaskCancelled"
  | "TaskOverdue";

export type ARWorkflowDocumentRequestEventType =
  | "DocumentRequested"
  | "DocumentUploaded"
  | "DocumentApproved"
  | "DocumentRejected"
  | "DocumentExpired";

export type ARWorkflowManagerEventType =
  | "CollectorAssigned"
  | "CollectorRemoved"
  | "ManagerAssigned"
  | "PortfolioAssigned"
  | "PortfolioRemoved"
  | "TeamActivated"
  | "TeamCreated"
  | "TeamDeactivated"
  | "TeamUpdated"
  | "ApprovalGranted"
  | "ApprovalRejected"
  | "Reviewed"
  | "Escalated"
  | "CommentAdded";

export type ARWorkflowEventType =
  | ARWorkflowCallEventType
  | ARWorkflowNoteEventType
  | ARWorkflowPromiseEventType
  | ARWorkflowTicklerEventType
  | ARWorkflowDocumentEventType
  | ARWorkflowEscalationEventType
  | ARWorkflowPaymentEventType
  | ARWorkflowReportEventType
  | ARWorkflowPortfolioIntakeEventType
  | ARWorkflowCommunicationEventType
  | ARWorkflowTaskEventType
  | ARWorkflowDocumentRequestEventType
  | ARWorkflowManagerEventType;

export type ARWorkflowEventCategory =
  | "call"
  | "note"
  | "promise"
  | "tickler"
  | "document"
  | "document_request"
  | "communication"
  | "task"
  | "manager"
  | "escalation"
  | "payment"
  | "reporting"
  | "client_exchange";

export type ARWorkflowEntityType =
  | "client"
  | "portfolio"
  | "customer"
  | "account"
  | "invoice"
  | "promise"
  | "tickler"
  | "letter"
  | "statement"
  | "document"
  | "document_request"
  | "communication"
  | "task"
  | "manager_review"
  | "escalation"
  | "payment"
  | "report"
  | "import_batch"
  | "portfolio_assignment"
  | "portfolio_activation";

export type ARWorkflowEntityReference = {
  entityType: ARWorkflowEntityType;
  entityId: string;
};

export type ARWorkflowEvent = {
  id: string;
  tenantId: string;
  correlationId: string;
  parentEventId?: string;
  eventType: ARWorkflowEventType;
  eventFamily: ARWorkflowEventFamily;
  eventCategory: ARWorkflowEventCategory;
  entity?: ARWorkflowEntityReference;
  actor: {
    actorId: string;
    actorName: string;
    actorEmail: string;
    actorRole: string;
    permissionLevel: string;
  };
  occurredAt: string;
  workspace: {
    sourceWorkspace: string;
    ownerWorkspace: string;
    initiatedFromRoute?: string;
  };
  scope: {
    clientId?: string;
    customerId?: string;
    accountId?: string;
    promiseId?: string;
    ticklerId?: string;
    letterId?: string;
    statementId?: string;
    paymentId?: string;
    escalationId?: string;
    documentRequestId?: string;
    managerReviewId?: string;
  };
  payload: {
    title: string;
    summary: string;
    previousValue?: unknown;
    newValue?: unknown;
    metadata?: Record<string, unknown>;
  };
  metadata?: Record<string, unknown>;
  routing: {
    recoveryActivityRequired: boolean;
    timelineRequired: boolean;
    managerUpdateRequired: boolean;
    clientPortalProjectionRequired: boolean;
    notificationRequired: boolean;
    auditRequired: boolean;
    reportingRequired: boolean;
  };
  visibility: {
    internal: boolean;
    clientVisible: boolean;
    managerVisible: boolean;
    collectorVisible: boolean;
    systemVisible: boolean;
  };
};

export type ARWorkflowActorContext = {
  actorId?: string;
  actorName: string;
  actorEmail?: string;
  actorRole: string;
  permissionLevel: string;
};

export type ARWorkflowCallDirection = "outbound" | "inbound";

export type ARWorkflowCallEventInput = {
  tenantId: string;
  clientId?: string;
  customerId?: string;
  accountId: string;
  promiseId?: string;
  callDirection?: ARWorkflowCallDirection;
  phoneNumber?: string;
  collectorAssignment?: string;
  customerName: string;
  customerNumber: string;
  outcome: string;
  durationMinutes: string;
  notes: string;
  nextAction: string;
  actor: ARWorkflowActorContext;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
};

export type ARWorkflowNoteVisibility = "internal" | "manager_only" | "client_visible";

export type ARWorkflowNoteEventInput = {
  tenantId: string;
  clientId?: string;
  customerId?: string;
  accountId: string;
  promiseId?: string;
  ticklerId?: string;
  escalationId?: string;
  customerName: string;
  customerNumber: string;
  noteType: string;
  visibility: ARWorkflowNoteVisibility;
  content: string;
  outcome: string;
  nextAction: string;
  actor: ARWorkflowActorContext;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
  relatedRecordType?: "account" | "promise" | "tickler" | "manager";
  relatedRecordId?: string;
  previousValue?: unknown;
};

export type ARWorkflowPromiseAction =
  | "Promise.Create"
  | "Promise.Update"
  | "Promise.Complete"
  | "Promise.MarkBroken"
  | "Promise.Cancel"
  | "Promise.Escalate"
  | "Promise.PaymentReceived";

export type ARWorkflowPromiseEventInput = {
  tenantId: string;
  clientId?: string;
  customerId?: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  promiseId: string;
  promiseRecord: Record<string, unknown>;
  action: ARWorkflowPromiseAction;
  actor: ARWorkflowActorContext;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
  previousValue?: unknown;
};

export type ARWorkflowTicklerAction =
  | "Tickler.Create"
  | "Tickler.Update"
  | "Tickler.Complete"
  | "Tickler.Reschedule"
  | "Tickler.Cancel"
  | "Tickler.Snooze"
  | "Tickler.Escalate";

export type ARWorkflowTicklerEventInput = {
  tenantId: string;
  clientId?: string;
  customerId?: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  ticklerId: string;
  ticklerRecord: Record<string, unknown>;
  action: ARWorkflowTicklerAction;
  actor: ARWorkflowActorContext;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
  previousValue?: unknown;
};

export type ARWorkflowDocumentType = "letter" | "statement";

export type ARWorkflowDocumentAction =
  | "Letter.Generate"
  | "Letter.Preview"
  | "Letter.Print"
  | "Letter.Download"
  | "Letter.Send"
  | "Letter.Archive"
  | "Statement.Generate"
  | "Statement.Preview"
  | "Statement.Print"
  | "Statement.Download"
  | "Statement.Send"
  | "Statement.Archive";

export type ARWorkflowDocumentEventInput = {
  tenantId: string;
  clientId?: string;
  customerId?: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  documentId: string;
  documentType: ARWorkflowDocumentType;
  documentRecord: Record<string, unknown>;
  action: ARWorkflowDocumentAction;
  actor: ARWorkflowActorContext;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
  previousValue?: unknown;
  deliveryStatus?: "delivered" | "failed" | "not_configured" | "pending" | "sent";
};

export type ARWorkflowEscalationAction =
  | "Escalation.Create"
  | "Escalation.Update"
  | "Escalation.Assign"
  | "Escalation.Resolve"
  | "Escalation.Close"
  | "Escalation.Reopen";

export type ARWorkflowEscalationEventInput = {
  tenantId: string;
  clientId?: string;
  customerId?: string;
  accountId: string;
  promiseId?: string;
  ticklerId?: string;
  customerName: string;
  customerNumber: string;
  escalationId: string;
  escalationRecord: Record<string, unknown>;
  action: ARWorkflowEscalationAction;
  actor: ARWorkflowActorContext;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
  previousValue?: unknown;
};

export type ARWorkflowPaymentAction =
  | "Payment.Record"
  | "Payment.Update"
  | "Payment.Void"
  | "Payment.ApplyToPromise"
  | "Payment.ApplyToAccount";

export type ARWorkflowPaymentEventInput = {
  tenantId: string;
  clientId?: string;
  customerId?: string;
  accountId: string;
  promiseId?: string;
  customerName: string;
  customerNumber: string;
  paymentId: string;
  paymentRecord: Record<string, unknown>;
  action: ARWorkflowPaymentAction;
  actor: ARWorkflowActorContext;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
  previousValue?: unknown;
};

export type ARWorkflowReportAction = "Report.View" | "Report.Filter" | "Report.Export" | "Report.DrillDown";

export type ARReportingExportFormat = "CSV" | "Excel" | "PDF" | "Future API";

export type ARReportingDrillDownTarget =
  | "Collector KPI"
  | "Broken Promise KPI"
  | "Escalation KPI"
  | "Customer KPI"
  | "Client KPI"
  | "Payment KPI"
  | "Letter KPI"
  | "Statement KPI";

export type ARWorkflowReportingFilter = {
  collector?: string;
  manager?: string;
  clientId?: string;
  portfolioId?: string;
  customerId?: string;
  accountId?: string;
  promiseStatus?: string;
  ticklerStatus?: string;
  dateRange?: {
    from?: string;
    to?: string;
  };
  balanceRange?: {
    minCents?: number;
    maxCents?: number;
  };
  agingBucket?: string;
  priority?: string;
  assignment?: string;
};

export type ARWorkflowReportEventInput = {
  tenantId: string;
  clientId?: string;
  customerId?: string;
  accountId?: string;
  actor: ARWorkflowActorContext;
  reportId: string;
  reportName: string;
  action: ARWorkflowReportAction;
  filters?: ARWorkflowReportingFilter;
  exportFormat?: ARReportingExportFormat;
  drillDownTarget?: ARReportingDrillDownTarget;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
};

export type ARWorkflowOperationalEventInput = {
  tenantId: string;
  clientId?: string;
  customerId?: string;
  accountId?: string;
  promiseId?: string;
  ticklerId?: string;
  letterId?: string;
  statementId?: string;
  paymentId?: string;
  escalationId?: string;
  documentRequestId?: string;
  managerReviewId?: string;
  entityId: string;
  entityType: ARWorkflowEntityType;
  customerName?: string;
  customerNumber?: string;
  actor: ARWorkflowActorContext;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
  parentEventId?: string;
  title?: string;
  summary?: string;
  previousValue?: unknown;
  newValue?: unknown;
  metadata?: Record<string, unknown>;
};

export type ARWorkflowCommunicationEventInput = ARWorkflowOperationalEventInput & {
  eventType: ARWorkflowCommunicationEventType;
  channel?: ARNotificationDeliveryChannel;
  recipient?: string;
};

export type ARWorkflowTaskEventInput = ARWorkflowOperationalEventInput & {
  eventType: ARWorkflowTaskEventType;
  assignedTo?: string;
  dueAt?: string;
};

export type ARWorkflowDocumentRequestEventInput = ARWorkflowOperationalEventInput & {
  eventType: ARWorkflowDocumentRequestEventType;
  documentRequestType?: string;
};

export type ARWorkflowManagerEventInput = ARWorkflowOperationalEventInput & {
  eventType: ARWorkflowManagerEventType;
  managerAction?: string;
};

export type AREnterpriseWorkflowOrchestrationCategory =
  | "Recovery"
  | "Communications"
  | "Documents"
  | "Assignments"
  | "Payments"
  | "Escalations"
  | "Notifications"
  | "Manager Operations"
  | "Client Operations"
  | "Portfolio Operations"
  | "Compliance"
  | "Audit";

export type AREnterpriseWorkflowStatus = "Pending" | "Evaluating" | "Skipped" | "Executing" | "Completed" | "Failed" | "Blocked";

export type AREnterpriseWorkflowRuleStatus = "Active" | "Inactive" | "Draft";

export type AREnterpriseWorkflowConditionOperator =
  | "equals"
  | "not_equals"
  | "in"
  | "not_in"
  | "exists"
  | "not_exists"
  | "greater_than"
  | "greater_than_or_equal"
  | "less_than"
  | "less_than_or_equal";

export type AREnterpriseWorkflowCondition = {
  fieldPath: string;
  operator: AREnterpriseWorkflowConditionOperator;
  value?: unknown;
};

export type AREnterpriseWorkflowActionKind =
  | "PublishCommunicationEvent"
  | "PublishTaskEvent"
  | "PublishDocumentRequestEvent"
  | "PublishManagerEvent"
  | "PublishRecoveryEvent"
  | "PublishNotificationEvent";

export type AREnterpriseWorkflowActionDefinition = {
  actionId: string;
  actionKind: AREnterpriseWorkflowActionKind;
  resultEventType: ARWorkflowCommunicationEventType | ARWorkflowTaskEventType | ARWorkflowDocumentRequestEventType | ARWorkflowManagerEventType | ARWorkflowNoteEventType;
  entityType?: ARWorkflowEntityType;
  entityIdPath?: string;
  title?: string;
  summary?: string;
  metadata?: Record<string, unknown>;
};

export type AREnterpriseWorkflowRuleDefinition = {
  ruleId: string;
  ruleName: string;
  category: AREnterpriseWorkflowOrchestrationCategory;
  status: AREnterpriseWorkflowRuleStatus;
  triggerEventTypes: ARWorkflowEventType[];
  conditions: AREnterpriseWorkflowCondition[];
  actions: AREnterpriseWorkflowActionDefinition[];
  maxExecutionsPerCorrelation?: number;
  allowRecursiveExecution?: boolean;
  metadata?: Record<string, unknown>;
};

export type AREnterpriseWorkflowExecutionRecord = {
  workflowId: string;
  ruleId: string;
  triggerEventId: string;
  resultEventIds: string[];
  workflowStatus: AREnterpriseWorkflowStatus;
  startedAt: string;
  completedAt?: string;
  failedAt?: string;
  retryCount: number;
  user: ARWorkflowActorContext;
  source: string;
  correlationId: string;
  parentWorkflowId?: string;
  metadata: Record<string, unknown>;
};

export type AREnterpriseWorkflowOrchestrationResult = {
  engine: "enterprise_workflow_orchestration_engine";
  workflowId: string;
  status: AREnterpriseWorkflowStatus;
  triggerEvent: ARWorkflowEvent;
  matchedRules: AREnterpriseWorkflowRuleDefinition[];
  skippedRules: Array<{ ruleId: string; reason: string }>;
  executionRecords: AREnterpriseWorkflowExecutionRecord[];
  resultEvents: ARWorkflowEvent[];
  workflowEvents: ARWorkflowEvent[];
  notificationRecords: ARWorkflowNotificationProjection[];
  reportingProjection: ARWorkflowReportingProjection;
  dataIntegrity: AREnterpriseWorkflowIntegrityAssessment;
  consumerTargets: typeof AR_WORKFLOW_EVENT_CONSUMER_TARGETS;
};

export type AREnterpriseWorkflowIntegrityAssessment = {
  duplicateWorkflowExecution: boolean;
  duplicateEventPublishing: boolean;
  circularWorkflowDetected: boolean;
  orphanWorkflowChains: boolean;
  brokenRelationships: boolean;
  inconsistentWorkflowState: boolean;
  issues: string[];
};

export type AREnterpriseWorkflowOrchestrationInput = {
  triggerEvent: ARWorkflowEvent;
  rules?: AREnterpriseWorkflowRuleDefinition[];
  existingEvents?: ARWorkflowEvent[];
  actor?: ARWorkflowActorContext;
  source?: string;
  parentWorkflowId?: string;
  retryCount?: number;
  publish?: boolean;
};

export type PortfolioImportStatus =
  | "Draft"
  | "Received"
  | "Validating"
  | "Validation Failed"
  | "Ready For Review"
  | "Approved"
  | "Importing"
  | "Completed"
  | "Failed"
  | "Rolled Back"
  | "Archived";

export type PortfolioSourceType = "Manual" | "Excel" | "CSV" | "Future API" | "Future ERP Sync" | "Future Secure Upload";

export type PortfolioValidationSeverity = "Error" | "Warning" | "Information";

export type PortfolioValidationCategory =
  | "Required fields"
  | "Duplicate customers"
  | "Duplicate accounts"
  | "Duplicate invoices"
  | "Missing account numbers"
  | "Missing customer names"
  | "Invalid balances"
  | "Negative balances"
  | "Invalid dates"
  | "Invalid aging buckets"
  | "Unsupported statuses"
  | "Malformed email"
  | "Malformed phone"
  | "Orphaned invoices"
  | "Orphaned accounts"
  | "Unknown collectors"
  | "Unknown managers"
  | "Invalid portfolio metadata";

export type PortfolioValidationSummary = {
  totalRecords: number;
  validRecords: number;
  errorCount: number;
  warningCount: number;
  informationCount: number;
  duplicateCount: number;
  customerCount: number;
  accountCount: number;
  invoiceCount: number;
  importReadiness: PortfolioImportReadiness;
  overallValidationStatus: PortfolioOverallValidationStatus;
  isImportReady: boolean;
};

export type PortfolioImportReadiness = "Ready" | "Ready With Warnings" | "Blocked";

export type PortfolioOverallValidationStatus = "Passed" | "Passed With Warnings" | "Failed";

export type PortfolioValidationResult = {
  validationId: string;
  batchId: string;
  tenantId: string;
  clientId: string;
  portfolioId: string;
  severity: PortfolioValidationSeverity;
  category: PortfolioValidationCategory;
  message: string;
  rowNumber?: number;
  fieldName?: string;
  sourceValue?: unknown;
  createdAt: string;
};

export type PortfolioColumnMapping = {
  sourceColumn: string;
  targetField: string;
  required: boolean;
  transform?: string;
};

export type PortfolioMappingIssue = {
  issueId: string;
  severity: "Error" | "Warning" | "Information";
  sourceColumn?: string;
  targetField?: string;
  message: string;
};

export type PortfolioMappingReadiness = "Ready" | "Ready With Warnings" | "Blocked";

export type PortfolioMappingSuggestion = {
  sourceColumn: string;
  suggestedTargetField: string;
  confidence: "deterministic";
};

export type PortfolioMappingProfile = {
  profileId: string;
  mappingProfileId?: string;
  tenantId?: string;
  clientId: string;
  profileName: string;
  sourceFormat: PortfolioSourceType;
  sourceColumns?: string[];
  columnMappings: PortfolioColumnMapping[];
  requiredFieldsMapped?: boolean;
  unmappedFields?: string[];
  createdBy: string;
  updatedBy: string;
  activeStatus: "Active" | "Inactive";
  createdAt: string;
  updatedAt: string;
};

export type PortfolioMappingEngineProjectionTarget = {
  target: "Validation Engine" | "Review & Approval Engine" | "Import Engine" | "Reporting" | "Recovery Activity" | "Timeline";
  projectionStatus: "Prepared";
  description: string;
};

export type PortfolioMappingEngineResult = {
  engine: "PortfolioMappingEngine";
  profileBuilder: "MappingProfileBuilder";
  requiredFieldDetectionService: "RequiredFieldDetectionService";
  unmappedFieldDetectionService: "UnmappedFieldDetectionService";
  readinessEvaluator: "MappingReadinessEvaluator";
  defaultSuggestionService: "DefaultMappingSuggestionService";
  mappingProfile: PortfolioMappingProfile;
  missingRequiredFields: string[];
  unmappedFields: string[];
  unsupportedSourceColumns: string[];
  duplicateTargetMappings: string[];
  invalidTargetFields: string[];
  suggestions: PortfolioMappingSuggestion[];
  readiness: PortfolioMappingReadiness;
  issues: PortfolioMappingIssue[];
  projectionTargets: PortfolioMappingEngineProjectionTarget[];
};

export type PortfolioApprovalStatus = "Pending" | "Approved" | "Rejected";
export type PortfolioImportRiskLevel = "Low" | "Medium" | "High" | "Critical";
export type PortfolioApprovalReadiness = "Ready" | "Ready With Warnings" | "Blocked";

export type PortfolioApprovalRecord = {
  approvalId: string;
  batchId: string;
  tenantId: string;
  correlationId?: string;
  clientId: string;
  portfolioId: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  approvalNotes?: string;
  approvalStatus: PortfolioApprovalStatus;
  riskLevel?: PortfolioImportRiskLevel;
};

export type PortfolioMappingSummary = {
  readiness: PortfolioMappingReadiness;
  requiredFieldsMapped: boolean;
  missingRequiredFields: string[];
  unmappedFields: string[];
  warningCount: number;
  errorCount: number;
};

export type PortfolioReviewSummary = {
  client: string;
  portfolioName: string;
  batchId: string;
  submittedBy: string;
  submittedAt: string;
  sourceType: PortfolioSourceType;
  sourceFile: string;
  recordCount: number;
  customerCount: number;
  accountCount: number;
  invoiceCount: number;
  totalBalance: number;
  validationSummary: PortfolioValidationSummary;
  mappingSummary: PortfolioMappingSummary;
  assignmentReadiness: PortfolioApprovalReadiness;
  approvalStatus: PortfolioApprovalStatus;
  importReadiness: PortfolioApprovalReadiness;
  importRiskLevel: PortfolioImportRiskLevel;
};

export type PortfolioReviewEngineProjectionTarget = {
  target: "Import Engine" | "Reporting" | "Recovery Activity" | "Timeline" | "Audit";
  projectionStatus: "Prepared";
  description: string;
};

export type PortfolioReviewEngineResult = {
  engine: "PortfolioReviewEngine";
  approvalEngine: "PortfolioApprovalEngine";
  readinessEvaluator: "ApprovalReadinessEvaluator";
  riskAssessmentService: "ImportRiskAssessmentService";
  summaryBuilder: "ApprovalSummaryBuilder";
  batchId: string;
  tenantId: string;
  clientId: string;
  portfolioId: string;
  reviewSummary: PortfolioReviewSummary;
  approvalRecord?: PortfolioApprovalRecord;
  projectionTargets: PortfolioReviewEngineProjectionTarget[];
};

export type PortfolioImportLifecycleStatus = "Approved" | "Importing" | "Completed" | "Failed" | "Rolled Back" | "Archived";
export type PortfolioActivationStatus = "Pending Activation" | "Activated" | "Archived";
export type PortfolioAssignmentStatus = "Ready" | "Ready With Warnings" | "Blocked";
export type PortfolioWorkQueueEligibility = "Eligible" | "Not Eligible" | "Pending Assignment" | "Pending Activation" | "Blocked";
export type PortfolioOperationalActivationStatus = "Pending Activation" | "Ready For Activation" | "Activating" | "Activated" | "Activation Failed" | "Archived";
export type PortfolioActivationAction = "Portfolio.Activate" | "Portfolio.Deactivate" | "Portfolio.Archive";

export type PortfolioDuplicateSummary = {
  duplicateCustomers: number;
  duplicateAccounts: number;
  duplicateInvoices: number;
  duplicateImportBatches: number;
  duplicatePortfolioIds: number;
  duplicateRecords: number;
  duplicateKeys: string[];
};

export type PortfolioImportExecutionRecord = {
  importId: string;
  batchId: string;
  tenantId: string;
  correlationId: string;
  clientId: string;
  portfolioId: string;
  startedBy: string;
  startedAt: string;
  completedAt?: string;
  status: PortfolioImportLifecycleStatus;
  processedRecords: number;
  successfulRecords: number;
  failedRecords: number;
  duplicateRecords: number;
  rollbackAvailable: boolean;
  rollbackId?: string;
  activationStatus: PortfolioActivationStatus;
};

export type PortfolioRollbackMetadata = {
  rollbackId: string;
  batchId: string;
  rollbackReason: string;
  rollbackRequestedBy: string;
  rollbackRequestedAt: string;
  rollbackCompletedAt?: string;
  rollbackStatus: "Requested" | "Metadata Prepared" | "Completed" | "Failed";
};

export type PortfolioImportHistoryEntry = {
  importId: string;
  client: string;
  portfolio: string;
  batch: string;
  source: PortfolioSourceType;
  startedBy: string;
  completedBy?: string;
  startedAt: string;
  completedAt?: string;
  status: PortfolioImportLifecycleStatus;
  recordCounts: {
    processedRecords: number;
    successfulRecords: number;
    failedRecords: number;
    duplicateRecords: number;
  };
  durationMs?: number;
  rollbackAvailable: boolean;
};

export type PortfolioImportProjectionTarget = {
  target: "Customers & Accounts" | "Recovery Workbench" | "Manager Command" | "Recovery Activity" | "Timeline" | "Reporting";
  projectionStatus: "Prepared";
  description: string;
};

export type PortfolioImportEngineResult = {
  engine: "PortfolioImportEngine";
  executionEngine: "ImportExecutionEngine";
  historyService: "ImportHistoryService";
  rollbackService: "ImportRollbackService";
  duplicateProtectionService: "DuplicateProtectionService";
  summaryBuilder: "ImportExecutionSummaryBuilder";
  execution: PortfolioImportExecutionRecord;
  history: PortfolioImportHistoryEntry;
  duplicateSummary: PortfolioDuplicateSummary;
  rollbackMetadata?: PortfolioRollbackMetadata;
  projectionTargets: PortfolioImportProjectionTarget[];
};

export type PortfolioAssignmentRecord = {
  assignmentId: string;
  tenantId: string;
  correlationId: string;
  clientId: string;
  portfolioId: string;
  collectorId?: string;
  managerId?: string;
  teamId?: string;
  priority?: string;
  recoveryStage?: string;
  workQueueEligible: boolean;
  assignedBy: string;
  assignedAt: string;
  effectiveDate: string;
  status: PortfolioAssignmentStatus;
};

export type PortfolioAssignmentValidationResult = {
  issueId: string;
  severity: "Error" | "Warning" | "Information";
  category: string;
  message: string;
};

export type PortfolioAssignmentSummary = {
  portfolio: string;
  collector: string;
  manager: string;
  team: string;
  priority: string;
  recoveryStage: string;
  assignedRecordCount: number;
  assignmentCompletionPercent: number;
  workQueueEligibility: PortfolioWorkQueueEligibility;
  activationReadiness: PortfolioAssignmentStatus;
  outstandingAssignmentIssues: string[];
};

export type PortfolioAssignmentProjectionTarget = {
  target: "Recovery Workbench" | "Customers & Accounts" | "Manager Command" | "Recovery Activity" | "Timeline" | "Reporting";
  projectionStatus: "Prepared";
  description: string;
};

export type PortfolioAssignmentEngineResult = {
  engine: "PortfolioAssignmentEngine";
  executionEngine: "AssignmentExecutionEngine";
  validationService: "AssignmentValidationService";
  workQueueEligibilityService: "WorkQueueEligibilityService";
  activationReadinessEvaluator: "ActivationReadinessEvaluator";
  summaryBuilder: "AssignmentSummaryBuilder";
  assignment: PortfolioAssignmentRecord;
  validationResults: PortfolioAssignmentValidationResult[];
  assignmentSummary: PortfolioAssignmentSummary;
  projectionTargets: PortfolioAssignmentProjectionTarget[];
};

export type PortfolioOperationalActivationValidationResult = {
  issueId: string;
  severity: "Error" | "Warning" | "Information";
  category: string;
  message: string;
};

export type PortfolioOperationalActivationRecord = {
  activationId: string;
  tenantId: string;
  correlationId: string;
  clientId: string;
  portfolioId: string;
  action: PortfolioActivationAction;
  requestedBy: string;
  requestedAt: string;
  activatedBy?: string;
  activatedAt?: string;
  deactivatedBy?: string;
  deactivatedAt?: string;
  archivedBy?: string;
  archivedAt?: string;
  status: PortfolioOperationalActivationStatus;
};

export type PortfolioOperationalActivationSummary = {
  portfolio: string;
  client: string;
  activationStatus: PortfolioOperationalActivationStatus;
  activationTime?: string;
  activatedBy?: string;
  eligibleRecordCount: number;
  blockedRecordCount: number;
  projectionStatus: "Prepared";
  activationResult: "Activated" | "Blocked" | "Deactivated" | "Archived";
};

export type PortfolioActivationProjectionTarget = {
  target: "Recovery Workbench" | "Customers & Accounts" | "Manager Command" | "Recovery Activity" | "Timeline" | "Reporting";
  projectionStatus: "Prepared";
  description: string;
};

export type PortfolioActivationEngineResult = {
  engine: "PortfolioActivationEngine";
  executionEngine: "ActivationExecutionEngine";
  eligibilityService: "ActivationEligibilityService";
  validator: "OperationalActivationValidator";
  summaryBuilder: "ActivationSummaryBuilder";
  action: PortfolioActivationAction;
  activation: PortfolioOperationalActivationRecord;
  validationResults: PortfolioOperationalActivationValidationResult[];
  activationSummary: PortfolioOperationalActivationSummary;
  projectionTargets: PortfolioActivationProjectionTarget[];
};

export type PortfolioPipelineWalkthroughStage = {
  stage: "Intake" | "Validation" | "Mapping" | "Review" | "Approval" | "Import" | "Assignment" | "Ready For Activation" | "Activation";
  status: "Passed" | "Failed";
  detail: string;
};

export type PortfolioPipelineFailurePathResult = {
  scenario: "Validation failure" | "Mapping failure" | "Approval rejection" | "Import failure" | "Assignment blocked" | "Activation blocked";
  status: "Passed" | "Failed";
  blockedAt: string;
  eventTypes: ARWorkflowPortfolioIntakeEventType[];
  detail: string;
};

export type PortfolioPipelineProjectionVerification = {
  target: "Recovery Workbench" | "Customers & Accounts" | "Manager Command" | "Recovery Activity" | "Timeline" | "Reporting";
  projectionStatus: "Prepared" | "Missing";
};

export type PortfolioPipelineWalkthroughResult = {
  verifier: "enterprise_portfolio_intake_walkthrough";
  tenantId: string;
  correlationId: string;
  overallStatus: "READY FOR PILOT CLIENTS" | "READY FOR NEXT IMPLEMENTATION PHASE";
  stages: PortfolioPipelineWalkthroughStage[];
  failurePaths: PortfolioPipelineFailurePathResult[];
  expectedEventsVerified: ARWorkflowPortfolioIntakeEventType[];
  missingEvents: ARWorkflowPortfolioIntakeEventType[];
  eventEnvelopeVerified: boolean;
  eventEnvelopeIssues: string[];
  projectionVerification: PortfolioPipelineProjectionVerification[];
  operationalReadinessVerified: boolean;
  notes: string[];
};

export type PortfolioAssignmentPlan = {
  assignmentPlanId: string;
  batchId: string;
  tenantId: string;
  clientId: string;
  portfolioId: string;
  collector?: string;
  manager?: string;
  team?: string;
  priority?: string;
  portfolio?: string;
  recoveryStage?: string;
  workQueueEligibility: boolean;
  createdBy: string;
  createdAt: string;
};

export type PortfolioStagingRecord = {
  stagingRecordId: string;
  batchId: string;
  tenantId: string;
  clientId: string;
  portfolioId: string;
  rowNumber: number;
  recordType: "Customer" | "Account" | "Invoice" | "Balance" | "Supporting Document" | "Portfolio Metadata";
  rawData: Record<string, unknown>;
  mappedData: Record<string, unknown>;
  validationStatus: "Not Validated" | "Valid" | "Warning" | "Error";
  createdAt: string;
};

export type PortfolioIntakeBatch = {
  batchId: string;
  tenantId: string;
  correlationId: string;
  clientId: string;
  portfolioId: string;
  portfolioName: string;
  sourceType: PortfolioSourceType;
  sourceFileName: string;
  submittedBy: string;
  submittedAt: string;
  recordCount: number;
  status: PortfolioImportStatus;
  validationSummary: PortfolioValidationSummary;
  mappingProfileId?: string;
  approvalStatus: PortfolioApprovalStatus;
};

export type PortfolioProjectionTarget = {
  target: "Customers & Accounts" | "Recovery Workbench" | "Manager Command" | "Recovery Activity" | "Timeline" | "Reporting";
  projectionStatus: "Prepared" | "Future Projection";
  description: string;
};

export type EnterpriseClientExchangeFoundationProjection = {
  engine: "enterprise_client_exchange";
  lifecycle: PortfolioImportStatus[];
  validationCategories: PortfolioValidationCategory[];
  projectionTargets: PortfolioProjectionTarget[];
  batches: PortfolioIntakeBatch[];
  stagingRecords: PortfolioStagingRecord[];
  validationResults: PortfolioValidationResult[];
  mappingProfiles: PortfolioMappingProfile[];
  approvalRecords: PortfolioApprovalRecord[];
  assignmentPlans: PortfolioAssignmentPlan[];
};

export type PortfolioValidationEngineProjectionTarget = {
  target: "Review & Approval Engine" | "Import Engine" | "Reporting" | "Recovery Activity" | "Timeline";
  projectionStatus: "Prepared";
  description: string;
};

export type PortfolioValidationEngineResult = {
  engine: "PortfolioValidationEngine";
  summaryBuilder: "ValidationSummaryBuilder";
  duplicateService: "DuplicateDetectionService";
  readinessEvaluator: "ImportReadinessEvaluator";
  batchId: string;
  tenantId: string;
  clientId: string;
  portfolioId: string;
  validationSummary: PortfolioValidationSummary;
  validationResults: PortfolioValidationResult[];
  projectionTargets: PortfolioValidationEngineProjectionTarget[];
};

export type ARWorkflowPortfolioIntakeEventInput = {
  tenantId: string;
  clientId: string;
  portfolioId: string;
  batchId: string;
  portfolioName: string;
  actor: ARWorkflowActorContext;
  eventType: ARWorkflowPortfolioIntakeEventType;
  batch?: PortfolioIntakeBatch;
  stagingRecords?: PortfolioStagingRecord[];
  validationResults?: PortfolioValidationResult[];
  mappingProfile?: PortfolioMappingProfile;
  approvalRecord?: PortfolioApprovalRecord;
  reviewSummary?: PortfolioReviewSummary;
  importExecution?: PortfolioImportExecutionRecord;
  importHistory?: PortfolioImportHistoryEntry;
  duplicateSummary?: PortfolioDuplicateSummary;
  rollbackMetadata?: PortfolioRollbackMetadata;
  assignmentRecord?: PortfolioAssignmentRecord;
  assignmentSummary?: PortfolioAssignmentSummary;
  activationRecord?: PortfolioOperationalActivationRecord;
  activationSummary?: PortfolioOperationalActivationSummary;
  assignmentPlan?: PortfolioAssignmentPlan;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
};

export type ARWorkflowCallProjection = {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  collector: string;
  outcome: string;
  durationMinutes: string;
  notes: string;
  nextAction: string;
  createdAt: string;
};

export type ARWorkflowNoteProjection = {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  collector: string;
  noteType: string;
  visibility: ARWorkflowNoteVisibility;
  content: string;
  outcome: string;
  nextAction: string;
  createdAt: string;
};

export type ARWorkflowPromiseProjection = Record<string, unknown> & {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  status: string;
  updatedAt: string;
};

export type ARWorkflowTicklerProjection = Record<string, unknown> & {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  status: string;
  dueDate: string;
  updatedAt: string;
};

export type ARWorkflowDocumentProjection = Record<string, unknown> & {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  status: string;
  deliveryStatus: string;
  updatedAt: string;
  generatedAt: string;
};

export type ARWorkflowEscalationProjection = Record<string, unknown> & {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  status: string;
  priority: string;
  reason: string;
  assignedTo: string;
  updatedAt: string;
  createdAt: string;
};

export type ARWorkflowPaymentProjection = Record<string, unknown> & {
  id: string;
  companyId: string;
  accountId: string;
  customerName: string;
  customerNumber: string;
  amountCents: number;
  status: string;
  lifecycleStatus: string;
  paymentMethod: string;
  applicationScope: string;
  externalPaymentReference?: string;
  paymentDate?: string;
  updatedAt: string;
  recordedAt: string;
};

export type ARNotificationDeliveryChannel = "in_app" | "email" | "sms" | "client_portal" | "teams" | "slack" | "webhook";
export type ARNotificationLifecycleStatus = "Created" | "Queued" | "Delivered" | "Read" | "Dismissed" | "Archived";
export type ARNotificationPriority = "low" | "medium" | "high" | "critical";
export type ARNotificationVisibility = "internal" | "manager_only" | "collector" | "administrator" | "client_future";

export type ARWorkflowNotificationProjection = {
  notificationId: string;
  tenantId: string;
  correlationId: string;
  workflowEventId: string;
  notificationType: string;
  recipient: string;
  recipientRole: string;
  deliveryChannel: ARNotificationDeliveryChannel;
  supportedDeliveryChannels: ARNotificationDeliveryChannel[];
  status: ARNotificationLifecycleStatus;
  createdAt: string;
  scheduledFor?: string;
  deliveredAt?: string;
  readAt?: string;
  dismissedAt?: string;
  priority: ARNotificationPriority;
  visibility: ARNotificationVisibility;
  title: string;
  message: string;
  sourceWorkspace: string;
  targetWorkspace: string;
  accountId?: string;
  customerName?: string;
  metadata: Record<string, unknown>;
};

export type ARReportingMetricProjection = {
  id: string;
  label: string;
  value: number;
  amountCents?: number;
  trend: "up" | "down" | "flat";
  sourceEventTypes: ARWorkflowEventType[];
  drillDownTarget?: ARReportingDrillDownTarget;
  drillDownRoute?: string;
};

export type ARReportingPerformanceProjection = {
  projectionId: string;
  projectionType:
    | "Collector Performance"
    | "Manager Performance"
    | "Client Performance"
    | "Portfolio Performance"
    | "Recovery Performance"
    | "Promise Performance"
    | "Tickler Performance"
    | "Letter Performance"
    | "Statement Performance"
    | "Payment Performance"
    | "Escalation Performance"
    | "Assignment Performance"
    | "Activity Trends"
    | "Aging Trends"
    | "Recovery Trends";
  metrics: ARReportingMetricProjection[];
  generatedAt: string;
};

export type ARWorkflowReportingProjection = {
  engine: "enterprise_reporting_engine";
  tenantId: string;
  correlationId: string;
  generatedAt: string;
  sourceWorkflowEventCount: number;
  filters: ARWorkflowReportingFilter;
  workflowActions: ARWorkflowReportAction[];
  supportedExportFormats: ARReportingExportFormat[];
  drillDownRoutes: Record<ARReportingDrillDownTarget, string>;
  collectorPerformance: ARReportingPerformanceProjection;
  managerPerformance: ARReportingPerformanceProjection;
  clientPerformance: ARReportingPerformanceProjection;
  portfolioPerformance: ARReportingPerformanceProjection;
  recoveryPerformance: ARReportingPerformanceProjection;
  promisePerformance: ARReportingPerformanceProjection;
  ticklerPerformance: ARReportingPerformanceProjection;
  letterPerformance: ARReportingPerformanceProjection;
  statementPerformance: ARReportingPerformanceProjection;
  paymentPerformance: ARReportingPerformanceProjection;
  escalationPerformance: ARReportingPerformanceProjection;
  assignmentPerformance: ARReportingPerformanceProjection;
  activityTrends: ARReportingPerformanceProjection;
  agingTrends: ARReportingPerformanceProjection;
  recoveryTrends: ARReportingPerformanceProjection;
};

const AR_WORKFLOW_EVENT_STORAGE_KEY = "arRecoveryWorkflowEvents";
const CALL_COMPLETED_OUTCOMES = new Set(["Reached AP Contact", "Dispute Discussed", "Payment Discussed", "Escalation Required"]);
const CALL_FAILED_OUTCOMES = new Set(["No Answer", "Wrong Number"]);

export const AR_NOTIFICATION_DELIVERY_CHANNELS: ARNotificationDeliveryChannel[] = [
  "in_app",
  "email",
  "sms",
  "client_portal",
  "teams",
  "slack",
  "webhook",
];

export const AR_NOTIFICATION_LIFECYCLE: ARNotificationLifecycleStatus[] = [
  "Created",
  "Queued",
  "Delivered",
  "Read",
  "Dismissed",
  "Archived",
];

export const AR_REPORTING_WORKFLOW_ACTIONS: ARWorkflowReportAction[] = [
  "Report.View",
  "Report.Filter",
  "Report.Export",
  "Report.DrillDown",
];

export const AR_REPORTING_EXPORT_FORMATS: ARReportingExportFormat[] = [
  "CSV",
  "Excel",
  "PDF",
  "Future API",
];

export const AR_REPORTING_DRILL_DOWN_ROUTES: Record<ARReportingDrillDownTarget, string> = {
  "Collector KPI": "/admin/ar-recovery/customer-accounts",
  "Broken Promise KPI": "/admin/ar-recovery/promise-to-pay",
  "Escalation KPI": "/admin/ar-recovery/manager-command-center",
  "Customer KPI": "/admin/ar-recovery/customer-account-management",
  "Client KPI": "/admin/ar-recovery/client-management",
  "Payment KPI": "/admin/ar-recovery/customer-accounts?tab=payments",
  "Letter KPI": "/admin/ar-recovery/collection-letters",
  "Statement KPI": "/admin/ar-recovery/statements",
};

export const AR_WORKFLOW_EVENT_CONSUMER_TARGETS = [
  "Recovery Activity",
  "Timeline",
  "Manager Command",
  "Reporting",
  "Notifications",
  "Audit",
  "Future AI",
  "Future Automation",
] as const;

export const AR_STANDARD_WORKFLOW_EVENT_CONTRACT_FIELDS = [
  "Event ID",
  "Event Type",
  "Entity Type",
  "Entity ID",
  "Workspace",
  "User",
  "Timestamp",
  "Correlation ID",
  "Parent Event ID",
  "Metadata",
] as const;

export const AR_ENTERPRISE_WORKFLOW_ORCHESTRATION_CATEGORIES: AREnterpriseWorkflowOrchestrationCategory[] = [
  "Recovery",
  "Communications",
  "Documents",
  "Assignments",
  "Payments",
  "Escalations",
  "Notifications",
  "Manager Operations",
  "Client Operations",
  "Portfolio Operations",
  "Compliance",
  "Audit",
];

export const AR_ENTERPRISE_WORKFLOW_CONTRACT_FIELDS = [
  "Workflow ID",
  "Rule ID",
  "Trigger Event",
  "Result Event",
  "Workflow Status",
  "Started Timestamp",
  "Completed Timestamp",
  "Failed Timestamp",
  "Retry Count",
  "User",
  "Source",
  "Correlation ID",
  "Parent Workflow",
  "Metadata",
] as const;

export const AR_ENTERPRISE_WORKFLOW_ACTION_CATALOG: Record<AREnterpriseWorkflowActionKind, {
  category: AREnterpriseWorkflowOrchestrationCategory;
  description: string;
  supportedEventCategories: ARWorkflowEventCategory[];
}> = {
  PublishCommunicationEvent: {
    category: "Communications",
    description: "Publishes communication lifecycle events without owning delivery providers.",
    supportedEventCategories: ["communication"],
  },
  PublishTaskEvent: {
    category: "Recovery",
    description: "Publishes task lifecycle events for recovery work orchestration.",
    supportedEventCategories: ["task"],
  },
  PublishDocumentRequestEvent: {
    category: "Documents",
    description: "Publishes document request lifecycle events without owning document storage.",
    supportedEventCategories: ["document_request"],
  },
  PublishManagerEvent: {
    category: "Manager Operations",
    description: "Publishes manager operation events while Manager Command remains a consumer.",
    supportedEventCategories: ["manager"],
  },
  PublishRecoveryEvent: {
    category: "Recovery",
    description: "Publishes recovery activity events through the immutable event bus.",
    supportedEventCategories: ["note"],
  },
  PublishNotificationEvent: {
    category: "Notifications",
    description: "Publishes notification workflow events while notification delivery remains a projection.",
    supportedEventCategories: ["communication", "manager", "task", "document_request"],
  },
};

export const AR_ENTERPRISE_WORKFLOW_RULE_TEMPLATES: AREnterpriseWorkflowRuleDefinition[] = [];

export const AR_LEGACY_ACTIVITY_PROJECTION_INVENTORY = [
  {
    surface: "Recovery Activity",
    legacyPattern: "Activity rows previously projected directly from workspace activity arrays.",
    migrationStatus: "Consumes immutable workflow events through recoveryActivityRequired routing.",
  },
  {
    surface: "Timeline",
    legacyPattern: "Timeline rows previously depended on module-local history/activity arrays.",
    migrationStatus: "Consumes immutable workflow events through timelineRequired routing.",
  },
  {
    surface: "Manager Command",
    legacyPattern: "Manager attention signals previously depended on module-specific status snapshots.",
    migrationStatus: "Consumes immutable workflow events through managerUpdateRequired routing.",
  },
  {
    surface: "Reporting",
    legacyPattern: "Reporting previously depended on read-model snapshots and module counters.",
    migrationStatus: "Consumes immutable workflow events through reportingRequired routing.",
  },
  {
    surface: "Notifications",
    legacyPattern: "Notifications previously depended on individual workflow surfaces.",
    migrationStatus: "Consumes immutable workflow events through notificationRequired routing.",
  },
  {
    surface: "Audit",
    legacyPattern: "Audit history previously depended on local workflow records.",
    migrationStatus: "Consumes immutable workflow events through auditRequired routing.",
  },
] as const;

export const AR_REPORTING_WORKFLOW_ACTION_CATALOG: Record<ARWorkflowReportAction, { label: string; sourceWorkspace: string; requiredPermission: string; primaryEvent: ARWorkflowReportEventType }> = {
  "Report.View": {
    label: "View Report",
    sourceWorkspace: "Reports",
    requiredPermission: "collector_read",
    primaryEvent: "ReportViewed",
  },
  "Report.Filter": {
    label: "Filter Report",
    sourceWorkspace: "Reports",
    requiredPermission: "collector_read",
    primaryEvent: "ReportFiltered",
  },
  "Report.Export": {
    label: "Export Report",
    sourceWorkspace: "Reports",
    requiredPermission: "manager_override",
    primaryEvent: "ReportExported",
  },
  "Report.DrillDown": {
    label: "Open Drill Down",
    sourceWorkspace: "Reports",
    requiredPermission: "collector_read",
    primaryEvent: "ReportDrillDownOpened",
  },
};

export const PORTFOLIO_IMPORT_ENGINE_LIFECYCLE: PortfolioImportLifecycleStatus[] = [
  "Approved",
  "Importing",
  "Completed",
  "Failed",
  "Rolled Back",
  "Archived",
];

export const PORTFOLIO_IMPORT_STATUS_LIFECYCLE: PortfolioImportStatus[] = [
  "Draft",
  "Received",
  "Validating",
  "Validation Failed",
  "Ready For Review",
  "Approved",
  "Importing",
  "Completed",
  "Rolled Back",
  "Archived",
];

export const PORTFOLIO_VALIDATION_CATEGORIES: PortfolioValidationCategory[] = [
  "Required fields",
  "Duplicate customers",
  "Duplicate accounts",
  "Duplicate invoices",
  "Missing account numbers",
  "Missing customer names",
  "Invalid balances",
  "Negative balances",
  "Invalid dates",
  "Invalid aging buckets",
  "Unsupported statuses",
  "Malformed email",
  "Malformed phone",
  "Orphaned invoices",
  "Orphaned accounts",
  "Unknown collectors",
  "Unknown managers",
  "Invalid portfolio metadata",
];

export const ENTERPRISE_CLIENT_EXCHANGE_PROJECTION_TARGETS: PortfolioProjectionTarget[] = [
  {
    target: "Customers & Accounts",
    projectionStatus: "Prepared",
    description: "Approved imports will project customers, accounts, invoices, balances, and portfolio metadata.",
  },
  {
    target: "Recovery Workbench",
    projectionStatus: "Prepared",
    description: "Activated portfolios will project collector-ready work queues.",
  },
  {
    target: "Manager Command",
    projectionStatus: "Prepared",
    description: "Assigned portfolios will project manager oversight and operational attention signals.",
  },
  {
    target: "Recovery Activity",
    projectionStatus: "Prepared",
    description: "Intake milestones will project into recovery activity history.",
  },
  {
    target: "Timeline",
    projectionStatus: "Prepared",
    description: "Batch review, approval, import, rollback, and activation events will project to timeline history.",
  },
  {
    target: "Reporting",
    projectionStatus: "Prepared",
    description: "Import metrics, validation outcomes, and assignment readiness will project to enterprise reporting.",
  },
];

export const PORTFOLIO_VALIDATION_ENGINE_PROJECTION_TARGETS: PortfolioValidationEngineProjectionTarget[] = [
  {
    target: "Review & Approval Engine",
    projectionStatus: "Prepared",
    description: "Validation results determine whether a batch can move to human review.",
  },
  {
    target: "Import Engine",
    projectionStatus: "Prepared",
    description: "Import execution remains blocked until validation readiness is established.",
  },
  {
    target: "Reporting",
    projectionStatus: "Prepared",
    description: "Validation summaries, duplicate counts, and readiness status project to reporting.",
  },
  {
    target: "Recovery Activity",
    projectionStatus: "Prepared",
    description: "Validation milestones project to operational activity history.",
  },
  {
    target: "Timeline",
    projectionStatus: "Prepared",
    description: "Validation completion, warnings, and errors project to timeline history.",
  },
];

export const PORTFOLIO_MAPPING_REQUIRED_FIELDS = [
  "customer.name",
  "account.accountNumber",
  "invoice.invoiceNumber",
  "invoice.currentBalance",
  "invoice.dueDate",
] as const;

export const PORTFOLIO_MAPPING_SUPPORTED_TARGET_FIELDS = [
  "customer.name",
  "customer.dba",
  "customer.company",
  "account.accountNumber",
  "account.agingBucket",
  "invoice.invoiceNumber",
  "invoice.currentBalance",
  "invoice.dueDate",
  "invoice.invoiceDate",
  "assignment.collectorId",
  "assignment.managerId",
  "portfolio.metadata",
] as const;

export const PORTFOLIO_DEFAULT_MAPPING_SUGGESTIONS: Record<string, string> = {
  "customer name": "customer.name",
  customer: "customer.name",
  company: "customer.name",
  dba: "customer.dba",
  "account number": "account.accountNumber",
  "account #": "account.accountNumber",
  "invoice number": "invoice.invoiceNumber",
  "invoice #": "invoice.invoiceNumber",
  balance: "invoice.currentBalance",
  "amount due": "invoice.currentBalance",
  "open balance": "invoice.currentBalance",
  "due date": "invoice.dueDate",
  "invoice date": "invoice.invoiceDate",
  aging: "account.agingBucket",
  "aging bucket": "account.agingBucket",
  collector: "assignment.collectorId",
  manager: "assignment.managerId",
};

export const PORTFOLIO_MAPPING_ENGINE_PROJECTION_TARGETS: PortfolioMappingEngineProjectionTarget[] = [
  {
    target: "Validation Engine",
    projectionStatus: "Prepared",
    description: "Mapping readiness determines whether staged records can be value-validated.",
  },
  {
    target: "Review & Approval Engine",
    projectionStatus: "Prepared",
    description: "Mapping issues and unmapped fields project to review before approval.",
  },
  {
    target: "Import Engine",
    projectionStatus: "Prepared",
    description: "Import execution will use approved mapping profiles only.",
  },
  {
    target: "Reporting",
    projectionStatus: "Prepared",
    description: "Mapping readiness, warnings, and blocked fields project to reporting.",
  },
  {
    target: "Recovery Activity",
    projectionStatus: "Prepared",
    description: "Mapping milestones project to operational activity history.",
  },
  {
    target: "Timeline",
    projectionStatus: "Prepared",
    description: "Mapping profile creation, updates, warnings, and errors project to timeline history.",
  },
];

export const PORTFOLIO_REVIEW_ENGINE_PROJECTION_TARGETS: PortfolioReviewEngineProjectionTarget[] = [
  {
    target: "Import Engine",
    projectionStatus: "Prepared",
    description: "Approved staged portfolios become eligible for future import execution.",
  },
  {
    target: "Reporting",
    projectionStatus: "Prepared",
    description: "Approval readiness, approval status, and risk level project to reporting.",
  },
  {
    target: "Recovery Activity",
    projectionStatus: "Prepared",
    description: "Approval milestones project to operational activity history.",
  },
  {
    target: "Timeline",
    projectionStatus: "Prepared",
    description: "Approval required, completed, rejected, and risk events project to timeline history.",
  },
  {
    target: "Audit",
    projectionStatus: "Prepared",
    description: "Approval decisions remain immutable for audit review.",
  },
];

export const PORTFOLIO_IMPORT_ENGINE_PROJECTION_TARGETS: PortfolioImportProjectionTarget[] = [
  {
    target: "Customers & Accounts",
    projectionStatus: "Prepared",
    description: "Approved imports prepare customer, account, and invoice projections without overwriting existing records.",
  },
  {
    target: "Recovery Workbench",
    projectionStatus: "Prepared",
    description: "Activated portfolios prepare future collector workspace availability without triggering automatic work.",
  },
  {
    target: "Manager Command",
    projectionStatus: "Prepared",
    description: "Import progress, duplicate protection, and activation status prepare manager oversight projections.",
  },
  {
    target: "Recovery Activity",
    projectionStatus: "Prepared",
    description: "Import execution milestones project to operational activity history.",
  },
  {
    target: "Timeline",
    projectionStatus: "Prepared",
    description: "Import executing, completed, failed, rolled back, activated, and archived events project to timeline history.",
  },
  {
    target: "Reporting",
    projectionStatus: "Prepared",
    description: "Import history, record counts, duplicate summaries, and rollback availability project to reporting.",
  },
];

export const PORTFOLIO_ASSIGNMENT_ENGINE_PROJECTION_TARGETS: PortfolioAssignmentProjectionTarget[] = [
  {
    target: "Recovery Workbench",
    projectionStatus: "Prepared",
    description: "Assignment readiness prepares future collector workbench availability without activating live work queues.",
  },
  {
    target: "Customers & Accounts",
    projectionStatus: "Prepared",
    description: "Assignment ownership prepares customer and account portfolio context.",
  },
  {
    target: "Manager Command",
    projectionStatus: "Prepared",
    description: "Assignment status, blockers, and activation readiness prepare manager oversight projections.",
  },
  {
    target: "Recovery Activity",
    projectionStatus: "Prepared",
    description: "Assignment milestones project to operational activity history.",
  },
  {
    target: "Timeline",
    projectionStatus: "Prepared",
    description: "Assignment changes, readiness, activation, and blockers project to timeline history.",
  },
  {
    target: "Reporting",
    projectionStatus: "Prepared",
    description: "Assignment completion, eligibility, and readiness project to reporting.",
  },
];

export const PORTFOLIO_ACTIVATION_ENGINE_PROJECTION_TARGETS: PortfolioActivationProjectionTarget[] = [
  {
    target: "Recovery Workbench",
    projectionStatus: "Prepared",
    description: "Operational activation prepares imported and assigned portfolio records for collector workbench availability.",
  },
  {
    target: "Customers & Accounts",
    projectionStatus: "Prepared",
    description: "Operational activation prepares activated portfolio hierarchy projections.",
  },
  {
    target: "Manager Command",
    projectionStatus: "Prepared",
    description: "Operational activation status prepares manager oversight projections.",
  },
  {
    target: "Recovery Activity",
    projectionStatus: "Prepared",
    description: "Operational activation events project to recovery activity history.",
  },
  {
    target: "Timeline",
    projectionStatus: "Prepared",
    description: "Activation requested, validated, activated, failed, deactivated, and archived events project to timeline history.",
  },
  {
    target: "Reporting",
    projectionStatus: "Prepared",
    description: "Operational activation summaries and blockers project to reporting.",
  },
];

export const ENTERPRISE_CLIENT_EXCHANGE_EVENT_CATALOG: Record<ARWorkflowPortfolioIntakeEventType, { label: string; status: PortfolioImportStatus; requiredPermission: string; projectionTargets: PortfolioProjectionTarget["target"][] }> = {
  ImportStarted: {
    label: "Import Started",
    status: "Received",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioCreated: {
    label: "Portfolio Created",
    status: "Draft",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ImportValidated: {
    label: "Import Validated",
    status: "Ready For Review",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ImportFailed: {
    label: "Import Failed",
    status: "Validation Failed",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ValidationCompleted: {
    label: "Validation Completed",
    status: "Ready For Review",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ValidationWarningDetected: {
    label: "Validation Warning Detected",
    status: "Ready For Review",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ValidationErrorDetected: {
    label: "Validation Error Detected",
    status: "Validation Failed",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ImportMapped: {
    label: "Import Mapped",
    status: "Ready For Review",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  MappingProfileCreated: {
    label: "Mapping Profile Created",
    status: "Draft",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  MappingProfileUpdated: {
    label: "Mapping Profile Updated",
    status: "Draft",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  MappingWarningDetected: {
    label: "Mapping Warning Detected",
    status: "Ready For Review",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  MappingErrorDetected: {
    label: "Mapping Error Detected",
    status: "Validation Failed",
    requiredPermission: "collector_write",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ApprovalCompleted: {
    label: "Approval Completed",
    status: "Approved",
    requiredPermission: "manager_override",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ApprovalRequired: {
    label: "Approval Required",
    status: "Ready For Review",
    requiredPermission: "manager_override",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  RiskLevelCalculated: {
    label: "Risk Level Calculated",
    status: "Ready For Review",
    requiredPermission: "manager_override",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ImportApproved: {
    label: "Import Approved",
    status: "Approved",
    requiredPermission: "manager_override",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ImportRejected: {
    label: "Import Rejected",
    status: "Ready For Review",
    requiredPermission: "manager_override",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ImportExecuting: {
    label: "Import Executing",
    status: "Importing",
    requiredPermission: "manager_override",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
  ImportCompleted: {
    label: "Import Completed",
    status: "Completed",
    requiredPermission: "manager_override",
    projectionTargets: ["Customers & Accounts", "Recovery Workbench", "Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  ImportRolledBack: {
    label: "Import Rolled Back",
    status: "Rolled Back",
    requiredPermission: "manager_override",
    projectionTargets: ["Customers & Accounts", "Recovery Workbench", "Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioAssigned: {
    label: "Portfolio Assigned",
    status: "Completed",
    requiredPermission: "manager_override",
    projectionTargets: ["Recovery Workbench", "Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioAssignmentUpdated: {
    label: "Portfolio Assignment Updated",
    status: "Completed",
    requiredPermission: "manager_override",
    projectionTargets: ["Recovery Workbench", "Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioAssignmentRemoved: {
    label: "Portfolio Assignment Removed",
    status: "Ready For Review",
    requiredPermission: "manager_override",
    projectionTargets: ["Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioReadyForActivation: {
    label: "Portfolio Ready For Activation",
    status: "Completed",
    requiredPermission: "manager_override",
    projectionTargets: ["Customers & Accounts", "Recovery Workbench", "Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioActivationRequested: {
    label: "Portfolio Activation Requested",
    status: "Completed",
    requiredPermission: "manager_override",
    projectionTargets: ["Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioActivationValidated: {
    label: "Portfolio Activation Validated",
    status: "Completed",
    requiredPermission: "manager_override",
    projectionTargets: ["Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioActivated: {
    label: "Portfolio Activated",
    status: "Completed",
    requiredPermission: "manager_override",
    projectionTargets: ["Customers & Accounts", "Recovery Workbench", "Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioActivationFailed: {
    label: "Portfolio Activation Failed",
    status: "Failed",
    requiredPermission: "manager_override",
    projectionTargets: ["Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioDeactivated: {
    label: "Portfolio Deactivated",
    status: "Ready For Review",
    requiredPermission: "manager_override",
    projectionTargets: ["Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioActivationBlocked: {
    label: "Portfolio Activation Blocked",
    status: "Ready For Review",
    requiredPermission: "manager_override",
    projectionTargets: ["Manager Command", "Recovery Activity", "Timeline", "Reporting"],
  },
  PortfolioArchived: {
    label: "Portfolio Archived",
    status: "Archived",
    requiredPermission: "manager_override",
    projectionTargets: ["Recovery Activity", "Timeline", "Reporting"],
  },
};

type ARNotificationRecipientRole = "collector" | "manager" | "administrator" | "client_future";

type ARNotificationProjectionRule = {
  notificationType: string;
  priority: ARNotificationPriority;
  recipientRoles: ARNotificationRecipientRole[];
  deliveryChannels: ARNotificationDeliveryChannel[];
  visibility: ARNotificationVisibility;
  targetWorkspace: string;
  reason: string;
};

export const AR_NOTIFICATION_PROJECTION_CATALOG: Partial<Record<ARWorkflowEventType, ARNotificationProjectionRule>> = {
  EmailDrafted: {
    notificationType: "email_drafted",
    priority: "low",
    recipientRoles: ["collector"],
    deliveryChannels: ["in_app"],
    visibility: "collector",
    targetWorkspace: "Recovery Workbench",
    reason: "Email draft was prepared.",
  },
  CallCompleted: {
    notificationType: "call_completed",
    priority: "medium",
    recipientRoles: ["collector"],
    deliveryChannels: ["in_app"],
    visibility: "collector",
    targetWorkspace: "Recovery Workbench",
    reason: "Call outcome recorded.",
  },
  CallFailed: {
    notificationType: "call_failed",
    priority: "medium",
    recipientRoles: ["collector"],
    deliveryChannels: ["in_app"],
    visibility: "collector",
    targetWorkspace: "Recovery Workbench",
    reason: "Call attempt needs follow-up.",
  },
  PromiseBroken: {
    notificationType: "promise_broken",
    priority: "critical",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Promise Center",
    reason: "Broken promise requires collector and manager attention.",
  },
  PromiseCompleted: {
    notificationType: "promise_completed",
    priority: "medium",
    recipientRoles: ["collector"],
    deliveryChannels: ["in_app"],
    visibility: "collector",
    targetWorkspace: "Promise Center",
    reason: "Promise completion was recorded.",
  },
  TicklerEscalated: {
    notificationType: "tickler_escalated",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Tickler Center",
    reason: "Escalated tickler requires manager review.",
  },
  LetterDelivered: {
    notificationType: "letter_delivered",
    priority: "low",
    recipientRoles: ["collector"],
    deliveryChannels: ["in_app"],
    visibility: "collector",
    targetWorkspace: "Letter Center",
    reason: "Letter delivery was confirmed.",
  },
  LetterFailed: {
    notificationType: "letter_failed",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Letter Center",
    reason: "Letter delivery failed.",
  },
  StatementDelivered: {
    notificationType: "statement_delivered",
    priority: "low",
    recipientRoles: ["collector"],
    deliveryChannels: ["in_app"],
    visibility: "collector",
    targetWorkspace: "Statement Center",
    reason: "Statement delivery was confirmed.",
  },
  StatementFailed: {
    notificationType: "statement_failed",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Statement Center",
    reason: "Statement delivery failed.",
  },
  PaymentRecorded: {
    notificationType: "payment_recorded",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Recovery Workbench",
    reason: "Payment was recorded.",
  },
  EscalationCreated: {
    notificationType: "escalation_created",
    priority: "critical",
    recipientRoles: ["manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Escalation was created.",
  },
  EscalationAssigned: {
    notificationType: "escalation_assigned",
    priority: "high",
    recipientRoles: ["manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Escalation ownership changed.",
  },
  EscalationResolved: {
    notificationType: "escalation_resolved",
    priority: "medium",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Escalation was resolved.",
  },
  EmailFailed: {
    notificationType: "email_failed",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Recovery Workbench",
    reason: "Email delivery failed.",
  },
  SMSFailed: {
    notificationType: "sms_failed",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Recovery Workbench",
    reason: "SMS delivery failed.",
  },
  PortalNotification: {
    notificationType: "portal_notification",
    priority: "medium",
    recipientRoles: ["client_future"],
    deliveryChannels: ["client_portal"],
    visibility: "client_future",
    targetWorkspace: "Client Portal",
    reason: "Portal notification was published.",
  },
  MicrosoftTeamsNotification: {
    notificationType: "microsoft_teams_notification",
    priority: "medium",
    recipientRoles: ["manager"],
    deliveryChannels: ["teams"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Microsoft Teams notification was prepared.",
  },
  OutlookReminder: {
    notificationType: "outlook_reminder",
    priority: "medium",
    recipientRoles: ["collector"],
    deliveryChannels: ["email"],
    visibility: "collector",
    targetWorkspace: "Recovery Workbench",
    reason: "Outlook reminder was prepared.",
  },
  TaskCreated: {
    notificationType: "task_created",
    priority: "medium",
    recipientRoles: ["collector"],
    deliveryChannels: ["in_app"],
    visibility: "collector",
    targetWorkspace: "Recovery Workbench",
    reason: "Task was created.",
  },
  TaskAssigned: {
    notificationType: "task_assigned",
    priority: "medium",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Task ownership changed.",
  },
  TaskReassigned: {
    notificationType: "task_reassigned",
    priority: "medium",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Task ownership was reassigned.",
  },
  TaskOverdue: {
    notificationType: "task_overdue",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Task is overdue.",
  },
  DocumentRequested: {
    notificationType: "document_requested",
    priority: "medium",
    recipientRoles: ["collector", "client_future"],
    deliveryChannels: ["in_app", "client_portal"],
    visibility: "client_future",
    targetWorkspace: "Document Center",
    reason: "Document request was created.",
  },
  DocumentUploaded: {
    notificationType: "document_uploaded",
    priority: "medium",
    recipientRoles: ["collector"],
    deliveryChannels: ["in_app"],
    visibility: "collector",
    targetWorkspace: "Document Center",
    reason: "Requested document was uploaded.",
  },
  DocumentApproved: {
    notificationType: "document_approved",
    priority: "medium",
    recipientRoles: ["collector"],
    deliveryChannels: ["in_app"],
    visibility: "collector",
    targetWorkspace: "Document Center",
    reason: "Document request was approved.",
  },
  DocumentRejected: {
    notificationType: "document_rejected",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Document request was rejected.",
  },
  DocumentExpired: {
    notificationType: "document_expired",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Document request expired.",
  },
  CollectorAssigned: {
    notificationType: "collector_assigned",
    priority: "medium",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Collector assignment changed.",
  },
  ManagerAssigned: {
    notificationType: "manager_assigned",
    priority: "medium",
    recipientRoles: ["manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Manager assignment changed.",
  },
  ApprovalGranted: {
    notificationType: "approval_granted",
    priority: "medium",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Manager approval was granted.",
  },
  ApprovalRejected: {
    notificationType: "approval_rejected",
    priority: "high",
    recipientRoles: ["collector", "manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Manager approval was rejected.",
  },
  Escalated: {
    notificationType: "manager_escalated",
    priority: "high",
    recipientRoles: ["manager"],
    deliveryChannels: ["in_app"],
    visibility: "manager_only",
    targetWorkspace: "Manager Command",
    reason: "Manager escalation was recorded.",
  },
};

export const AR_CALL_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowCallEventType, { ownerWorkspace: string; auditRequired: boolean; managerUpdateRequired: boolean }> = {
  CallAttempted: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false },
  CallCompleted: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: true },
  CallFailed: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false },
  CallVoicemailLeft: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false },
  CallOutcomeUpdated: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: true },
};

export const AR_CALL_WORKFLOW_ACTION_CATALOG = {
  "Call.Log": {
    label: "Log Call",
    sourceWorkspace: "Recovery Workbench",
    requiredPermission: "collector_write",
    primaryEvent: "CallCompleted" as ARWorkflowCallEventType,
  },
} as const;

export const AR_NOTE_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowNoteEventType, { ownerWorkspace: string; auditRequired: boolean; managerUpdateRequired: boolean }> = {
  NoteCreated: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false },
  NoteUpdated: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: true },
  CollectorNoteCreated: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false },
  ManagerNoteCreated: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true },
  CustomerNoteCreated: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false },
};

export const AR_NOTE_WORKFLOW_ACTION_CATALOG = {
  "Note.Create": {
    label: "Create Note",
    sourceWorkspace: "Recovery Workbench",
    requiredPermission: "collector_write",
    primaryEvent: "NoteCreated" as ARWorkflowNoteEventType,
  },
  "Note.Update": {
    label: "Update Note",
    sourceWorkspace: "Recovery Workbench",
    requiredPermission: "collector_write",
    primaryEvent: "NoteUpdated" as ARWorkflowNoteEventType,
  },
} as const;

export const AR_PROMISE_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowPromiseEventType, { ownerWorkspace: string; auditRequired: boolean; managerUpdateRequired: boolean }> = {
  PromiseCreated: { ownerWorkspace: "Promise Center", auditRequired: true, managerUpdateRequired: false },
  PromiseUpdated: { ownerWorkspace: "Promise Center", auditRequired: true, managerUpdateRequired: false },
  PromiseCompleted: { ownerWorkspace: "Promise Center", auditRequired: true, managerUpdateRequired: false },
  PromiseBroken: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true },
  PromiseCancelled: { ownerWorkspace: "Promise Center", auditRequired: true, managerUpdateRequired: false },
  PromiseEscalated: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true },
  PromisePaymentReceived: { ownerWorkspace: "Promise Center", auditRequired: true, managerUpdateRequired: true },
};

export const AR_PROMISE_WORKFLOW_ACTION_CATALOG: Record<ARWorkflowPromiseAction, { label: string; sourceWorkspace: string; requiredPermission: string; primaryEvent: ARWorkflowPromiseEventType }> = {
  "Promise.Create": {
    label: "Create Promise",
    sourceWorkspace: "Promise Center",
    requiredPermission: "collector_write",
    primaryEvent: "PromiseCreated",
  },
  "Promise.Update": {
    label: "Update Promise",
    sourceWorkspace: "Promise Center",
    requiredPermission: "collector_write",
    primaryEvent: "PromiseUpdated",
  },
  "Promise.Complete": {
    label: "Complete Promise",
    sourceWorkspace: "Promise Center",
    requiredPermission: "collector_write",
    primaryEvent: "PromiseCompleted",
  },
  "Promise.MarkBroken": {
    label: "Mark Promise Broken",
    sourceWorkspace: "Promise Center",
    requiredPermission: "collector_write",
    primaryEvent: "PromiseBroken",
  },
  "Promise.Cancel": {
    label: "Cancel Promise",
    sourceWorkspace: "Promise Center",
    requiredPermission: "collector_write",
    primaryEvent: "PromiseCancelled",
  },
  "Promise.Escalate": {
    label: "Escalate Promise",
    sourceWorkspace: "Promise Center",
    requiredPermission: "manager_override",
    primaryEvent: "PromiseEscalated",
  },
  "Promise.PaymentReceived": {
    label: "Promise Payment Received",
    sourceWorkspace: "Promise Center",
    requiredPermission: "collector_write",
    primaryEvent: "PromisePaymentReceived",
  },
};

export const AR_TICKLER_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowTicklerEventType, { ownerWorkspace: string; auditRequired: boolean; managerUpdateRequired: boolean }> = {
  TicklerCreated: { ownerWorkspace: "Tickler Center", auditRequired: true, managerUpdateRequired: false },
  TicklerUpdated: { ownerWorkspace: "Tickler Center", auditRequired: true, managerUpdateRequired: false },
  TicklerCompleted: { ownerWorkspace: "Tickler Center", auditRequired: true, managerUpdateRequired: false },
  TicklerRescheduled: { ownerWorkspace: "Tickler Center", auditRequired: true, managerUpdateRequired: false },
  TicklerCancelled: { ownerWorkspace: "Tickler Center", auditRequired: true, managerUpdateRequired: false },
  TicklerSnoozed: { ownerWorkspace: "Tickler Center", auditRequired: true, managerUpdateRequired: false },
  TicklerEscalated: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true },
};

export const AR_TICKLER_WORKFLOW_ACTION_CATALOG: Record<ARWorkflowTicklerAction, { label: string; sourceWorkspace: string; requiredPermission: string; primaryEvent: ARWorkflowTicklerEventType }> = {
  "Tickler.Create": {
    label: "Create Tickler",
    sourceWorkspace: "Tickler Center",
    requiredPermission: "collector_write",
    primaryEvent: "TicklerCreated",
  },
  "Tickler.Update": {
    label: "Update Tickler",
    sourceWorkspace: "Tickler Center",
    requiredPermission: "collector_write",
    primaryEvent: "TicklerUpdated",
  },
  "Tickler.Complete": {
    label: "Complete Tickler",
    sourceWorkspace: "Tickler Center",
    requiredPermission: "collector_write",
    primaryEvent: "TicklerCompleted",
  },
  "Tickler.Reschedule": {
    label: "Reschedule Tickler",
    sourceWorkspace: "Tickler Center",
    requiredPermission: "collector_write",
    primaryEvent: "TicklerRescheduled",
  },
  "Tickler.Cancel": {
    label: "Cancel Tickler",
    sourceWorkspace: "Tickler Center",
    requiredPermission: "collector_write",
    primaryEvent: "TicklerCancelled",
  },
  "Tickler.Snooze": {
    label: "Snooze Tickler",
    sourceWorkspace: "Tickler Center",
    requiredPermission: "collector_write",
    primaryEvent: "TicklerSnoozed",
  },
  "Tickler.Escalate": {
    label: "Escalate Tickler",
    sourceWorkspace: "Tickler Center",
    requiredPermission: "manager_override",
    primaryEvent: "TicklerEscalated",
  },
};

export const AR_DOCUMENT_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowDocumentEventType, { ownerWorkspace: string; auditRequired: boolean; managerUpdateRequired: boolean }> = {
  LetterGenerated: { ownerWorkspace: "Letter Center", auditRequired: true, managerUpdateRequired: false },
  LetterPreviewed: { ownerWorkspace: "Letter Center", auditRequired: true, managerUpdateRequired: false },
  LetterPrinted: { ownerWorkspace: "Letter Center", auditRequired: true, managerUpdateRequired: false },
  LetterDownloaded: { ownerWorkspace: "Letter Center", auditRequired: true, managerUpdateRequired: false },
  LetterSent: { ownerWorkspace: "Letter Center", auditRequired: true, managerUpdateRequired: true },
  LetterDelivered: { ownerWorkspace: "Letter Center", auditRequired: true, managerUpdateRequired: true },
  LetterFailed: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true },
  LetterArchived: { ownerWorkspace: "Letter Center", auditRequired: true, managerUpdateRequired: false },
  StatementGenerated: { ownerWorkspace: "Statement Center", auditRequired: true, managerUpdateRequired: false },
  StatementPreviewed: { ownerWorkspace: "Statement Center", auditRequired: true, managerUpdateRequired: false },
  StatementPrinted: { ownerWorkspace: "Statement Center", auditRequired: true, managerUpdateRequired: false },
  StatementDownloaded: { ownerWorkspace: "Statement Center", auditRequired: true, managerUpdateRequired: false },
  StatementSent: { ownerWorkspace: "Statement Center", auditRequired: true, managerUpdateRequired: true },
  StatementDelivered: { ownerWorkspace: "Statement Center", auditRequired: true, managerUpdateRequired: true },
  StatementFailed: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true },
  StatementArchived: { ownerWorkspace: "Statement Center", auditRequired: true, managerUpdateRequired: false },
};

export const AR_DOCUMENT_WORKFLOW_ACTION_CATALOG: Record<ARWorkflowDocumentAction, { label: string; sourceWorkspace: string; requiredPermission: string; primaryEvent: ARWorkflowDocumentEventType }> = {
  "Letter.Generate": {
    label: "Generate Letter",
    sourceWorkspace: "Letter Center",
    requiredPermission: "collector_write",
    primaryEvent: "LetterGenerated",
  },
  "Letter.Preview": {
    label: "Preview Letter",
    sourceWorkspace: "Letter Center",
    requiredPermission: "collector_write",
    primaryEvent: "LetterPreviewed",
  },
  "Letter.Print": {
    label: "Print Letter",
    sourceWorkspace: "Letter Center",
    requiredPermission: "collector_write",
    primaryEvent: "LetterPrinted",
  },
  "Letter.Download": {
    label: "Download Letter",
    sourceWorkspace: "Letter Center",
    requiredPermission: "collector_write",
    primaryEvent: "LetterDownloaded",
  },
  "Letter.Send": {
    label: "Send Letter",
    sourceWorkspace: "Letter Center",
    requiredPermission: "collector_write",
    primaryEvent: "LetterSent",
  },
  "Letter.Archive": {
    label: "Archive Letter",
    sourceWorkspace: "Letter Center",
    requiredPermission: "collector_write",
    primaryEvent: "LetterArchived",
  },
  "Statement.Generate": {
    label: "Generate Statement",
    sourceWorkspace: "Statement Center",
    requiredPermission: "collector_write",
    primaryEvent: "StatementGenerated",
  },
  "Statement.Preview": {
    label: "Preview Statement",
    sourceWorkspace: "Statement Center",
    requiredPermission: "collector_write",
    primaryEvent: "StatementPreviewed",
  },
  "Statement.Print": {
    label: "Print Statement",
    sourceWorkspace: "Statement Center",
    requiredPermission: "collector_write",
    primaryEvent: "StatementPrinted",
  },
  "Statement.Download": {
    label: "Download Statement",
    sourceWorkspace: "Statement Center",
    requiredPermission: "collector_write",
    primaryEvent: "StatementDownloaded",
  },
  "Statement.Send": {
    label: "Send Statement",
    sourceWorkspace: "Statement Center",
    requiredPermission: "collector_write",
    primaryEvent: "StatementSent",
  },
  "Statement.Archive": {
    label: "Archive Statement",
    sourceWorkspace: "Statement Center",
    requiredPermission: "collector_write",
    primaryEvent: "StatementArchived",
  },
};

export const AR_ESCALATION_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowEscalationEventType, { ownerWorkspace: string; auditRequired: boolean; managerUpdateRequired: boolean; lifecycleStatus: string }> = {
  EscalationCreated: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Created" },
  EscalationUpdated: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "In Review" },
  EscalationAssigned: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Assigned" },
  EscalationResolved: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Resolved" },
  EscalationClosed: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Closed" },
  EscalationReopened: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Reopened" },
};

export const AR_ESCALATION_WORKFLOW_ACTION_CATALOG: Record<ARWorkflowEscalationAction, { label: string; sourceWorkspace: string; requiredPermission: string; primaryEvent: ARWorkflowEscalationEventType }> = {
  "Escalation.Create": {
    label: "Create Escalation",
    sourceWorkspace: "Recovery Workbench",
    requiredPermission: "collector_write",
    primaryEvent: "EscalationCreated",
  },
  "Escalation.Update": {
    label: "Update Escalation",
    sourceWorkspace: "Manager Command",
    requiredPermission: "manager_override",
    primaryEvent: "EscalationUpdated",
  },
  "Escalation.Assign": {
    label: "Assign Escalation",
    sourceWorkspace: "Manager Command",
    requiredPermission: "manager_override",
    primaryEvent: "EscalationAssigned",
  },
  "Escalation.Resolve": {
    label: "Resolve Escalation",
    sourceWorkspace: "Manager Command",
    requiredPermission: "manager_override",
    primaryEvent: "EscalationResolved",
  },
  "Escalation.Close": {
    label: "Close Escalation",
    sourceWorkspace: "Manager Command",
    requiredPermission: "manager_override",
    primaryEvent: "EscalationClosed",
  },
  "Escalation.Reopen": {
    label: "Reopen Escalation",
    sourceWorkspace: "Manager Command",
    requiredPermission: "manager_override",
    primaryEvent: "EscalationReopened",
  },
};

export const AR_PAYMENT_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowPaymentEventType, { ownerWorkspace: string; auditRequired: boolean; managerUpdateRequired: boolean; lifecycleStatus: string }> = {
  PaymentRecorded: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Recorded" },
  PaymentUpdated: { ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Updated" },
  PaymentVoided: { ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Voided" },
  PaymentAppliedToPromise: { ownerWorkspace: "Promise Center", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Applied" },
  PaymentAppliedToAccount: { ownerWorkspace: "Customers & Accounts", auditRequired: true, managerUpdateRequired: true, lifecycleStatus: "Applied" },
};

export const AR_PAYMENT_WORKFLOW_ACTION_CATALOG: Record<ARWorkflowPaymentAction, { label: string; sourceWorkspace: string; requiredPermission: string; primaryEvent: ARWorkflowPaymentEventType }> = {
  "Payment.Record": {
    label: "Record Payment",
    sourceWorkspace: "Recovery Workbench",
    requiredPermission: "collector_write",
    primaryEvent: "PaymentRecorded",
  },
  "Payment.Update": {
    label: "Update Payment",
    sourceWorkspace: "Recovery Workbench",
    requiredPermission: "manager_override",
    primaryEvent: "PaymentUpdated",
  },
  "Payment.Void": {
    label: "Void Payment",
    sourceWorkspace: "Manager Command",
    requiredPermission: "manager_override",
    primaryEvent: "PaymentVoided",
  },
  "Payment.ApplyToPromise": {
    label: "Apply Payment to Promise",
    sourceWorkspace: "Promise Center",
    requiredPermission: "collector_write",
    primaryEvent: "PaymentAppliedToPromise",
  },
  "Payment.ApplyToAccount": {
    label: "Apply Payment to Account",
    sourceWorkspace: "Customers & Accounts",
    requiredPermission: "collector_write",
    primaryEvent: "PaymentAppliedToAccount",
  },
};

type ARWorkflowOperationalEventCatalogEntry = {
  label: string;
  ownerWorkspace: string;
  auditRequired: boolean;
  managerUpdateRequired: boolean;
  notificationRequired: boolean;
  clientPortalProjectionRequired: boolean;
  requiredPermission: string;
  defaultEntityType: ARWorkflowEntityType;
};

export const AR_COMMUNICATION_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowCommunicationEventType, ARWorkflowOperationalEventCatalogEntry> = {
  EmailDrafted: { label: "Email Drafted", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
  EmailSent: { label: "Email Sent", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
  EmailDelivered: { label: "Email Delivered", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
  EmailFailed: { label: "Email Failed", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
  SMSSent: { label: "SMS Sent", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
  SMSDelivered: { label: "SMS Delivered", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
  SMSFailed: { label: "SMS Failed", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
  PortalNotification: { label: "Portal Notification", ownerWorkspace: "Client Portal", auditRequired: true, managerUpdateRequired: false, notificationRequired: true, clientPortalProjectionRequired: true, requiredPermission: "collector_write", defaultEntityType: "communication" },
  MicrosoftTeamsNotification: { label: "Microsoft Teams Notification", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "communication" },
  OutlookReminder: { label: "Outlook Reminder", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
  PhoneCallStarted: { label: "Phone Call Started", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
  PhoneCallCompleted: { label: "Phone Call Completed", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: true, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "communication" },
};

export const AR_TASK_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowTaskEventType, ARWorkflowOperationalEventCatalogEntry> = {
  TaskCreated: { label: "Task Created", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "task" },
  TaskAssigned: { label: "Task Assigned", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "task" },
  TaskReassigned: { label: "Task Reassigned", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "task" },
  TaskCompleted: { label: "Task Completed", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "task" },
  TaskCancelled: { label: "Task Cancelled", ownerWorkspace: "Recovery Workbench", auditRequired: true, managerUpdateRequired: false, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "task" },
  TaskOverdue: { label: "Task Overdue", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "collector_write", defaultEntityType: "task" },
};

export const AR_DOCUMENT_REQUEST_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowDocumentRequestEventType, ARWorkflowOperationalEventCatalogEntry> = {
  DocumentRequested: { label: "Document Requested", ownerWorkspace: "Document Center", auditRequired: true, managerUpdateRequired: false, notificationRequired: true, clientPortalProjectionRequired: true, requiredPermission: "collector_write", defaultEntityType: "document_request" },
  DocumentUploaded: { label: "Document Uploaded", ownerWorkspace: "Document Center", auditRequired: true, managerUpdateRequired: false, notificationRequired: true, clientPortalProjectionRequired: true, requiredPermission: "collector_write", defaultEntityType: "document_request" },
  DocumentApproved: { label: "Document Approved", ownerWorkspace: "Document Center", auditRequired: true, managerUpdateRequired: false, notificationRequired: true, clientPortalProjectionRequired: true, requiredPermission: "manager_override", defaultEntityType: "document_request" },
  DocumentRejected: { label: "Document Rejected", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: true, requiredPermission: "manager_override", defaultEntityType: "document_request" },
  DocumentExpired: { label: "Document Expired", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: true, requiredPermission: "collector_write", defaultEntityType: "document_request" },
};

export const AR_MANAGER_WORKFLOW_EVENT_CATALOG: Record<ARWorkflowManagerEventType, ARWorkflowOperationalEventCatalogEntry> = {
  CollectorAssigned: { label: "Collector Assigned", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  CollectorRemoved: { label: "Collector Removed", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  ManagerAssigned: { label: "Manager Assigned", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  PortfolioAssigned: { label: "Portfolio Assigned", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "portfolio_assignment" },
  PortfolioRemoved: { label: "Portfolio Removed", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "portfolio_assignment" },
  TeamActivated: { label: "Team Activated", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  TeamCreated: { label: "Team Created", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  TeamDeactivated: { label: "Team Deactivated", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  TeamUpdated: { label: "Team Updated", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  ApprovalGranted: { label: "Approval Granted", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  ApprovalRejected: { label: "Approval Rejected", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  Reviewed: { label: "Reviewed", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  Escalated: { label: "Escalated", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: true, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
  CommentAdded: { label: "Comment Added", ownerWorkspace: "Manager Command", auditRequired: true, managerUpdateRequired: true, notificationRequired: false, clientPortalProjectionRequired: false, requiredPermission: "manager_override", defaultEntityType: "manager_review" },
};

export function loadStoredARWorkflowEvents(): ARWorkflowEvent[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(AR_WORKFLOW_EVENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isARWorkflowEvent).map(normalizeWorkflowEventContract) : [];
  } catch {
    return [];
  }
}

export function saveStoredARWorkflowEvents(events: ARWorkflowEvent[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AR_WORKFLOW_EVENT_STORAGE_KEY, JSON.stringify(events));
}

export function publishARWorkflowEvents(events: ARWorkflowEvent[]) {
  const existing = loadStoredARWorkflowEvents();
  const existingIds = new Set(existing.map((event) => event.id));
  const immutableNewEvents = events.map(normalizeWorkflowEventContract).filter((event) => !existingIds.has(event.id));
  const nextEvents = [...immutableNewEvents, ...existing].sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
  saveStoredARWorkflowEvents(nextEvents);
  return nextEvents;
}

export function buildCallWorkflowEvents(input: ARWorkflowCallEventInput): ARWorkflowEvent[] {
  const now = new Date().toISOString();
  const correlationId = createWorkflowId("correlation", input.accountId);
  const attemptedEvent = buildCallWorkflowEvent(input, {
    eventType: "CallAttempted",
    occurredAt: now,
    correlationId,
    title: `Call attempted for ${input.customerName}`,
    summary: `Collector attempted a call with outcome pending.`,
    managerUpdateRequired: false,
  });
  const outcomeEventType = resolveCallOutcomeEventType(input.outcome);
  const outcomeEvent = buildCallWorkflowEvent(input, {
    eventType: outcomeEventType,
    occurredAt: now,
    correlationId,
    title: buildCallOutcomeTitle(outcomeEventType, input.customerName),
    summary: buildCallOutcomeSummary(input),
    managerUpdateRequired: outcomeEventType === "CallCompleted" || input.outcome === "Escalation Required",
  });

  return [attemptedEvent, outcomeEvent];
}

export function projectCallEventsToCallLogs(events: ARWorkflowEvent[]): ARWorkflowCallProjection[] {
  return events
    .filter((event) => event.eventCategory === "call" && ["CallCompleted", "CallFailed", "CallVoicemailLeft", "CallOutcomeUpdated"].includes(event.eventType))
    .map((event) => ({
      id: event.id.replace(/^workflow-event-/, "call-"),
      companyId: event.tenantId,
      accountId: event.scope.accountId || "",
      customerName: String(event.payload.metadata?.customerName || ""),
      customerNumber: String(event.payload.metadata?.customerNumber || ""),
      collector: event.actor.actorName,
      outcome: String(event.payload.metadata?.outcome || "No Answer"),
      durationMinutes: String(event.payload.metadata?.durationMinutes || ""),
      notes: String(event.payload.metadata?.notes || event.payload.summary),
      nextAction: String(event.payload.metadata?.nextAction || "Review call outcome."),
      createdAt: event.occurredAt,
    }))
    .filter((call) => call.companyId && call.accountId && call.customerName && call.outcome && call.createdAt)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

export function publishCallWorkflowAction(input: ARWorkflowCallEventInput) {
  const callEvents = buildCallWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(callEvents);
  return {
    callEvents,
    workflowEvents: nextWorkflowEvents,
    callLogs: projectCallEventsToCallLogs(nextWorkflowEvents),
  };
}

export function buildNoteWorkflowEvents(input: ARWorkflowNoteEventInput, action: "create" | "update" = "create"): ARWorkflowEvent[] {
  const now = new Date().toISOString();
  const correlationId = createWorkflowId("correlation", input.accountId);

  if (action === "update") {
    return [
      buildNoteWorkflowEvent(input, {
        eventType: "NoteUpdated",
        occurredAt: now,
        correlationId,
        title: `${input.noteType} updated for ${input.customerName}`,
        summary: buildNoteSummary(input, "updated"),
        managerUpdateRequired: true,
        workflowAction: "Note.Update",
      }),
    ];
  }

  const specializedEventType = resolveNoteCreatedEventType(input);
  return [
    buildNoteWorkflowEvent(input, {
      eventType: "NoteCreated",
      occurredAt: now,
      correlationId,
      title: `${input.noteType} created for ${input.customerName}`,
      summary: buildNoteSummary(input, "created"),
      managerUpdateRequired: shouldRouteNoteToManager(input),
      workflowAction: "Note.Create",
    }),
    buildNoteWorkflowEvent(input, {
      eventType: specializedEventType,
      occurredAt: now,
      correlationId,
      title: buildSpecializedNoteTitle(specializedEventType, input),
      summary: buildNoteSummary(input, "created"),
      managerUpdateRequired: shouldRouteNoteToManager(input),
      workflowAction: "Note.Create",
    }),
  ];
}

export function projectNoteEventsToNoteLogs(events: ARWorkflowEvent[]): ARWorkflowNoteProjection[] {
  const projectableNoteEvents = events.filter((event) =>
    event.eventCategory === "note"
      && ["CollectorNoteCreated", "ManagerNoteCreated", "CustomerNoteCreated", "NoteUpdated"].includes(event.eventType),
  );

  return projectableNoteEvents
    .map((event) => ({
      id: event.id.replace(/^workflow-event-/, "note-"),
      companyId: event.tenantId,
      accountId: event.scope.accountId || "",
      customerName: String(event.payload.metadata?.customerName || ""),
      customerNumber: String(event.payload.metadata?.customerNumber || ""),
      collector: event.actor.actorName,
      noteType: String(event.payload.metadata?.noteType || "Internal Note"),
      visibility: normalizeNoteVisibility(event.payload.metadata?.visibility),
      content: String(event.payload.metadata?.content || event.payload.summary),
      outcome: String(event.payload.metadata?.outcome || ""),
      nextAction: String(event.payload.metadata?.nextAction || ""),
      createdAt: event.occurredAt,
    }))
    .filter((note) => note.companyId && note.accountId && note.customerName && note.noteType && note.visibility && note.content && note.createdAt)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

export function publishNoteWorkflowAction(input: ARWorkflowNoteEventInput, action: "create" | "update" = "create") {
  const noteEvents = buildNoteWorkflowEvents(input, action);
  const nextWorkflowEvents = publishARWorkflowEvents(noteEvents);
  return {
    noteEvents,
    workflowEvents: nextWorkflowEvents,
    noteLogs: projectNoteEventsToNoteLogs(nextWorkflowEvents),
  };
}

export function buildPromiseWorkflowEvents(input: ARWorkflowPromiseEventInput): ARWorkflowEvent[] {
  const now = new Date().toISOString();
  const eventType = resolvePromiseActionEventType(input.action);
  const managerUpdateRequired = eventType === "PromiseBroken" || eventType === "PromiseEscalated" || eventType === "PromisePaymentReceived";
  return [
    buildPromiseWorkflowEvent(input, {
      eventType,
      occurredAt: now,
      correlationId: createWorkflowId("correlation", input.promiseId),
      title: buildPromiseEventTitle(eventType, input.customerName),
      summary: buildPromiseSummary(eventType, input.promiseRecord),
      managerUpdateRequired,
    }),
  ];
}

export function projectPromiseEventsToPromiseWorkflows(events: ARWorkflowEvent[]): ARWorkflowPromiseProjection[] {
  const latestByPromiseId = new Map<string, { event: ARWorkflowEvent; promiseRecord: Record<string, unknown> }>();
  events
    .filter((event) => event.eventCategory === "promise" && isPromiseWorkflowEventType(event.eventType))
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt))
    .forEach((event) => {
      const promiseRecord = toRecord(event.payload.newValue);
      const promiseId = String(event.payload.metadata?.promiseId || promiseRecord.id || "");
      if (!promiseId) return;
      latestByPromiseId.set(promiseId, { event, promiseRecord });
    });

  return Array.from(latestByPromiseId.values())
    .map(({ event, promiseRecord }) => ({
      ...promiseRecord,
      id: String(promiseRecord.id || event.payload.metadata?.promiseId || event.id),
      companyId: String(promiseRecord.companyId || event.tenantId),
      accountId: String(promiseRecord.accountId || event.scope.accountId || ""),
      customerName: String(promiseRecord.customerName || event.payload.metadata?.customerName || ""),
      customerNumber: String(promiseRecord.customerNumber || event.payload.metadata?.customerNumber || ""),
      status: String(promiseRecord.status || event.payload.metadata?.promiseStatus || ""),
      updatedAt: String(promiseRecord.updatedAt || event.occurredAt),
    }))
    .filter((promise) => promise.id && promise.companyId && promise.accountId && promise.customerName && promise.customerNumber && promise.status && promise.updatedAt)
    .sort((a, b) => Date.parse(String(b.updatedAt)) - Date.parse(String(a.updatedAt)));
}

export function publishPromiseWorkflowAction(input: ARWorkflowPromiseEventInput) {
  const promiseEvents = buildPromiseWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(promiseEvents);
  return {
    promiseEvents,
    workflowEvents: nextWorkflowEvents,
    promiseWorkflows: projectPromiseEventsToPromiseWorkflows(nextWorkflowEvents),
  };
}

export function buildTicklerWorkflowEvents(input: ARWorkflowTicklerEventInput): ARWorkflowEvent[] {
  const now = new Date().toISOString();
  const eventType = resolveTicklerActionEventType(input.action);
  const managerUpdateRequired = eventType === "TicklerEscalated";
  return [
    buildTicklerWorkflowEvent(input, {
      eventType,
      occurredAt: now,
      correlationId: createWorkflowId("correlation", input.ticklerId),
      title: buildTicklerEventTitle(eventType, input.customerName),
      summary: buildTicklerSummary(eventType, input.ticklerRecord),
      managerUpdateRequired,
    }),
  ];
}

export function projectTicklerEventsToTicklerWorkflows(events: ARWorkflowEvent[]): ARWorkflowTicklerProjection[] {
  const latestByTicklerId = new Map<string, { event: ARWorkflowEvent; ticklerRecord: Record<string, unknown> }>();
  events
    .filter((event) => event.eventCategory === "tickler" && isTicklerWorkflowEventType(event.eventType))
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt))
    .forEach((event) => {
      const ticklerRecord = toRecord(event.payload.newValue);
      const ticklerId = String(event.payload.metadata?.ticklerId || ticklerRecord.id || "");
      if (!ticklerId) return;
      latestByTicklerId.set(ticklerId, { event, ticklerRecord });
    });

  return Array.from(latestByTicklerId.values())
    .map(({ event, ticklerRecord }) => ({
      ...ticklerRecord,
      id: String(ticklerRecord.id || event.payload.metadata?.ticklerId || event.id),
      companyId: String(ticklerRecord.companyId || event.tenantId),
      accountId: String(ticklerRecord.accountId || event.scope.accountId || ""),
      customerName: String(ticklerRecord.customerName || event.payload.metadata?.customerName || ""),
      status: String(ticklerRecord.status || event.payload.metadata?.ticklerStatus || ""),
      dueDate: String(ticklerRecord.dueDate || event.payload.metadata?.dueDate || ""),
      updatedAt: String(ticklerRecord.updatedAt || event.occurredAt),
    }))
    .filter((tickler) => tickler.id && tickler.companyId && tickler.accountId && tickler.customerName && tickler.status && tickler.dueDate && tickler.updatedAt)
    .sort((a, b) => Date.parse(String(a.dueDate)) - Date.parse(String(b.dueDate)));
}

export function publishTicklerWorkflowAction(input: ARWorkflowTicklerEventInput) {
  const ticklerEvents = buildTicklerWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(ticklerEvents);
  return {
    ticklerEvents,
    workflowEvents: nextWorkflowEvents,
    ticklerWorkflows: projectTicklerEventsToTicklerWorkflows(nextWorkflowEvents),
  };
}

export function buildDocumentWorkflowEvents(input: ARWorkflowDocumentEventInput): ARWorkflowEvent[] {
  const now = new Date().toISOString();
  const primaryEventType = resolveDocumentActionEventType(input.action, input.deliveryStatus);
  const events = [
    buildDocumentWorkflowEvent(input, {
      eventType: primaryEventType,
      occurredAt: now,
      correlationId: createWorkflowId("correlation", input.documentId),
      title: buildDocumentEventTitle(primaryEventType, input.customerName),
      summary: buildDocumentSummary(primaryEventType, input.documentRecord),
      managerUpdateRequired: primaryEventType === "LetterSent" || primaryEventType === "LetterDelivered" || primaryEventType === "LetterFailed",
    }),
  ];

  if ((input.action === "Letter.Send" || input.action === "Statement.Send") && input.deliveryStatus === "delivered") {
    const deliveredEventType = input.documentType === "statement" ? "StatementDelivered" : "LetterDelivered";
    events.push(buildDocumentWorkflowEvent(input, {
      eventType: deliveredEventType,
      occurredAt: now,
      correlationId: events[0].correlationId,
      title: buildDocumentEventTitle(deliveredEventType, input.customerName),
      summary: buildDocumentSummary(deliveredEventType, input.documentRecord),
      managerUpdateRequired: true,
    }));
  }

  return events;
}

export function projectDocumentEventsToDocumentWorkflows(events: ARWorkflowEvent[], documentType: ARWorkflowDocumentType = "letter"): ARWorkflowDocumentProjection[] {
  const latestByDocumentId = new Map<string, { event: ARWorkflowEvent; documentRecord: Record<string, unknown> }>();
  events
    .filter((event) => event.eventCategory === "document" && isDocumentWorkflowEventType(event.eventType) && event.payload.metadata?.documentType === documentType)
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt))
    .forEach((event) => {
      const documentRecord = toRecord(event.payload.newValue);
      const documentId = String(event.payload.metadata?.documentId || documentRecord.id || "");
      if (!documentId) return;
      latestByDocumentId.set(documentId, { event, documentRecord });
    });

  return Array.from(latestByDocumentId.values())
    .map(({ event, documentRecord }) => ({
      ...documentRecord,
      id: String(documentRecord.id || event.payload.metadata?.documentId || event.id),
      companyId: String(documentRecord.companyId || event.tenantId),
      accountId: String(documentRecord.accountId || event.scope.accountId || ""),
      customerName: String(documentRecord.customerName || event.payload.metadata?.customerName || ""),
      customerNumber: String(documentRecord.customerNumber || event.payload.metadata?.customerNumber || ""),
      status: String(documentRecord.status || event.payload.metadata?.documentStatus || ""),
      deliveryStatus: String(documentRecord.deliveryStatus || event.payload.metadata?.deliveryStatus || ""),
      updatedAt: String(documentRecord.updatedAt || event.occurredAt),
      generatedAt: String(documentRecord.generatedAt || documentRecord.createdAt || event.occurredAt),
    }))
    .filter((document) => document.id && document.companyId && document.accountId && document.customerName && document.customerNumber && document.status && document.generatedAt)
    .sort((a, b) => Date.parse(String(b.updatedAt || b.generatedAt)) - Date.parse(String(a.updatedAt || a.generatedAt)));
}

export function publishDocumentWorkflowAction(input: ARWorkflowDocumentEventInput) {
  const documentEvents = buildDocumentWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(documentEvents);
  return {
    documentEvents,
    workflowEvents: nextWorkflowEvents,
    documentWorkflows: projectDocumentEventsToDocumentWorkflows(nextWorkflowEvents, input.documentType),
  };
}

export function buildEscalationWorkflowEvents(input: ARWorkflowEscalationEventInput): ARWorkflowEvent[] {
  const now = new Date().toISOString();
  const eventType = resolveEscalationActionEventType(input.action);
  return [
    buildEscalationWorkflowEvent(input, {
      eventType,
      occurredAt: now,
      correlationId: createWorkflowId("correlation", input.escalationId),
      title: buildEscalationEventTitle(eventType, input.customerName),
      summary: buildEscalationSummary(eventType, input.escalationRecord),
    }),
  ];
}

export function projectEscalationEventsToEscalationWorkflows(events: ARWorkflowEvent[]): ARWorkflowEscalationProjection[] {
  const latestByEscalationId = new Map<string, { event: ARWorkflowEvent; escalationRecord: Record<string, unknown> }>();
  events
    .filter((event) => event.eventCategory === "escalation" && isEscalationWorkflowEventType(event.eventType))
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt))
    .forEach((event) => {
      const escalationRecord = toRecord(event.payload.newValue);
      const escalationId = String(event.payload.metadata?.escalationId || escalationRecord.id || "");
      if (!escalationId) return;
      latestByEscalationId.set(escalationId, { event, escalationRecord });
    });

  return Array.from(latestByEscalationId.values())
    .map(({ event, escalationRecord }) => ({
      ...escalationRecord,
      id: String(escalationRecord.id || event.payload.metadata?.escalationId || event.id),
      companyId: String(escalationRecord.companyId || event.tenantId),
      accountId: String(escalationRecord.accountId || event.scope.accountId || ""),
      customerName: String(escalationRecord.customerName || event.payload.metadata?.customerName || ""),
      customerNumber: String(escalationRecord.customerNumber || event.payload.metadata?.customerNumber || ""),
      status: String(escalationRecord.status || event.payload.metadata?.escalationStatus || AR_ESCALATION_WORKFLOW_EVENT_CATALOG[event.eventType as ARWorkflowEscalationEventType]?.lifecycleStatus || ""),
      priority: String(escalationRecord.priority || event.payload.metadata?.priority || "Manager Review"),
      reason: String(escalationRecord.reason || event.payload.metadata?.reason || event.payload.summary),
      assignedTo: String(escalationRecord.assignedTo || event.payload.metadata?.assignedTo || "Manager Command"),
      updatedAt: String(escalationRecord.updatedAt || event.occurredAt),
      createdAt: String(escalationRecord.createdAt || event.occurredAt),
    }))
    .filter((escalation) => escalation.id && escalation.companyId && escalation.accountId && escalation.customerName && escalation.status && escalation.updatedAt)
    .sort((a, b) => Date.parse(String(b.updatedAt)) - Date.parse(String(a.updatedAt)));
}

export function publishEscalationWorkflowAction(input: ARWorkflowEscalationEventInput) {
  const escalationEvents = buildEscalationWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(escalationEvents);
  return {
    escalationEvents,
    workflowEvents: nextWorkflowEvents,
    escalationWorkflows: projectEscalationEventsToEscalationWorkflows(nextWorkflowEvents),
  };
}

export function buildPaymentWorkflowEvents(input: ARWorkflowPaymentEventInput): ARWorkflowEvent[] {
  const now = new Date().toISOString();
  const eventType = resolvePaymentActionEventType(input.action);
  return [
    buildPaymentWorkflowEvent(input, {
      eventType,
      occurredAt: now,
      correlationId: createWorkflowId("correlation", input.paymentId),
      title: buildPaymentEventTitle(eventType, input.customerName),
      summary: buildPaymentSummary(eventType, input.paymentRecord),
    }),
  ];
}

function getPaymentProjectionEventRank(eventType: string) {
  if (eventType === "PaymentAppliedToAccount") return 3;
  if (eventType === "PaymentAppliedToPromise") return 2;
  if (eventType === "PaymentRecorded") return 1;
  return 0;
}

export function projectPaymentEventsToPaymentWorkflows(events: ARWorkflowEvent[]): ARWorkflowPaymentProjection[] {
  const latestByPaymentId = new Map<string, { event: ARWorkflowEvent; paymentRecord: Record<string, unknown> }>();
  events
    .filter((event) => event.eventCategory === "payment" && isPaymentWorkflowEventType(event.eventType))
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt))
    .forEach((event) => {
      const paymentRecord = toRecord(event.payload.newValue);
      const paymentId = String(event.payload.metadata?.paymentId || paymentRecord.id || "");
      if (!paymentId) return;
      const existing = latestByPaymentId.get(paymentId);
      if (!existing) {
        latestByPaymentId.set(paymentId, { event, paymentRecord });
        return;
      }
      const existingTime = Date.parse(existing.event.occurredAt);
      const eventTime = Date.parse(event.occurredAt);
      const shouldReplace = eventTime > existingTime
        || (eventTime === existingTime && getPaymentProjectionEventRank(event.eventType) > getPaymentProjectionEventRank(existing.event.eventType));
      if (shouldReplace) {
        latestByPaymentId.set(paymentId, { event, paymentRecord });
      }
    });

  return Array.from(latestByPaymentId.values())
    .map(({ event, paymentRecord }) => ({
      ...paymentRecord,
      id: String(paymentRecord.id || event.payload.metadata?.paymentId || event.id),
      companyId: String(paymentRecord.companyId || event.tenantId),
      accountId: String(paymentRecord.accountId || event.scope.accountId || ""),
      customerName: String(paymentRecord.customerName || event.payload.metadata?.customerName || ""),
      customerNumber: String(paymentRecord.customerNumber || event.payload.metadata?.customerNumber || ""),
      amountCents: normalizePaymentAmountCents(paymentRecord.amountCents || event.payload.metadata?.amountCents),
      status: String(paymentRecord.status || event.payload.metadata?.paymentStatus || AR_PAYMENT_WORKFLOW_EVENT_CATALOG[event.eventType as ARWorkflowPaymentEventType]?.lifecycleStatus || ""),
      lifecycleStatus: String(paymentRecord.lifecycleStatus || event.payload.metadata?.lifecycleStatus || AR_PAYMENT_WORKFLOW_EVENT_CATALOG[event.eventType as ARWorkflowPaymentEventType]?.lifecycleStatus || ""),
      paymentMethod: String(paymentRecord.paymentMethod || event.payload.metadata?.paymentMethod || "Not Captured"),
      applicationScope: String(paymentRecord.applicationScope || event.payload.metadata?.applicationScope || ""),
      externalPaymentReference: String(paymentRecord.externalPaymentReference || event.payload.metadata?.externalPaymentReference || ""),
      paymentDate: String(paymentRecord.paymentDate || event.payload.metadata?.paymentDate || ""),
      updatedAt: String(paymentRecord.updatedAt || event.occurredAt),
      recordedAt: String(paymentRecord.recordedAt || paymentRecord.createdAt || event.occurredAt),
    }))
    .filter((payment) => payment.id && payment.companyId && payment.accountId && payment.customerName && payment.status && payment.recordedAt)
    .sort((a, b) => Date.parse(String(b.updatedAt || b.recordedAt)) - Date.parse(String(a.updatedAt || a.recordedAt)));
}

export function publishPaymentWorkflowAction(input: ARWorkflowPaymentEventInput) {
  const paymentEvents = buildPaymentWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(paymentEvents);
  return {
    paymentEvents,
    workflowEvents: nextWorkflowEvents,
    paymentWorkflows: projectPaymentEventsToPaymentWorkflows(nextWorkflowEvents),
  };
}

export function buildReportWorkflowEvents(input: ARWorkflowReportEventInput): ARWorkflowEvent[] {
  const now = new Date().toISOString();
  const eventType = AR_REPORTING_WORKFLOW_ACTION_CATALOG[input.action].primaryEvent;
  return [
    buildReportWorkflowEvent(input, {
      eventType,
      occurredAt: now,
      correlationId: createWorkflowId("correlation", input.reportId),
      title: buildReportEventTitle(eventType, input.reportName),
      summary: buildReportSummary(input),
    }),
  ];
}

export function publishReportWorkflowAction(input: ARWorkflowReportEventInput) {
  const reportEvents = buildReportWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(reportEvents);
  return {
    reportEvents,
    workflowEvents: nextWorkflowEvents,
    reportingProjection: projectWorkflowEventsToReportingAnalytics(nextWorkflowEvents, input.filters),
  };
}

export function buildCommunicationWorkflowEvents(input: ARWorkflowCommunicationEventInput): ARWorkflowEvent[] {
  return [
    buildOperationalWorkflowEvent(input, {
      eventType: input.eventType,
      eventCategory: "communication",
      catalogEntry: AR_COMMUNICATION_WORKFLOW_EVENT_CATALOG[input.eventType],
    }),
  ];
}

export function publishCommunicationWorkflowAction(input: ARWorkflowCommunicationEventInput) {
  const communicationEvents = buildCommunicationWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(communicationEvents);
  return {
    communicationEvents,
    workflowEvents: nextWorkflowEvents,
    notificationRecords: projectWorkflowEventsToNotificationRecords(nextWorkflowEvents),
    reportingProjection: projectWorkflowEventsToReportingAnalytics(nextWorkflowEvents),
  };
}

export function buildTaskWorkflowEvents(input: ARWorkflowTaskEventInput): ARWorkflowEvent[] {
  return [
    buildOperationalWorkflowEvent(input, {
      eventType: input.eventType,
      eventCategory: "task",
      catalogEntry: AR_TASK_WORKFLOW_EVENT_CATALOG[input.eventType],
    }),
  ];
}

export function publishTaskWorkflowAction(input: ARWorkflowTaskEventInput) {
  const taskEvents = buildTaskWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(taskEvents);
  return {
    taskEvents,
    workflowEvents: nextWorkflowEvents,
    notificationRecords: projectWorkflowEventsToNotificationRecords(nextWorkflowEvents),
    reportingProjection: projectWorkflowEventsToReportingAnalytics(nextWorkflowEvents),
  };
}

export function buildDocumentRequestWorkflowEvents(input: ARWorkflowDocumentRequestEventInput): ARWorkflowEvent[] {
  return [
    buildOperationalWorkflowEvent(input, {
      eventType: input.eventType,
      eventCategory: "document_request",
      catalogEntry: AR_DOCUMENT_REQUEST_WORKFLOW_EVENT_CATALOG[input.eventType],
    }),
  ];
}

export function publishDocumentRequestWorkflowAction(input: ARWorkflowDocumentRequestEventInput) {
  const documentRequestEvents = buildDocumentRequestWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(documentRequestEvents);
  return {
    documentRequestEvents,
    workflowEvents: nextWorkflowEvents,
    notificationRecords: projectWorkflowEventsToNotificationRecords(nextWorkflowEvents),
    reportingProjection: projectWorkflowEventsToReportingAnalytics(nextWorkflowEvents),
  };
}

export function buildManagerWorkflowEvents(input: ARWorkflowManagerEventInput): ARWorkflowEvent[] {
  return [
    buildOperationalWorkflowEvent(input, {
      eventType: input.eventType,
      eventCategory: "manager",
      catalogEntry: AR_MANAGER_WORKFLOW_EVENT_CATALOG[input.eventType],
    }),
  ];
}

export function publishManagerWorkflowAction(input: ARWorkflowManagerEventInput) {
  const managerEvents = buildManagerWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(managerEvents);
  return {
    managerEvents,
    workflowEvents: nextWorkflowEvents,
    notificationRecords: projectWorkflowEventsToNotificationRecords(nextWorkflowEvents),
    reportingProjection: projectWorkflowEventsToReportingAnalytics(nextWorkflowEvents),
  };
}

export const EnterpriseWorkflowRuleEngine = {
  evaluate(input: Pick<AREnterpriseWorkflowOrchestrationInput, "triggerEvent" | "rules" | "existingEvents" | "parentWorkflowId">) {
    const triggerEvent = normalizeWorkflowEventContract(input.triggerEvent);
    const rules = input.rules || AR_ENTERPRISE_WORKFLOW_RULE_TEMPLATES;
    const existingEvents = input.existingEvents || loadStoredARWorkflowEvents();
    const skippedRules: Array<{ ruleId: string; reason: string }> = [];

    const matchedRules = rules.filter((rule) => {
      if (rule.status !== "Active") {
        skippedRules.push({ ruleId: rule.ruleId, reason: "Rule is not active." });
        return false;
      }
      if (!rule.triggerEventTypes.includes(triggerEvent.eventType)) {
        skippedRules.push({ ruleId: rule.ruleId, reason: "Trigger event type does not match." });
        return false;
      }
      if (!rule.allowRecursiveExecution && rule.actions.some((action) => action.resultEventType === triggerEvent.eventType)) {
        skippedRules.push({ ruleId: rule.ruleId, reason: "Circular workflow execution blocked." });
        return false;
      }
      if (hasRuleExceededExecutionLimit(rule, triggerEvent, existingEvents)) {
        skippedRules.push({ ruleId: rule.ruleId, reason: "Rule execution limit reached for this correlation." });
        return false;
      }
      if (!rule.conditions.every((condition) => evaluateWorkflowCondition(triggerEvent, condition))) {
        skippedRules.push({ ruleId: rule.ruleId, reason: "Rule conditions did not match." });
        return false;
      }
      return true;
    });

    return {
      triggerEvent,
      matchedRules,
      skippedRules,
      existingEvents,
    };
  },
};

export const EnterpriseWorkflowOrchestrationEngine = {
  execute(input: AREnterpriseWorkflowOrchestrationInput): AREnterpriseWorkflowOrchestrationResult {
    const workflowId = createWorkflowId("enterprise-workflow", input.triggerEvent.id);
    const startedAt = new Date().toISOString();
    const actor = input.actor || input.triggerEvent.actor;
    const source = input.source || "Enterprise Workflow Orchestration Engine";
    const evaluation = EnterpriseWorkflowRuleEngine.evaluate(input);
    const executionRecords: AREnterpriseWorkflowExecutionRecord[] = [];
    const resultEvents: ARWorkflowEvent[] = [];

    evaluation.matchedRules.forEach((rule) => {
      const ruleWorkflowId = createWorkflowId("enterprise-workflow-rule", rule.ruleId);
      const ruleResultEvents = rule.actions.flatMap((action) =>
        buildOrchestratedResultEvents({
          action,
          actor,
          rule,
          source,
          triggerEvent: evaluation.triggerEvent,
          workflowId: ruleWorkflowId,
          parentWorkflowId: input.parentWorkflowId,
          retryCount: input.retryCount || 0,
        }),
      );
      const uniqueRuleEvents = dedupeWorkflowEvents(ruleResultEvents);
      resultEvents.push(...uniqueRuleEvents);

      executionRecords.push({
        workflowId: ruleWorkflowId,
        ruleId: rule.ruleId,
        triggerEventId: evaluation.triggerEvent.id,
        resultEventIds: uniqueRuleEvents.map((event) => event.id),
        workflowStatus: uniqueRuleEvents.length > 0 ? "Completed" : "Skipped",
        startedAt,
        completedAt: new Date().toISOString(),
        retryCount: input.retryCount || 0,
        user: actor,
        source,
        correlationId: evaluation.triggerEvent.correlationId,
        parentWorkflowId: input.parentWorkflowId,
        metadata: {
          category: rule.category,
          triggerEventType: evaluation.triggerEvent.eventType,
          actionCount: rule.actions.length,
        },
      });
    });

    const uniqueResultEvents = dedupeWorkflowEvents(resultEvents);
    const workflowEvents = input.publish === false
      ? [...uniqueResultEvents, ...evaluation.existingEvents]
      : publishARWorkflowEvents(uniqueResultEvents);
    const dataIntegrity = assessEnterpriseWorkflowIntegrity({
      triggerEvent: evaluation.triggerEvent,
      resultEvents: uniqueResultEvents,
      executionRecords,
      existingEvents: evaluation.existingEvents,
    });
    const status: AREnterpriseWorkflowStatus = dataIntegrity.issues.length > 0
      ? "Blocked"
      : evaluation.matchedRules.length === 0
        ? "Skipped"
        : uniqueResultEvents.length > 0
          ? "Completed"
          : "Skipped";

    return {
      engine: "enterprise_workflow_orchestration_engine",
      workflowId,
      status,
      triggerEvent: evaluation.triggerEvent,
      matchedRules: evaluation.matchedRules,
      skippedRules: evaluation.skippedRules,
      executionRecords,
      resultEvents: uniqueResultEvents,
      workflowEvents,
      notificationRecords: projectWorkflowEventsToNotificationRecords(workflowEvents),
      reportingProjection: projectWorkflowEventsToReportingAnalytics(workflowEvents),
      dataIntegrity,
      consumerTargets: AR_WORKFLOW_EVENT_CONSUMER_TARGETS,
    };
  },
};

export function runEnterpriseWorkflowOrchestration(input: AREnterpriseWorkflowOrchestrationInput): AREnterpriseWorkflowOrchestrationResult {
  return EnterpriseWorkflowOrchestrationEngine.execute(input);
}

export function projectWorkflowEventsToReportingAnalytics(events: ARWorkflowEvent[], filters: ARWorkflowReportingFilter = {}): ARWorkflowReportingProjection {
  const filteredEvents = applyReportingFilters(events, filters);
  const generatedAt = new Date().toISOString();
  const tenantId = filteredEvents[0]?.tenantId || events[0]?.tenantId || "ar-recovery";
  const correlationId = createWorkflowId("correlation", "enterprise_reporting_engine");
  const count = (eventTypes: ARWorkflowEventType[]) => filteredEvents.filter((event) => eventTypes.includes(event.eventType)).length;
  const amountCents = filteredEvents
    .filter((event) => isPaymentWorkflowEventType(event.eventType))
    .reduce((total, event) => total + normalizePaymentAmountCents(event.payload.metadata?.amountCents || toRecord(event.payload.newValue).amountCents), 0);
  const uniqueActors = new Set(filteredEvents.map((event) => event.actor.actorName || event.actor.actorEmail).filter(Boolean)).size;
  const uniqueClients = new Set(filteredEvents.map((event) => event.scope.clientId).filter(Boolean)).size;
  const uniqueCustomers = new Set(filteredEvents.map((event) => event.scope.customerId || event.payload.metadata?.customerNumber || event.payload.metadata?.customerName).filter(Boolean)).size;
  const uniqueAccounts = new Set(filteredEvents.map((event) => event.scope.accountId).filter(Boolean)).size;

  const buildProjection = (
    projectionType: ARReportingPerformanceProjection["projectionType"],
    metrics: ARReportingMetricProjection[],
  ): ARReportingPerformanceProjection => ({
    projectionId: `reporting-${projectionType.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    projectionType,
    metrics,
    generatedAt,
  });

  return {
    engine: "enterprise_reporting_engine",
    tenantId,
    correlationId,
    generatedAt,
    sourceWorkflowEventCount: filteredEvents.length,
    filters,
    workflowActions: AR_REPORTING_WORKFLOW_ACTIONS,
    supportedExportFormats: AR_REPORTING_EXPORT_FORMATS,
    drillDownRoutes: AR_REPORTING_DRILL_DOWN_ROUTES,
    collectorPerformance: buildProjection("Collector Performance", [
      buildReportingMetric("collector-actions", "Collector Actions", filteredEvents.filter((event) => event.visibility.collectorVisible).length, ["CallCompleted", "NoteCreated", "PromiseCreated"], "Collector KPI"),
      buildReportingMetric("active-collectors", "Active Collectors", uniqueActors, ["CallCompleted", "NoteCreated"], "Collector KPI"),
    ]),
    managerPerformance: buildProjection("Manager Performance", [
      buildReportingMetric("manager-visible-events", "Manager Visible Events", filteredEvents.filter((event) => event.visibility.managerVisible).length, ["EscalationCreated", "PromiseBroken"], "Escalation KPI"),
      buildReportingMetric("manager-updates", "Manager Updates", filteredEvents.filter((event) => event.routing.managerUpdateRequired).length, ["EscalationCreated", "EscalationAssigned"], "Escalation KPI"),
    ]),
    clientPerformance: buildProjection("Client Performance", [
      buildReportingMetric("active-clients", "Active Clients", uniqueClients, ["CallCompleted", "PaymentRecorded"], "Client KPI"),
      buildReportingMetric("client-visible-events", "Client Visible Events", filteredEvents.filter((event) => event.visibility.clientVisible).length, ["CustomerNoteCreated", "StatementDelivered"], "Client KPI"),
    ]),
    portfolioPerformance: buildProjection("Portfolio Performance", [
      buildReportingMetric("customer-count", "Customers", uniqueCustomers, ["CallCompleted", "NoteCreated"], "Customer KPI"),
      buildReportingMetric("account-count", "Accounts", uniqueAccounts, ["PromiseCreated", "PaymentRecorded"], "Customer KPI"),
    ]),
    recoveryPerformance: buildProjection("Recovery Performance", [
      buildReportingMetric("recovery-activity", "Recovery Activity", filteredEvents.length, ["CallCompleted", "NoteCreated", "EmailSent", "PaymentRecorded"], "Collector KPI"),
      buildReportingMetric("completed-calls", "Completed Calls", count(["CallCompleted"]), ["CallCompleted"], "Collector KPI"),
      buildReportingMetric("recovery-emails", "Recovery Emails", count(["EmailDrafted", "EmailSent", "EmailDelivered", "EmailFailed"]), ["EmailSent", "EmailFailed"], "Collector KPI"),
      buildReportingMetric("recovery-notes", "Recovery Notes", count(["NoteCreated", "CollectorNoteCreated", "ManagerNoteCreated", "CustomerNoteCreated", "NoteUpdated"]), ["NoteCreated", "ManagerNoteCreated"], "Collector KPI"),
    ]),
    promisePerformance: buildProjection("Promise Performance", [
      buildReportingMetric("promises-created", "Promises Created", count(["PromiseCreated"]), ["PromiseCreated"], "Broken Promise KPI"),
      buildReportingMetric("broken-promises", "Broken Promises", count(["PromiseBroken"]), ["PromiseBroken"], "Broken Promise KPI"),
    ]),
    ticklerPerformance: buildProjection("Tickler Performance", [
      buildReportingMetric("ticklers-created", "Ticklers Created", count(["TicklerCreated"]), ["TicklerCreated"]),
      buildReportingMetric("ticklers-escalated", "Ticklers Escalated", count(["TicklerEscalated"]), ["TicklerEscalated"], "Escalation KPI"),
    ]),
    letterPerformance: buildProjection("Letter Performance", [
      buildReportingMetric("letters-generated", "Letters Generated", count(["LetterGenerated"]), ["LetterGenerated"], "Letter KPI"),
      buildReportingMetric("letters-failed", "Letters Failed", count(["LetterFailed"]), ["LetterFailed"], "Letter KPI"),
    ]),
    statementPerformance: buildProjection("Statement Performance", [
      buildReportingMetric("statements-generated", "Statements Generated", count(["StatementGenerated"]), ["StatementGenerated"], "Statement KPI"),
      buildReportingMetric("statements-failed", "Statements Failed", count(["StatementFailed"]), ["StatementFailed"], "Statement KPI"),
    ]),
    paymentPerformance: buildProjection("Payment Performance", [
      buildReportingMetric("payments-recorded", "Payments Recorded", count(["PaymentRecorded"]), ["PaymentRecorded"], "Payment KPI", amountCents),
      buildReportingMetric("payment-events", "Payment Events", filteredEvents.filter((event) => event.eventCategory === "payment").length, ["PaymentRecorded", "PaymentUpdated"], "Payment KPI"),
    ]),
    escalationPerformance: buildProjection("Escalation Performance", [
      buildReportingMetric("escalations-created", "Escalations Created", count(["EscalationCreated"]), ["EscalationCreated"], "Escalation KPI"),
      buildReportingMetric("escalations-resolved", "Escalations Resolved", count(["EscalationResolved"]), ["EscalationResolved"], "Escalation KPI"),
      buildReportingMetric("manager-escalation-events", "Manager Escalation Events", count(["EscalationCreated", "EscalationAssigned", "EscalationResolved", "EscalationClosed", "EscalationReopened"]), ["EscalationCreated", "EscalationAssigned"], "Escalation KPI"),
    ]),
    assignmentPerformance: buildProjection("Assignment Performance", [
      buildReportingMetric("assignment-updates", "Assignment Updates", filteredEvents.filter((event) => String(event.payload.metadata?.assignment || event.payload.metadata?.assignedTo || "").length > 0).length, ["EscalationAssigned"], "Escalation KPI"),
      buildReportingMetric("assigned-records", "Assigned Records", filteredEvents.filter((event) => event.actor.actorName || event.payload.metadata?.assignedTo).length, ["CallCompleted", "EscalationAssigned"], "Collector KPI"),
    ]),
    activityTrends: buildProjection("Activity Trends", [
      buildReportingMetric("timeline-events", "Timeline Events", filteredEvents.filter((event) => event.routing.timelineRequired).length, ["CallCompleted", "NoteCreated", "EmailSent", "PaymentRecorded", "EscalationCreated"]),
      buildReportingMetric("recovery-activity-events", "Recovery Activity Events", filteredEvents.filter((event) => event.routing.recoveryActivityRequired).length, ["CallCompleted", "NoteCreated", "EmailSent", "PaymentRecorded", "EscalationCreated"]),
    ]),
    agingTrends: buildProjection("Aging Trends", [
      buildReportingMetric("aging-tagged-events", "Aging Tagged Events", filteredEvents.filter((event) => event.payload.metadata?.agingBucket || event.payload.metadata?.aging).length, ["CallCompleted", "PromiseBroken"], "Customer KPI"),
      buildReportingMetric("priority-events", "Priority Events", filteredEvents.filter((event) => event.payload.metadata?.priority).length, ["EscalationCreated", "TicklerEscalated"], "Escalation KPI"),
    ]),
    recoveryTrends: buildProjection("Recovery Trends", [
      buildReportingMetric("payments-and-promises", "Payments and Promises", count(["PaymentRecorded", "PromiseCreated", "PromisePaymentReceived"]), ["PaymentRecorded", "PromiseCreated"], "Payment KPI"),
      buildReportingMetric("risk-signals", "Risk Signals", count(["PromiseBroken", "TicklerEscalated", "EscalationCreated", "LetterFailed", "StatementFailed"]), ["PromiseBroken", "EscalationCreated"], "Escalation KPI"),
    ]),
  };
}

export function buildPortfolioIntakeWorkflowEvents(input: ARWorkflowPortfolioIntakeEventInput): ARWorkflowEvent[] {
  const now = new Date().toISOString();
  return [
    buildPortfolioIntakeWorkflowEvent(input, {
      occurredAt: now,
      correlationId: input.batch?.correlationId || createWorkflowId("correlation", input.batchId),
      title: buildPortfolioIntakeEventTitle(input.eventType, input.portfolioName),
      summary: buildPortfolioIntakeSummary(input),
    }),
  ];
}

export function publishPortfolioIntakeWorkflowAction(input: ARWorkflowPortfolioIntakeEventInput) {
  const intakeEvents = buildPortfolioIntakeWorkflowEvents(input);
  const nextWorkflowEvents = publishARWorkflowEvents(intakeEvents);
  return {
    intakeEvents,
    workflowEvents: nextWorkflowEvents,
    clientExchangeProjection: projectWorkflowEventsToEnterpriseClientExchange(nextWorkflowEvents),
  };
}

export function projectWorkflowEventsToEnterpriseClientExchange(events: ARWorkflowEvent[]): EnterpriseClientExchangeFoundationProjection {
  const intakeEvents = events
    .filter((event) => event.eventCategory === "client_exchange" && isPortfolioIntakeWorkflowEventType(event.eventType))
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));
  const batchesById = new Map<string, PortfolioIntakeBatch>();
  const mappingProfilesById = new Map<string, PortfolioMappingProfile>();
  const approvalRecordsById = new Map<string, PortfolioApprovalRecord>();
  const assignmentPlansById = new Map<string, PortfolioAssignmentPlan>();
  const stagingRecordsById = new Map<string, PortfolioStagingRecord>();
  const validationResultsById = new Map<string, PortfolioValidationResult>();

  intakeEvents.forEach((event) => {
    const batch = toPortfolioIntakeBatch(event.payload.metadata?.batch);
    if (batch) batchesById.set(batch.batchId, { ...batch, status: ENTERPRISE_CLIENT_EXCHANGE_EVENT_CATALOG[event.eventType as ARWorkflowPortfolioIntakeEventType].status });
    const mappingProfile = toPortfolioMappingProfile(event.payload.metadata?.mappingProfile);
    if (mappingProfile) mappingProfilesById.set(mappingProfile.profileId, mappingProfile);
    const approvalRecord = toPortfolioApprovalRecord(event.payload.metadata?.approvalRecord);
    if (approvalRecord) approvalRecordsById.set(approvalRecord.approvalId, approvalRecord);
    const assignmentPlan = toPortfolioAssignmentPlan(event.payload.metadata?.assignmentPlan);
    if (assignmentPlan) assignmentPlansById.set(assignmentPlan.assignmentPlanId, assignmentPlan);
    toPortfolioStagingRecords(event.payload.metadata?.stagingRecords).forEach((record) => stagingRecordsById.set(record.stagingRecordId, record));
    toPortfolioValidationResults(event.payload.metadata?.validationResults).forEach((result) => validationResultsById.set(result.validationId, result));
  });

  return {
    engine: "enterprise_client_exchange",
    lifecycle: PORTFOLIO_IMPORT_STATUS_LIFECYCLE,
    validationCategories: PORTFOLIO_VALIDATION_CATEGORIES,
    projectionTargets: ENTERPRISE_CLIENT_EXCHANGE_PROJECTION_TARGETS,
    batches: Array.from(batchesById.values()),
    stagingRecords: Array.from(stagingRecordsById.values()),
    validationResults: Array.from(validationResultsById.values()),
    mappingProfiles: Array.from(mappingProfilesById.values()),
    approvalRecords: Array.from(approvalRecordsById.values()),
    assignmentPlans: Array.from(assignmentPlansById.values()),
  };
}

export const DuplicateDetectionService = {
  service: "DuplicateDetectionService",
  detect(records: PortfolioStagingRecord[]): PortfolioValidationResult[] {
    return [
      ...buildDuplicateResults(records, "Customer", "customerName", "Duplicate customers"),
      ...buildDuplicateResults(records, "Account", "accountNumber", "Duplicate accounts"),
      ...buildDuplicateResults(records, "Invoice", "invoiceNumber", "Duplicate invoices"),
    ];
  },
};

export const ImportReadinessEvaluator = {
  service: "ImportReadinessEvaluator",
  evaluate(results: PortfolioValidationResult[]): PortfolioImportReadiness {
    if (results.some((result) => result.severity === "Error")) return "Blocked";
    if (results.some((result) => result.severity === "Warning")) return "Ready With Warnings";
    return "Ready";
  },
};

export const ValidationSummaryBuilder = {
  service: "ValidationSummaryBuilder",
  build(records: PortfolioStagingRecord[], results: PortfolioValidationResult[]): PortfolioValidationSummary {
    const errorCount = results.filter((result) => result.severity === "Error").length;
    const warningCount = results.filter((result) => result.severity === "Warning").length;
    const informationCount = results.filter((result) => result.severity === "Information").length;
    const duplicateCount = results.filter((result) => result.category.startsWith("Duplicate")).length;
    const importReadiness = ImportReadinessEvaluator.evaluate(results);
    return {
      totalRecords: records.length,
      validRecords: records.filter((record) => record.validationStatus === "Valid").length,
      errorCount,
      warningCount,
      informationCount,
      duplicateCount,
      customerCount: records.filter((record) => record.recordType === "Customer").length,
      accountCount: records.filter((record) => record.recordType === "Account").length,
      invoiceCount: records.filter((record) => record.recordType === "Invoice").length,
      importReadiness,
      overallValidationStatus: importReadiness === "Blocked" ? "Failed" : importReadiness === "Ready With Warnings" ? "Passed With Warnings" : "Passed",
      isImportReady: importReadiness !== "Blocked",
    };
  },
};

export const PortfolioValidationEngine = {
  engine: "PortfolioValidationEngine",
  validate(input: {
    batch: PortfolioIntakeBatch;
    stagingRecords: PortfolioStagingRecord[];
    knownCollectors?: string[];
    knownManagers?: string[];
  }): PortfolioValidationEngineResult {
    const ruleResults = input.stagingRecords.flatMap((record) => validatePortfolioStagingRecord(record, input.knownCollectors || [], input.knownManagers || []));
    const duplicateResults = DuplicateDetectionService.detect(input.stagingRecords);
    const validationResults = [...ruleResults, ...duplicateResults];
    const validationSummary = ValidationSummaryBuilder.build(input.stagingRecords, validationResults);
    return {
      engine: "PortfolioValidationEngine",
      summaryBuilder: "ValidationSummaryBuilder",
      duplicateService: "DuplicateDetectionService",
      readinessEvaluator: "ImportReadinessEvaluator",
      batchId: input.batch.batchId,
      tenantId: input.batch.tenantId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      validationSummary,
      validationResults,
      projectionTargets: PORTFOLIO_VALIDATION_ENGINE_PROJECTION_TARGETS,
    };
  },
};

export function publishPortfolioValidationWorkflowAction(input: {
  batch: PortfolioIntakeBatch;
  stagingRecords: PortfolioStagingRecord[];
  actor: ARWorkflowActorContext;
  knownCollectors?: string[];
  knownManagers?: string[];
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
}) {
  const validation = PortfolioValidationEngine.validate(input);
  const validationEventType: ARWorkflowPortfolioIntakeEventType = validation.validationSummary.importReadiness === "Blocked" ? "ImportFailed" : "ImportValidated";
  const validationEvents = [
    ...buildPortfolioIntakeWorkflowEvents({
      tenantId: input.batch.tenantId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      batchId: input.batch.batchId,
      portfolioName: input.batch.portfolioName,
      actor: input.actor,
      eventType: "ValidationCompleted",
      batch: { ...input.batch, validationSummary: validation.validationSummary },
      stagingRecords: input.stagingRecords,
      validationResults: validation.validationResults,
      sourceWorkspace: input.sourceWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    }),
    ...buildPortfolioIntakeWorkflowEvents({
      tenantId: input.batch.tenantId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      batchId: input.batch.batchId,
      portfolioName: input.batch.portfolioName,
      actor: input.actor,
      eventType: validationEventType,
      batch: { ...input.batch, validationSummary: validation.validationSummary },
      stagingRecords: input.stagingRecords,
      validationResults: validation.validationResults,
      sourceWorkspace: input.sourceWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    }),
    ...buildValidationSignalEvents(input.batch, input.stagingRecords, validation.validationResults, input.actor, input.sourceWorkspace, input.initiatedFromRoute),
  ];
  const nextWorkflowEvents = publishARWorkflowEvents(validationEvents);
  return {
    validation,
    validationEvents,
    workflowEvents: nextWorkflowEvents,
    clientExchangeProjection: projectWorkflowEventsToEnterpriseClientExchange(nextWorkflowEvents),
  };
}

export const DefaultMappingSuggestionService = {
  service: "DefaultMappingSuggestionService",
  suggest(sourceColumns: string[]): PortfolioMappingSuggestion[] {
    return sourceColumns.flatMap((sourceColumn) => {
      const suggestedTargetField = PORTFOLIO_DEFAULT_MAPPING_SUGGESTIONS[normalizeMappingColumnName(sourceColumn)];
      return suggestedTargetField
        ? [{ sourceColumn, suggestedTargetField, confidence: "deterministic" }]
        : [];
    });
  },
};

export const RequiredFieldDetectionService = {
  service: "RequiredFieldDetectionService",
  detect(columnMappings: PortfolioColumnMapping[]): string[] {
    const mappedTargets = new Set(columnMappings.map((mapping) => mapping.targetField));
    return PORTFOLIO_MAPPING_REQUIRED_FIELDS.filter((field) => !mappedTargets.has(field));
  },
};

export const UnmappedFieldDetectionService = {
  service: "UnmappedFieldDetectionService",
  detect(sourceColumns: string[], columnMappings: PortfolioColumnMapping[]) {
    const mappedSources = new Set(columnMappings.map((mapping) => normalizeMappingColumnName(mapping.sourceColumn)));
    const targetCounts = new Map<string, number>();
    columnMappings.forEach((mapping) => targetCounts.set(mapping.targetField, (targetCounts.get(mapping.targetField) || 0) + 1));
    const supportedTargets = new Set<string>(PORTFOLIO_MAPPING_SUPPORTED_TARGET_FIELDS);

    return {
      unmappedFields: sourceColumns.filter((column) => !mappedSources.has(normalizeMappingColumnName(column))),
      unsupportedSourceColumns: sourceColumns.filter((column) => !PORTFOLIO_DEFAULT_MAPPING_SUGGESTIONS[normalizeMappingColumnName(column)]),
      duplicateTargetMappings: Array.from(targetCounts.entries()).filter(([, count]) => count > 1).map(([targetField]) => targetField),
      invalidTargetFields: columnMappings.map((mapping) => mapping.targetField).filter((targetField) => !supportedTargets.has(targetField)),
    };
  },
};

export const MappingReadinessEvaluator = {
  service: "MappingReadinessEvaluator",
  evaluate(input: {
    missingRequiredFields: string[];
    unmappedFields: string[];
    duplicateTargetMappings: string[];
    invalidTargetFields: string[];
  }): PortfolioMappingReadiness {
    if (input.missingRequiredFields.length > 0 || input.duplicateTargetMappings.length > 0 || input.invalidTargetFields.length > 0) return "Blocked";
    if (input.unmappedFields.length > 0) return "Ready With Warnings";
    return "Ready";
  },
};

export const MappingProfileBuilder = {
  service: "MappingProfileBuilder",
  build(input: {
    tenantId: string;
    clientId: string;
    profileName: string;
    sourceFormat: PortfolioSourceType;
    sourceColumns: string[];
    columnMappings?: PortfolioColumnMapping[];
    createdBy: string;
    updatedBy?: string;
    activeStatus?: "Active" | "Inactive";
    profileId?: string;
  }): PortfolioMappingProfile {
    const now = new Date().toISOString();
    const profileId = input.profileId || createWorkflowId("mapping-profile", input.clientId);
    const suggestions = DefaultMappingSuggestionService.suggest(input.sourceColumns);
    const columnMappings = input.columnMappings?.length
      ? input.columnMappings
      : suggestions.map((suggestion) => ({
        sourceColumn: suggestion.sourceColumn,
        targetField: suggestion.suggestedTargetField,
        required: PORTFOLIO_MAPPING_REQUIRED_FIELDS.includes(suggestion.suggestedTargetField as typeof PORTFOLIO_MAPPING_REQUIRED_FIELDS[number]),
      }));
    const missingRequiredFields = RequiredFieldDetectionService.detect(columnMappings);
    const unmapped = UnmappedFieldDetectionService.detect(input.sourceColumns, columnMappings);

    return {
      profileId,
      mappingProfileId: profileId,
      tenantId: input.tenantId,
      clientId: input.clientId,
      profileName: input.profileName,
      sourceFormat: input.sourceFormat,
      sourceColumns: input.sourceColumns,
      columnMappings,
      requiredFieldsMapped: missingRequiredFields.length === 0,
      unmappedFields: unmapped.unmappedFields,
      createdBy: input.createdBy,
      updatedBy: input.updatedBy || input.createdBy,
      activeStatus: input.activeStatus || "Active",
      createdAt: now,
      updatedAt: now,
    };
  },
};

export const PortfolioMappingEngine = {
  engine: "PortfolioMappingEngine",
  map(input: {
    tenantId: string;
    clientId: string;
    profileName: string;
    sourceFormat: PortfolioSourceType;
    sourceColumns: string[];
    columnMappings?: PortfolioColumnMapping[];
    createdBy: string;
    updatedBy?: string;
    activeStatus?: "Active" | "Inactive";
    profileId?: string;
  }): PortfolioMappingEngineResult {
    const mappingProfile = MappingProfileBuilder.build(input);
    const missingRequiredFields = RequiredFieldDetectionService.detect(mappingProfile.columnMappings);
    const unmapped = UnmappedFieldDetectionService.detect(mappingProfile.sourceColumns || [], mappingProfile.columnMappings);
    const readiness = MappingReadinessEvaluator.evaluate({ missingRequiredFields, ...unmapped });
    const suggestions = DefaultMappingSuggestionService.suggest(mappingProfile.sourceColumns || []);
    const issues = buildPortfolioMappingIssues(mappingProfile, missingRequiredFields, unmapped);

    return {
      engine: "PortfolioMappingEngine",
      profileBuilder: "MappingProfileBuilder",
      requiredFieldDetectionService: "RequiredFieldDetectionService",
      unmappedFieldDetectionService: "UnmappedFieldDetectionService",
      readinessEvaluator: "MappingReadinessEvaluator",
      defaultSuggestionService: "DefaultMappingSuggestionService",
      mappingProfile,
      missingRequiredFields,
      unmappedFields: unmapped.unmappedFields,
      unsupportedSourceColumns: unmapped.unsupportedSourceColumns,
      duplicateTargetMappings: unmapped.duplicateTargetMappings,
      invalidTargetFields: unmapped.invalidTargetFields,
      suggestions,
      readiness,
      issues,
      projectionTargets: PORTFOLIO_MAPPING_ENGINE_PROJECTION_TARGETS,
    };
  },
};

export function publishPortfolioMappingWorkflowAction(input: {
  tenantId: string;
  clientId: string;
  portfolioId: string;
  batchId: string;
  portfolioName: string;
  actor: ARWorkflowActorContext;
  profileName: string;
  sourceFormat: PortfolioSourceType;
  sourceColumns: string[];
  columnMappings?: PortfolioColumnMapping[];
  createdBy: string;
  updatedBy?: string;
  activeStatus?: "Active" | "Inactive";
  profileId?: string;
  batch?: PortfolioIntakeBatch;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
}) {
  const mapping = PortfolioMappingEngine.map(input);
  const batch = input.batch ? { ...input.batch, mappingProfileId: mapping.mappingProfile.mappingProfileId } : undefined;
  const profileEventType: ARWorkflowPortfolioIntakeEventType = input.profileId ? "MappingProfileUpdated" : "MappingProfileCreated";
  const mappingEvents = [
    ...buildPortfolioIntakeWorkflowEvents({
      tenantId: input.tenantId,
      clientId: input.clientId,
      portfolioId: input.portfolioId,
      batchId: input.batchId,
      portfolioName: input.portfolioName,
      actor: input.actor,
      eventType: profileEventType,
      batch,
      mappingProfile: mapping.mappingProfile,
      sourceWorkspace: input.sourceWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    }),
    ...buildPortfolioIntakeWorkflowEvents({
      tenantId: input.tenantId,
      clientId: input.clientId,
      portfolioId: input.portfolioId,
      batchId: input.batchId,
      portfolioName: input.portfolioName,
      actor: input.actor,
      eventType: "ImportMapped",
      batch,
      mappingProfile: mapping.mappingProfile,
      sourceWorkspace: input.sourceWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    }),
    ...buildMappingSignalEvents(input, mapping),
  ];
  const nextWorkflowEvents = publishARWorkflowEvents(mappingEvents);
  return {
    mapping,
    mappingEvents,
    workflowEvents: nextWorkflowEvents,
    clientExchangeProjection: projectWorkflowEventsToEnterpriseClientExchange(nextWorkflowEvents),
  };
}

export const ImportRiskAssessmentService = {
  service: "ImportRiskAssessmentService",
  assess(input: {
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    assignmentPlan?: PortfolioAssignmentPlan;
  }): PortfolioImportRiskLevel {
    if (input.validation.validationSummary.errorCount > 0 || input.mapping.missingRequiredFields.length > 0 || input.mapping.invalidTargetFields.length > 0) return "Critical";
    if (input.validation.validationSummary.duplicateCount > 0 || input.mapping.duplicateTargetMappings.length > 0) return "High";
    if (input.validation.validationSummary.warningCount > 0 || input.mapping.unmappedFields.length > 0 || !input.assignmentPlan?.workQueueEligibility) return "Medium";
    return "Low";
  },
};

export const ApprovalReadinessEvaluator = {
  service: "ApprovalReadinessEvaluator",
  evaluate(input: {
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    approvalRecord?: PortfolioApprovalRecord;
  }): PortfolioApprovalReadiness {
    if (input.validation.validationSummary.importReadiness === "Blocked") return "Blocked";
    if (input.mapping.readiness === "Blocked" || input.mapping.missingRequiredFields.length > 0) return "Blocked";
    if (!input.approvalRecord || input.approvalRecord.approvalStatus !== "Approved") return "Blocked";
    if (input.validation.validationSummary.warningCount > 0 || input.mapping.readiness === "Ready With Warnings" || input.mapping.unmappedFields.length > 0) return "Ready With Warnings";
    return "Ready";
  },
};

export const ApprovalSummaryBuilder = {
  service: "ApprovalSummaryBuilder",
  build(input: {
    batch: PortfolioIntakeBatch;
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    approvalRecord?: PortfolioApprovalRecord;
    assignmentPlan?: PortfolioAssignmentPlan;
    stagingRecords?: PortfolioStagingRecord[];
    clientName?: string;
  }): PortfolioReviewSummary {
    const importRiskLevel = ImportRiskAssessmentService.assess(input);
    const importReadiness = ApprovalReadinessEvaluator.evaluate(input);
    const mappingSummary = buildPortfolioMappingSummary(input.mapping);
    const validationSummary = input.validation.validationSummary;
    const stagingRecords = input.stagingRecords || [];

    return {
      client: input.clientName || input.batch.clientId,
      portfolioName: input.batch.portfolioName,
      batchId: input.batch.batchId,
      submittedBy: input.batch.submittedBy,
      submittedAt: input.batch.submittedAt,
      sourceType: input.batch.sourceType,
      sourceFile: input.batch.sourceFileName,
      recordCount: input.batch.recordCount,
      customerCount: validationSummary.customerCount,
      accountCount: validationSummary.accountCount,
      invoiceCount: validationSummary.invoiceCount,
      totalBalance: calculatePortfolioTotalBalance(stagingRecords),
      validationSummary,
      mappingSummary,
      assignmentReadiness: evaluateAssignmentReadiness(input.assignmentPlan),
      approvalStatus: input.approvalRecord?.approvalStatus || "Pending",
      importReadiness,
      importRiskLevel,
    };
  },
};

export const PortfolioReviewEngine = {
  engine: "PortfolioReviewEngine",
  review(input: {
    batch: PortfolioIntakeBatch;
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    approvalRecord?: PortfolioApprovalRecord;
    assignmentPlan?: PortfolioAssignmentPlan;
    stagingRecords?: PortfolioStagingRecord[];
    clientName?: string;
  }): PortfolioReviewEngineResult {
    const reviewSummary = ApprovalSummaryBuilder.build(input);
    return {
      engine: "PortfolioReviewEngine",
      approvalEngine: "PortfolioApprovalEngine",
      readinessEvaluator: "ApprovalReadinessEvaluator",
      riskAssessmentService: "ImportRiskAssessmentService",
      summaryBuilder: "ApprovalSummaryBuilder",
      batchId: input.batch.batchId,
      tenantId: input.batch.tenantId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      reviewSummary,
      approvalRecord: input.approvalRecord,
      projectionTargets: PORTFOLIO_REVIEW_ENGINE_PROJECTION_TARGETS,
    };
  },
};

export const PortfolioApprovalEngine = {
  engine: "PortfolioApprovalEngine",
  buildApprovalRecord(input: {
    batch: PortfolioIntakeBatch;
    approvalStatus: PortfolioApprovalStatus;
    actor: ARWorkflowActorContext;
    approvalNotes?: string;
    riskLevel: PortfolioImportRiskLevel;
    approvalId?: string;
  }): PortfolioApprovalRecord {
    const now = new Date().toISOString();
    return {
      approvalId: input.approvalId || createWorkflowId("approval", input.batch.batchId),
      batchId: input.batch.batchId,
      tenantId: input.batch.tenantId,
      correlationId: input.batch.correlationId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      approvedBy: input.approvalStatus === "Approved" ? input.actor.actorName : undefined,
      approvedAt: input.approvalStatus === "Approved" ? now : undefined,
      rejectedBy: input.approvalStatus === "Rejected" ? input.actor.actorName : undefined,
      rejectedAt: input.approvalStatus === "Rejected" ? now : undefined,
      approvalStatus: input.approvalStatus,
      approvalNotes: input.approvalNotes,
      riskLevel: input.riskLevel,
    };
  },
  approve(input: Omit<Parameters<typeof PortfolioReviewEngine.review>[0], "approvalRecord"> & {
    actor: ARWorkflowActorContext;
    approvalNotes?: string;
  }): PortfolioReviewEngineResult {
    const riskLevel = ImportRiskAssessmentService.assess(input);
    const approvalRecord = PortfolioApprovalEngine.buildApprovalRecord({
      batch: input.batch,
      approvalStatus: "Approved",
      actor: input.actor,
      approvalNotes: input.approvalNotes,
      riskLevel,
    });
    return PortfolioReviewEngine.review({ ...input, approvalRecord });
  },
  reject(input: Omit<Parameters<typeof PortfolioReviewEngine.review>[0], "approvalRecord"> & {
    actor: ARWorkflowActorContext;
    approvalNotes?: string;
  }): PortfolioReviewEngineResult {
    const riskLevel = ImportRiskAssessmentService.assess(input);
    const approvalRecord = PortfolioApprovalEngine.buildApprovalRecord({
      batch: input.batch,
      approvalStatus: "Rejected",
      actor: input.actor,
      approvalNotes: input.approvalNotes,
      riskLevel,
    });
    return PortfolioReviewEngine.review({ ...input, approvalRecord });
  },
};

export function publishPortfolioApprovalWorkflowAction(input: {
  batch: PortfolioIntakeBatch;
  validation: PortfolioValidationEngineResult;
  mapping: PortfolioMappingEngineResult;
  actor: ARWorkflowActorContext;
  decision: "Approve" | "Reject" | "Require Approval";
  approvalNotes?: string;
  assignmentPlan?: PortfolioAssignmentPlan;
  stagingRecords?: PortfolioStagingRecord[];
  clientName?: string;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
}) {
  const review = input.decision === "Approve"
    ? PortfolioApprovalEngine.approve(input)
    : input.decision === "Reject"
      ? PortfolioApprovalEngine.reject(input)
      : PortfolioReviewEngine.review(input);
  const approvalEvents = buildPortfolioApprovalWorkflowEvents(input, review);
  const nextWorkflowEvents = publishARWorkflowEvents(approvalEvents);
  return {
    review,
    approvalEvents,
    workflowEvents: nextWorkflowEvents,
    clientExchangeProjection: projectWorkflowEventsToEnterpriseClientExchange(nextWorkflowEvents),
  };
}

export const DuplicateProtectionService = {
  service: "DuplicateProtectionService",
  detect(input: {
    batch: PortfolioIntakeBatch;
    stagingRecords: PortfolioStagingRecord[];
    existingCustomerKeys?: string[];
    existingAccountKeys?: string[];
    existingInvoiceKeys?: string[];
    existingBatchIds?: string[];
    existingPortfolioIds?: string[];
  }): PortfolioDuplicateSummary {
    const customerKeys = buildPortfolioDuplicateKeys(input.stagingRecords, "Customer", ["customerName", "customer", "Customer Name"]);
    const accountKeys = buildPortfolioDuplicateKeys(input.stagingRecords, "Account", ["accountNumber", "account", "Account Number"]);
    const invoiceKeys = buildPortfolioDuplicateKeys(input.stagingRecords, "Invoice", ["invoiceNumber", "invoice", "Invoice Number"]);
    const duplicateCustomers = countDuplicatePortfolioKeys(customerKeys, input.existingCustomerKeys);
    const duplicateAccounts = countDuplicatePortfolioKeys(accountKeys, input.existingAccountKeys);
    const duplicateInvoices = countDuplicatePortfolioKeys(invoiceKeys, input.existingInvoiceKeys);
    const duplicateImportBatches = input.existingBatchIds?.includes(input.batch.batchId) ? 1 : 0;
    const duplicatePortfolioIds = input.existingPortfolioIds?.includes(input.batch.portfolioId) ? 1 : 0;
    const duplicateKeys = [
      ...findDuplicatePortfolioKeys(customerKeys, input.existingCustomerKeys).map((key) => `customer:${key}`),
      ...findDuplicatePortfolioKeys(accountKeys, input.existingAccountKeys).map((key) => `account:${key}`),
      ...findDuplicatePortfolioKeys(invoiceKeys, input.existingInvoiceKeys).map((key) => `invoice:${key}`),
      ...(duplicateImportBatches ? [`batch:${input.batch.batchId}`] : []),
      ...(duplicatePortfolioIds ? [`portfolio:${input.batch.portfolioId}`] : []),
    ];

    return {
      duplicateCustomers,
      duplicateAccounts,
      duplicateInvoices,
      duplicateImportBatches,
      duplicatePortfolioIds,
      duplicateRecords: duplicateCustomers + duplicateAccounts + duplicateInvoices + duplicateImportBatches + duplicatePortfolioIds,
      duplicateKeys,
    };
  },
};

export const ImportRollbackService = {
  service: "ImportRollbackService",
  prepare(input: {
    batch: PortfolioIntakeBatch;
    requestedBy: string;
    rollbackReason: string;
    rollbackId?: string;
  }): PortfolioRollbackMetadata {
    return {
      rollbackId: input.rollbackId || createWorkflowId("rollback", input.batch.batchId),
      batchId: input.batch.batchId,
      rollbackReason: input.rollbackReason,
      rollbackRequestedBy: input.requestedBy,
      rollbackRequestedAt: new Date().toISOString(),
      rollbackStatus: "Metadata Prepared",
    };
  },
};

export const ImportExecutionSummaryBuilder = {
  service: "ImportExecutionSummaryBuilder",
  build(input: {
    batch: PortfolioIntakeBatch;
    actor: ARWorkflowActorContext;
    status: PortfolioImportLifecycleStatus;
    stagingRecords: PortfolioStagingRecord[];
    duplicateSummary: PortfolioDuplicateSummary;
    rollbackMetadata?: PortfolioRollbackMetadata;
    importId?: string;
  }): PortfolioImportExecutionRecord {
    const now = new Date().toISOString();
    const failedRecords = input.status === "Failed" ? input.stagingRecords.length : 0;
    const successfulRecords = input.status === "Completed" ? Math.max(input.stagingRecords.length - input.duplicateSummary.duplicateRecords, 0) : 0;
    return {
      importId: input.importId || createWorkflowId("import", input.batch.batchId),
      batchId: input.batch.batchId,
      tenantId: input.batch.tenantId,
      correlationId: input.batch.correlationId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      startedBy: input.actor.actorName,
      startedAt: now,
      completedAt: input.status === "Importing" ? undefined : now,
      status: input.status,
      processedRecords: input.stagingRecords.length,
      successfulRecords,
      failedRecords,
      duplicateRecords: input.duplicateSummary.duplicateRecords,
      rollbackAvailable: input.status === "Completed",
      rollbackId: input.rollbackMetadata?.rollbackId,
      activationStatus: input.status === "Archived" ? "Archived" : "Pending Activation",
    };
  },
};

export const ImportHistoryService = {
  service: "ImportHistoryService",
  create(input: {
    batch: PortfolioIntakeBatch;
    execution: PortfolioImportExecutionRecord;
    completedBy?: string;
  }): PortfolioImportHistoryEntry {
    return {
      importId: input.execution.importId,
      client: input.batch.clientId,
      portfolio: input.batch.portfolioName,
      batch: input.batch.batchId,
      source: input.batch.sourceType,
      startedBy: input.execution.startedBy,
      completedBy: input.completedBy,
      startedAt: input.execution.startedAt,
      completedAt: input.execution.completedAt,
      status: input.execution.status,
      recordCounts: {
        processedRecords: input.execution.processedRecords,
        successfulRecords: input.execution.successfulRecords,
        failedRecords: input.execution.failedRecords,
        duplicateRecords: input.execution.duplicateRecords,
      },
      durationMs: input.execution.completedAt ? Math.max(Date.parse(input.execution.completedAt) - Date.parse(input.execution.startedAt), 0) : undefined,
      rollbackAvailable: input.execution.rollbackAvailable,
    };
  },
};

export const ImportExecutionEngine = {
  engine: "ImportExecutionEngine",
  execute(input: {
    batch: PortfolioIntakeBatch;
    review: PortfolioReviewEngineResult;
    actor: ARWorkflowActorContext;
    stagingRecords: PortfolioStagingRecord[];
    existingCustomerKeys?: string[];
    existingAccountKeys?: string[];
    existingInvoiceKeys?: string[];
    existingBatchIds?: string[];
    existingPortfolioIds?: string[];
  }) {
    const duplicateSummary = DuplicateProtectionService.detect(input);
    const approved = input.review.approvalRecord?.approvalStatus === "Approved" && input.review.reviewSummary.importReadiness !== "Blocked";
    const status: PortfolioImportLifecycleStatus = approved && duplicateSummary.duplicateRecords === 0 ? "Completed" : "Failed";
    const rollbackMetadata = status === "Completed"
      ? ImportRollbackService.prepare({
        batch: input.batch,
        requestedBy: input.actor.actorName,
        rollbackReason: "Rollback metadata prepared during controlled import execution.",
      })
      : undefined;
    const execution = ImportExecutionSummaryBuilder.build({
      batch: input.batch,
      actor: input.actor,
      status,
      stagingRecords: input.stagingRecords,
      duplicateSummary,
      rollbackMetadata,
    });
    const history = ImportHistoryService.create({ batch: input.batch, execution, completedBy: input.actor.actorName });
    return { execution, history, duplicateSummary, rollbackMetadata };
  },
};

export const PortfolioImportEngine = {
  engine: "PortfolioImportEngine",
  execute(input: {
    batch: PortfolioIntakeBatch;
    review: PortfolioReviewEngineResult;
    actor: ARWorkflowActorContext;
    stagingRecords?: PortfolioStagingRecord[];
    existingCustomerKeys?: string[];
    existingAccountKeys?: string[];
    existingInvoiceKeys?: string[];
    existingBatchIds?: string[];
    existingPortfolioIds?: string[];
  }): PortfolioImportEngineResult {
    const executionResult = ImportExecutionEngine.execute({
      ...input,
      stagingRecords: input.stagingRecords || [],
    });
    return {
      engine: "PortfolioImportEngine",
      executionEngine: "ImportExecutionEngine",
      historyService: "ImportHistoryService",
      rollbackService: "ImportRollbackService",
      duplicateProtectionService: "DuplicateProtectionService",
      summaryBuilder: "ImportExecutionSummaryBuilder",
      execution: executionResult.execution,
      history: executionResult.history,
      duplicateSummary: executionResult.duplicateSummary,
      rollbackMetadata: executionResult.rollbackMetadata,
      projectionTargets: PORTFOLIO_IMPORT_ENGINE_PROJECTION_TARGETS,
    };
  },
};

export function publishPortfolioImportWorkflowAction(input: {
  batch: PortfolioIntakeBatch;
  review: PortfolioReviewEngineResult;
  actor: ARWorkflowActorContext;
  stagingRecords?: PortfolioStagingRecord[];
  existingCustomerKeys?: string[];
  existingAccountKeys?: string[];
  existingInvoiceKeys?: string[];
  existingBatchIds?: string[];
  existingPortfolioIds?: string[];
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
}) {
  const importResult = PortfolioImportEngine.execute(input);
  const importEvents = buildPortfolioImportWorkflowEvents(input, importResult);
  const nextWorkflowEvents = publishARWorkflowEvents(importEvents);
  return {
    importResult,
    importEvents,
    workflowEvents: nextWorkflowEvents,
    clientExchangeProjection: projectWorkflowEventsToEnterpriseClientExchange(nextWorkflowEvents),
  };
}

export const AssignmentValidationService = {
  service: "AssignmentValidationService",
  validate(input: {
    assignment: PortfolioAssignmentRecord;
    importResult: PortfolioImportEngineResult;
    knownCollectors?: string[];
    knownManagers?: string[];
    knownTeams?: string[];
    existingAssignmentKeys?: string[];
  }): PortfolioAssignmentValidationResult[] {
    const results: PortfolioAssignmentValidationResult[] = [];
    if (!input.assignment.collectorId) results.push(createAssignmentValidationResult(input.assignment, "Error", "Collector exists", "Collector assignment is required."));
    if (!input.assignment.managerId) results.push(createAssignmentValidationResult(input.assignment, "Error", "Manager exists", "Manager assignment is required."));
    if (!input.assignment.teamId) results.push(createAssignmentValidationResult(input.assignment, "Warning", "Team exists", "Team assignment should be reviewed."));
    if (!input.assignment.portfolioId) results.push(createAssignmentValidationResult(input.assignment, "Error", "Portfolio exists", "Portfolio assignment is required."));
    if (!input.assignment.recoveryStage) results.push(createAssignmentValidationResult(input.assignment, "Error", "Recovery Stage assigned", "Recovery stage is required."));
    if (!input.assignment.priority) results.push(createAssignmentValidationResult(input.assignment, "Error", "Priority assigned", "Priority is required."));
    if (!input.assignment.collectorId || !input.assignment.managerId || !input.assignment.recoveryStage || !input.assignment.priority) {
      results.push(createAssignmentValidationResult(input.assignment, "Error", "Assignment ownership complete", "Assignment ownership is incomplete."));
    }
    if (input.existingAssignmentKeys?.includes(input.assignment.assignmentId)) {
      results.push(createAssignmentValidationResult(input.assignment, "Error", "No duplicate assignments", "Assignment already exists."));
    }
    if (input.importResult.execution.portfolioId !== input.assignment.portfolioId || input.importResult.execution.clientId !== input.assignment.clientId) {
      results.push(createAssignmentValidationResult(input.assignment, "Error", "No orphan assignments", "Assignment must match the imported portfolio and client."));
    }
    if (input.assignment.collectorId && input.knownCollectors?.length && !input.knownCollectors.includes(input.assignment.collectorId)) {
      results.push(createAssignmentValidationResult(input.assignment, "Warning", "Collector exists", "Collector is not in the known collector list."));
    }
    if (input.assignment.managerId && input.knownManagers?.length && !input.knownManagers.includes(input.assignment.managerId)) {
      results.push(createAssignmentValidationResult(input.assignment, "Warning", "Manager exists", "Manager is not in the known manager list."));
    }
    if (input.assignment.teamId && input.knownTeams?.length && !input.knownTeams.includes(input.assignment.teamId)) {
      results.push(createAssignmentValidationResult(input.assignment, "Warning", "Team exists", "Team is not in the known team list."));
    }
    return results;
  },
};

export const WorkQueueEligibilityService = {
  service: "WorkQueueEligibilityService",
  determine(input: {
    assignment: PortfolioAssignmentRecord;
    importResult: PortfolioImportEngineResult;
    validationResults: PortfolioAssignmentValidationResult[];
  }): PortfolioWorkQueueEligibility {
    if (input.validationResults.some((result) => result.severity === "Error")) return "Blocked";
    if (input.importResult.execution.status !== "Completed") return "Pending Activation";
    if (!input.assignment.collectorId || !input.assignment.managerId) return "Pending Assignment";
    return input.assignment.workQueueEligible ? "Eligible" : "Not Eligible";
  },
};

export const ActivationReadinessEvaluator = {
  service: "ActivationReadinessEvaluator",
  evaluate(input: {
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    review: PortfolioReviewEngineResult;
    importResult: PortfolioImportEngineResult;
    assignment: PortfolioAssignmentRecord;
    validationResults: PortfolioAssignmentValidationResult[];
  }): PortfolioAssignmentStatus {
    const hasBlockingAssignmentIssues = input.validationResults.some((result) => result.severity === "Error");
    const importCompleted = input.importResult.execution.status === "Completed";
    const validationPassed = input.validation.validationSummary.importReadiness !== "Blocked";
    const mappingCompleted = input.mapping.readiness !== "Blocked";
    const approvalCompleted = input.review.approvalRecord?.approvalStatus === "Approved";
    const assignmentCompleted = input.assignment.status !== "Blocked" && input.assignment.collectorId && input.assignment.managerId && input.assignment.priority && input.assignment.recoveryStage;
    if (!importCompleted || !validationPassed || !mappingCompleted || !approvalCompleted || !assignmentCompleted || hasBlockingAssignmentIssues) return "Blocked";
    if (input.validation.validationSummary.warningCount > 0 || input.mapping.unmappedFields.length > 0 || input.validationResults.some((result) => result.severity === "Warning")) return "Ready With Warnings";
    return "Ready";
  },
};

export const AssignmentSummaryBuilder = {
  service: "AssignmentSummaryBuilder",
  build(input: {
    assignment: PortfolioAssignmentRecord;
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    review: PortfolioReviewEngineResult;
    importResult: PortfolioImportEngineResult;
    validationResults: PortfolioAssignmentValidationResult[];
    assignedRecordCount: number;
  }): PortfolioAssignmentSummary {
    const workQueueEligibility = WorkQueueEligibilityService.determine(input);
    const activationReadiness = ActivationReadinessEvaluator.evaluate(input);
    const requiredFields = [input.assignment.collectorId, input.assignment.managerId, input.assignment.teamId, input.assignment.priority, input.assignment.recoveryStage];
    const completedFields = requiredFields.filter(Boolean).length;
    return {
      portfolio: input.importResult.history.portfolio,
      collector: input.assignment.collectorId || "Unassigned",
      manager: input.assignment.managerId || "Unassigned",
      team: input.assignment.teamId || "Unassigned",
      priority: input.assignment.priority || "Unassigned",
      recoveryStage: input.assignment.recoveryStage || "Unassigned",
      assignedRecordCount: input.assignedRecordCount,
      assignmentCompletionPercent: Math.round((completedFields / requiredFields.length) * 100),
      workQueueEligibility,
      activationReadiness,
      outstandingAssignmentIssues: input.validationResults.map((result) => result.message),
    };
  },
};

export const AssignmentExecutionEngine = {
  engine: "AssignmentExecutionEngine",
  execute(input: {
    tenantId: string;
    clientId: string;
    portfolioId: string;
    correlationId: string;
    collectorId?: string;
    managerId?: string;
    teamId?: string;
    priority?: string;
    recoveryStage?: string;
    assignedBy: string;
    effectiveDate?: string;
    importResult: PortfolioImportEngineResult;
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    review: PortfolioReviewEngineResult;
    knownCollectors?: string[];
    knownManagers?: string[];
    knownTeams?: string[];
    existingAssignmentKeys?: string[];
  }) {
    const now = new Date().toISOString();
    const assignment: PortfolioAssignmentRecord = {
      assignmentId: createWorkflowId("assignment", input.portfolioId),
      tenantId: input.tenantId,
      correlationId: input.correlationId,
      clientId: input.clientId,
      portfolioId: input.portfolioId,
      collectorId: input.collectorId,
      managerId: input.managerId,
      teamId: input.teamId,
      priority: input.priority,
      recoveryStage: input.recoveryStage,
      workQueueEligible: Boolean(input.collectorId && input.managerId && input.priority && input.recoveryStage),
      assignedBy: input.assignedBy,
      assignedAt: now,
      effectiveDate: input.effectiveDate || now,
      status: "Ready",
    };
    const validationResults = AssignmentValidationService.validate({ ...input, assignment });
    const status: PortfolioAssignmentStatus = validationResults.some((result) => result.severity === "Error")
      ? "Blocked"
      : validationResults.some((result) => result.severity === "Warning") ? "Ready With Warnings" : "Ready";
    const finalizedAssignment = { ...assignment, status, workQueueEligible: status !== "Blocked" && assignment.workQueueEligible };
    const assignmentSummary = AssignmentSummaryBuilder.build({
      assignment: finalizedAssignment,
      validation: input.validation,
      mapping: input.mapping,
      review: input.review,
      importResult: input.importResult,
      validationResults,
      assignedRecordCount: input.importResult.execution.successfulRecords,
    });
    return { assignment: finalizedAssignment, validationResults, assignmentSummary };
  },
};

export const PortfolioAssignmentEngine = {
  engine: "PortfolioAssignmentEngine",
  assign(input: Parameters<typeof AssignmentExecutionEngine.execute>[0]): PortfolioAssignmentEngineResult {
    const result = AssignmentExecutionEngine.execute(input);
    return {
      engine: "PortfolioAssignmentEngine",
      executionEngine: "AssignmentExecutionEngine",
      validationService: "AssignmentValidationService",
      workQueueEligibilityService: "WorkQueueEligibilityService",
      activationReadinessEvaluator: "ActivationReadinessEvaluator",
      summaryBuilder: "AssignmentSummaryBuilder",
      assignment: result.assignment,
      validationResults: result.validationResults,
      assignmentSummary: result.assignmentSummary,
      projectionTargets: PORTFOLIO_ASSIGNMENT_ENGINE_PROJECTION_TARGETS,
    };
  },
};

export function publishPortfolioAssignmentWorkflowAction(input: Parameters<typeof PortfolioAssignmentEngine.assign>[0] & {
  actor: ARWorkflowActorContext;
  batch: PortfolioIntakeBatch;
  portfolioName: string;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
}) {
  const assignmentResult = PortfolioAssignmentEngine.assign(input);
  const assignmentEvents = buildPortfolioAssignmentWorkflowEvents(input, assignmentResult);
  const nextWorkflowEvents = publishARWorkflowEvents(assignmentEvents);
  return {
    assignmentResult,
    assignmentEvents,
    workflowEvents: nextWorkflowEvents,
    clientExchangeProjection: projectWorkflowEventsToEnterpriseClientExchange(nextWorkflowEvents),
  };
}

export const ActivationEligibilityService = {
  service: "ActivationEligibilityService",
  evaluate(input: {
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    review: PortfolioReviewEngineResult;
    importResult: PortfolioImportEngineResult;
    assignmentResult: PortfolioAssignmentEngineResult;
  }): PortfolioOperationalActivationStatus {
    const importCompleted = input.importResult.execution.status === "Completed";
    const validationReady = input.validation.validationSummary.importReadiness === "Ready";
    const mappingReady = input.mapping.readiness === "Ready";
    const approvalApproved = input.review.approvalRecord?.approvalStatus === "Approved";
    const assignmentReady = input.assignmentResult.assignment.status === "Ready";
    const activationReady = input.assignmentResult.assignmentSummary.activationReadiness === "Ready";
    return importCompleted && validationReady && mappingReady && approvalApproved && assignmentReady && activationReady
      ? "Ready For Activation"
      : "Activation Failed";
  },
};

export const OperationalActivationValidator = {
  service: "OperationalActivationValidator",
  validate(input: {
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    review: PortfolioReviewEngineResult;
    importResult: PortfolioImportEngineResult;
    assignmentResult: PortfolioAssignmentEngineResult;
  }): PortfolioOperationalActivationValidationResult[] {
    const results: PortfolioOperationalActivationValidationResult[] = [];
    if (input.importResult.execution.status !== "Completed") results.push(createActivationValidationResult(input.importResult.execution.portfolioId, "Error", "Import Status", "Import must be completed before activation."));
    if (input.validation.validationSummary.importReadiness !== "Ready") results.push(createActivationValidationResult(input.importResult.execution.portfolioId, "Error", "Validation", "Validation must be Ready before activation."));
    if (input.mapping.readiness !== "Ready") results.push(createActivationValidationResult(input.importResult.execution.portfolioId, "Error", "Mapping", "Mapping must be Ready before activation."));
    if (input.review.approvalRecord?.approvalStatus !== "Approved") results.push(createActivationValidationResult(input.importResult.execution.portfolioId, "Error", "Approval", "Approval must be completed before activation."));
    if (input.assignmentResult.assignment.status !== "Ready") results.push(createActivationValidationResult(input.importResult.execution.portfolioId, "Error", "Assignment", "Assignment must be Ready before activation."));
    if (input.assignmentResult.assignmentSummary.activationReadiness !== "Ready") results.push(createActivationValidationResult(input.importResult.execution.portfolioId, "Error", "Activation Readiness", "Assignment activation readiness must be Ready."));
    return results;
  },
};

export const ActivationSummaryBuilder = {
  service: "ActivationSummaryBuilder",
  build(input: {
    action: PortfolioActivationAction;
    activation: PortfolioOperationalActivationRecord;
    validationResults: PortfolioOperationalActivationValidationResult[];
    importResult: PortfolioImportEngineResult;
  }): PortfolioOperationalActivationSummary {
    const blockedRecordCount = input.validationResults.filter((result) => result.severity === "Error").length;
    const activationResult = input.activation.status === "Activated"
      ? "Activated"
      : input.activation.status === "Archived"
        ? "Archived"
        : input.action === "Portfolio.Deactivate" ? "Deactivated" : "Blocked";
    return {
      portfolio: input.importResult.history.portfolio,
      client: input.importResult.history.client,
      activationStatus: input.activation.status,
      activationTime: input.activation.activatedAt,
      activatedBy: input.activation.activatedBy,
      eligibleRecordCount: input.importResult.execution.successfulRecords,
      blockedRecordCount,
      projectionStatus: "Prepared",
      activationResult,
    };
  },
};

export const ActivationExecutionEngine = {
  engine: "ActivationExecutionEngine",
  execute(input: {
    action: PortfolioActivationAction;
    actor: ARWorkflowActorContext;
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    review: PortfolioReviewEngineResult;
    importResult: PortfolioImportEngineResult;
    assignmentResult: PortfolioAssignmentEngineResult;
  }) {
    const now = new Date().toISOString();
    const validationResults = OperationalActivationValidator.validate(input);
    const eligibility = ActivationEligibilityService.evaluate(input);
    const status: PortfolioOperationalActivationStatus = input.action === "Portfolio.Archive"
      ? "Archived"
      : input.action === "Portfolio.Deactivate"
        ? "Pending Activation"
        : validationResults.some((result) => result.severity === "Error") || eligibility !== "Ready For Activation" ? "Activation Failed" : "Activated";
    const activation: PortfolioOperationalActivationRecord = {
      activationId: createWorkflowId("activation", input.importResult.execution.portfolioId, input.action),
      tenantId: input.importResult.execution.tenantId,
      correlationId: input.importResult.execution.correlationId,
      clientId: input.importResult.execution.clientId,
      portfolioId: input.importResult.execution.portfolioId,
      action: input.action,
      requestedBy: input.actor.actorName,
      requestedAt: now,
      activatedBy: status === "Activated" ? input.actor.actorName : undefined,
      activatedAt: status === "Activated" ? now : undefined,
      deactivatedBy: input.action === "Portfolio.Deactivate" ? input.actor.actorName : undefined,
      deactivatedAt: input.action === "Portfolio.Deactivate" ? now : undefined,
      archivedBy: status === "Archived" ? input.actor.actorName : undefined,
      archivedAt: status === "Archived" ? now : undefined,
      status,
    };
    const activationSummary = ActivationSummaryBuilder.build({
      action: input.action,
      activation,
      validationResults,
      importResult: input.importResult,
    });
    return { activation, validationResults, activationSummary };
  },
};

export const PortfolioActivationEngine = {
  engine: "PortfolioActivationEngine",
  execute(input: Parameters<typeof ActivationExecutionEngine.execute>[0]): PortfolioActivationEngineResult {
    const result = ActivationExecutionEngine.execute(input);
    return {
      engine: "PortfolioActivationEngine",
      executionEngine: "ActivationExecutionEngine",
      eligibilityService: "ActivationEligibilityService",
      validator: "OperationalActivationValidator",
      summaryBuilder: "ActivationSummaryBuilder",
      action: input.action,
      activation: result.activation,
      validationResults: result.validationResults,
      activationSummary: result.activationSummary,
      projectionTargets: PORTFOLIO_ACTIVATION_ENGINE_PROJECTION_TARGETS,
    };
  },
};

export function publishPortfolioActivationWorkflowAction(input: Parameters<typeof PortfolioActivationEngine.execute>[0] & {
  batch: PortfolioIntakeBatch;
  portfolioName: string;
  sourceWorkspace?: string;
  initiatedFromRoute?: string;
}) {
  const activationResult = PortfolioActivationEngine.execute(input);
  const activationEvents = buildPortfolioActivationWorkflowEvents(input, activationResult);
  const nextWorkflowEvents = publishARWorkflowEvents(activationEvents);
  return {
    activationResult,
    activationEvents,
    workflowEvents: nextWorkflowEvents,
    clientExchangeProjection: projectWorkflowEventsToEnterpriseClientExchange(nextWorkflowEvents),
  };
}

export function projectWorkflowEventsToNotificationRecords(events: ARWorkflowEvent[]): ARWorkflowNotificationProjection[] {
  return events
    .flatMap((event) => {
      const rule = AR_NOTIFICATION_PROJECTION_CATALOG[event.eventType];
      if (!rule) return [];
      return rule.recipientRoles.flatMap((recipientRole) =>
        rule.deliveryChannels.map((deliveryChannel) =>
          buildNotificationProjection(event, rule, recipientRole, deliveryChannel),
        ),
      );
    })
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

function buildOrchestratedResultEvents(input: {
  action: AREnterpriseWorkflowActionDefinition;
  actor: ARWorkflowActorContext;
  rule: AREnterpriseWorkflowRuleDefinition;
  source: string;
  triggerEvent: ARWorkflowEvent;
  workflowId: string;
  parentWorkflowId?: string;
  retryCount: number;
}): ARWorkflowEvent[] {
  const entityId = String(resolveWorkflowFieldPath(input.triggerEvent, input.action.entityIdPath || "entity.entityId") || input.triggerEvent.entity?.entityId || input.triggerEvent.scope.accountId || input.triggerEvent.id);
  const entityType = input.action.entityType || input.triggerEvent.entity?.entityType || "account";
  const baseInput: ARWorkflowOperationalEventInput = {
    tenantId: input.triggerEvent.tenantId,
    clientId: input.triggerEvent.scope.clientId,
    customerId: input.triggerEvent.scope.customerId,
    accountId: input.triggerEvent.scope.accountId,
    entityId,
    entityType,
    customerName: String(input.triggerEvent.payload.metadata?.customerName || ""),
    customerNumber: String(input.triggerEvent.payload.metadata?.customerNumber || ""),
    actor: input.actor,
    sourceWorkspace: input.source,
    initiatedFromRoute: input.triggerEvent.workspace.initiatedFromRoute,
    parentEventId: input.triggerEvent.id,
    title: input.action.title,
    summary: input.action.summary,
    metadata: {
      ...input.action.metadata,
      workflowId: input.workflowId,
      ruleId: input.rule.ruleId,
      ruleName: input.rule.ruleName,
      orchestrationCategory: input.rule.category,
      triggerEventId: input.triggerEvent.id,
      triggerEventType: input.triggerEvent.eventType,
      parentWorkflowId: input.parentWorkflowId,
      retryCount: input.retryCount,
      workflowEngine: "enterprise_workflow_orchestration_engine",
    },
  };

  switch (input.action.actionKind) {
    case "PublishCommunicationEvent":
    case "PublishNotificationEvent":
      if (!isCommunicationWorkflowEventType(input.action.resultEventType)) return [];
      return buildCommunicationWorkflowEvents({
        ...baseInput,
        eventType: input.action.resultEventType,
        channel: normalizeOrchestrationChannel(input.action.metadata?.channel),
        recipient: String(input.action.metadata?.recipient || ""),
      });
    case "PublishTaskEvent":
      if (!isTaskWorkflowEventType(input.action.resultEventType)) return [];
      return buildTaskWorkflowEvents({
        ...baseInput,
        eventType: input.action.resultEventType,
        assignedTo: String(input.action.metadata?.assignedTo || ""),
        dueAt: String(input.action.metadata?.dueAt || ""),
      });
    case "PublishDocumentRequestEvent":
      if (!isDocumentRequestWorkflowEventType(input.action.resultEventType)) return [];
      return buildDocumentRequestWorkflowEvents({
        ...baseInput,
        eventType: input.action.resultEventType,
        documentRequestType: String(input.action.metadata?.documentRequestType || ""),
      });
    case "PublishManagerEvent":
      if (!isManagerWorkflowEventType(input.action.resultEventType)) return [];
      return buildManagerWorkflowEvents({
        ...baseInput,
        eventType: input.action.resultEventType,
        managerAction: String(input.action.metadata?.managerAction || input.action.resultEventType),
      });
    case "PublishRecoveryEvent":
      if (!isNoteWorkflowEventType(input.action.resultEventType)) return [];
      return [buildRecoveryOrchestrationWorkflowEvent(baseInput, input.action.resultEventType)];
    default:
      return [];
  }
}

function buildRecoveryOrchestrationWorkflowEvent(input: ARWorkflowOperationalEventInput, eventType: ARWorkflowNoteEventType): ARWorkflowEvent {
  return normalizeWorkflowEventContract({
    id: createWorkflowId("workflow-event", input.entityId, eventType),
    tenantId: input.tenantId,
    correlationId: createWorkflowId("correlation", input.entityId),
    parentEventId: input.parentEventId,
    eventType,
    eventFamily: "business",
    eventCategory: "note",
    entity: { entityType: input.entityType, entityId: input.entityId },
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: new Date().toISOString(),
    workspace: {
      sourceWorkspace: input.sourceWorkspace || "Enterprise Workflow Orchestration Engine",
      ownerWorkspace: "Recovery Activity",
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
      promiseId: input.promiseId,
      ticklerId: input.ticklerId,
      letterId: input.letterId,
      statementId: input.statementId,
      paymentId: input.paymentId,
      escalationId: input.escalationId,
      documentRequestId: input.documentRequestId,
      managerReviewId: input.managerReviewId,
    },
    payload: {
      title: input.title || "Recovery event published",
      summary: input.summary || "Recovery activity published through enterprise workflow orchestration.",
      previousValue: input.previousValue,
      newValue: input.newValue,
      metadata: {
        ...input.metadata,
        workflowAction: "Workflow.PublishRecoveryEvent",
        customerName: input.customerName,
        customerNumber: input.customerNumber,
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: false,
      clientPortalProjectionRequired: false,
      notificationRequired: shouldProjectNotification(eventType),
      auditRequired: true,
      reportingRequired: true,
    },
    visibility: {
      internal: true,
      clientVisible: false,
      managerVisible: false,
      collectorVisible: true,
      systemVisible: false,
    },
  });
}

function evaluateWorkflowCondition(event: ARWorkflowEvent, condition: AREnterpriseWorkflowCondition) {
  const actualValue = resolveWorkflowFieldPath(event, condition.fieldPath);
  switch (condition.operator) {
    case "equals":
      return actualValue === condition.value;
    case "not_equals":
      return actualValue !== condition.value;
    case "in":
      return Array.isArray(condition.value) && condition.value.includes(actualValue);
    case "not_in":
      return Array.isArray(condition.value) && !condition.value.includes(actualValue);
    case "exists":
      return actualValue !== undefined && actualValue !== null && actualValue !== "";
    case "not_exists":
      return actualValue === undefined || actualValue === null || actualValue === "";
    case "greater_than":
      return Number(actualValue) > Number(condition.value);
    case "greater_than_or_equal":
      return Number(actualValue) >= Number(condition.value);
    case "less_than":
      return Number(actualValue) < Number(condition.value);
    case "less_than_or_equal":
      return Number(actualValue) <= Number(condition.value);
    default:
      return false;
  }
}

function resolveWorkflowFieldPath(event: ARWorkflowEvent, fieldPath: string): unknown {
  const sources: Record<string, unknown> = {
    event,
    metadata: event.payload.metadata || {},
    payload: event.payload,
    scope: event.scope,
    entity: event.entity || {},
    routing: event.routing,
    visibility: event.visibility,
    workspace: event.workspace,
    actor: event.actor,
  };
  const path = fieldPath.startsWith("event.") ? fieldPath : `event.${fieldPath}`;
  return path.split(".").reduce<unknown>((current, part, index) => {
    if (index === 0) return sources[part];
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[part];
  }, undefined);
}

function hasRuleExceededExecutionLimit(rule: AREnterpriseWorkflowRuleDefinition, triggerEvent: ARWorkflowEvent, existingEvents: ARWorkflowEvent[]) {
  if (!rule.maxExecutionsPerCorrelation) return false;
  const executionCount = existingEvents.filter((event) =>
    event.correlationId === triggerEvent.correlationId
      && event.payload.metadata?.ruleId === rule.ruleId,
  ).length;
  return executionCount >= rule.maxExecutionsPerCorrelation;
}

function dedupeWorkflowEvents(events: ARWorkflowEvent[]) {
  const seen = new Set<string>();
  return events.filter((event) => {
    const key = [
      event.eventType,
      event.parentEventId || "",
      event.payload.metadata?.ruleId || "",
      event.entity?.entityType || "",
      event.entity?.entityId || "",
    ].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function assessEnterpriseWorkflowIntegrity(input: {
  triggerEvent: ARWorkflowEvent;
  resultEvents: ARWorkflowEvent[];
  executionRecords: AREnterpriseWorkflowExecutionRecord[];
  existingEvents: ARWorkflowEvent[];
}): AREnterpriseWorkflowIntegrityAssessment {
  const issues: string[] = [];
  const duplicateKeys = new Set<string>();
  const duplicateEventPublishing = input.resultEvents.some((event) => {
    const key = `${event.eventType}|${event.parentEventId || ""}|${event.payload.metadata?.ruleId || ""}|${event.entity?.entityId || ""}`;
    if (duplicateKeys.has(key)) return true;
    duplicateKeys.add(key);
    return input.existingEvents.some((existingEvent) =>
      existingEvent.eventType === event.eventType
        && existingEvent.parentEventId === event.parentEventId
        && existingEvent.payload.metadata?.ruleId === event.payload.metadata?.ruleId
        && existingEvent.entity?.entityId === event.entity?.entityId,
    );
  });
  const circularWorkflowDetected = input.resultEvents.some((event) => event.eventType === input.triggerEvent.eventType && event.entity?.entityId === input.triggerEvent.entity?.entityId);
  const orphanWorkflowChains = input.resultEvents.some((event) => event.parentEventId !== input.triggerEvent.id);
  const brokenRelationships = input.resultEvents.some((event) => !event.tenantId || !event.correlationId || !event.entity?.entityId || !event.entity.entityType);
  const inconsistentWorkflowState = input.executionRecords.some((record) => record.workflowStatus === "Completed" && record.resultEventIds.length === 0);
  const duplicateWorkflowExecution = input.executionRecords.some((record, index, records) =>
    records.findIndex((candidate) => candidate.ruleId === record.ruleId && candidate.triggerEventId === record.triggerEventId) !== index,
  );

  if (duplicateWorkflowExecution) issues.push("Duplicate workflow execution detected.");
  if (duplicateEventPublishing) issues.push("Duplicate event publishing detected.");
  if (circularWorkflowDetected) issues.push("Circular workflow detected.");
  if (orphanWorkflowChains) issues.push("Orphan workflow chain detected.");
  if (brokenRelationships) issues.push("Broken workflow event relationship detected.");
  if (inconsistentWorkflowState) issues.push("Inconsistent workflow state detected.");

  return {
    duplicateWorkflowExecution,
    duplicateEventPublishing,
    circularWorkflowDetected,
    orphanWorkflowChains,
    brokenRelationships,
    inconsistentWorkflowState,
    issues,
  };
}

function normalizeOrchestrationChannel(value: unknown): ARNotificationDeliveryChannel | undefined {
  return AR_NOTIFICATION_DELIVERY_CHANNELS.includes(value as ARNotificationDeliveryChannel) ? value as ARNotificationDeliveryChannel : undefined;
}

function isCommunicationWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowCommunicationEventType {
  return eventType in AR_COMMUNICATION_WORKFLOW_EVENT_CATALOG;
}

function isTaskWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowTaskEventType {
  return eventType in AR_TASK_WORKFLOW_EVENT_CATALOG;
}

function isDocumentRequestWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowDocumentRequestEventType {
  return eventType in AR_DOCUMENT_REQUEST_WORKFLOW_EVENT_CATALOG;
}

function isManagerWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowManagerEventType {
  return eventType in AR_MANAGER_WORKFLOW_EVENT_CATALOG;
}

function isNoteWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowNoteEventType {
  return eventType in AR_NOTE_WORKFLOW_EVENT_CATALOG;
}

function buildOperationalWorkflowEvent(
  input: ARWorkflowOperationalEventInput,
  options: {
    eventType: ARWorkflowCommunicationEventType | ARWorkflowTaskEventType | ARWorkflowDocumentRequestEventType | ARWorkflowManagerEventType;
    eventCategory: "communication" | "task" | "document_request" | "manager";
    catalogEntry: ARWorkflowOperationalEventCatalogEntry;
  },
): ARWorkflowEvent {
  const occurredAt = new Date().toISOString();
  const entity = {
    entityType: input.entityType || options.catalogEntry.defaultEntityType,
    entityId: input.entityId,
  };
  const operationalMetadata = input as ARWorkflowOperationalEventInput & {
    channel?: ARNotificationDeliveryChannel;
    recipient?: string;
    assignedTo?: string;
    dueAt?: string;
    documentRequestType?: string;
    managerAction?: string;
  };
  const metadata = {
    workflowAction: options.catalogEntry.label,
    requiredPermission: options.catalogEntry.requiredPermission,
    customerName: input.customerName,
    customerNumber: input.customerNumber,
    parentEventId: input.parentEventId,
    channel: operationalMetadata.channel,
    recipient: operationalMetadata.recipient,
    assignedTo: operationalMetadata.assignedTo,
    dueAt: operationalMetadata.dueAt,
    documentRequestType: operationalMetadata.documentRequestType,
    managerAction: operationalMetadata.managerAction,
    ...input.metadata,
  };

  return normalizeWorkflowEventContract({
    id: createWorkflowId("workflow-event", input.entityId, options.eventType),
    tenantId: input.tenantId,
    correlationId: createWorkflowId("correlation", input.entityId),
    parentEventId: input.parentEventId,
    eventType: options.eventType,
    eventFamily: "business",
    eventCategory: options.eventCategory,
    entity,
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || options.catalogEntry.ownerWorkspace,
      ownerWorkspace: options.catalogEntry.ownerWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
      promiseId: input.promiseId,
      ticklerId: input.ticklerId,
      letterId: input.letterId,
      statementId: input.statementId,
      paymentId: input.paymentId,
      escalationId: input.escalationId,
      documentRequestId: input.documentRequestId,
      managerReviewId: input.managerReviewId,
    },
    payload: {
      title: input.title || `${options.catalogEntry.label}${input.customerName ? ` for ${input.customerName}` : ""}`,
      summary: input.summary || `${options.catalogEntry.label} recorded as an immutable workflow event.`,
      previousValue: input.previousValue,
      newValue: input.newValue,
      metadata,
    },
    metadata,
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: options.catalogEntry.managerUpdateRequired,
      clientPortalProjectionRequired: options.catalogEntry.clientPortalProjectionRequired,
      notificationRequired: options.catalogEntry.notificationRequired,
      auditRequired: options.catalogEntry.auditRequired,
      reportingRequired: true,
    },
    visibility: {
      internal: !options.catalogEntry.clientPortalProjectionRequired,
      clientVisible: options.catalogEntry.clientPortalProjectionRequired,
      managerVisible: options.catalogEntry.managerUpdateRequired,
      collectorVisible: true,
      systemVisible: false,
    },
  });
}

function buildCallWorkflowEvent(
  input: ARWorkflowCallEventInput,
  options: {
    eventType: ARWorkflowCallEventType;
    occurredAt: string;
    correlationId: string;
    title: string;
    summary: string;
    managerUpdateRequired: boolean;
  },
): ARWorkflowEvent {
  const catalogEntry = AR_CALL_WORKFLOW_EVENT_CATALOG[options.eventType];
  return {
    id: createWorkflowId("workflow-event", input.accountId, options.eventType),
    tenantId: input.tenantId,
    correlationId: options.correlationId,
    eventType: options.eventType,
    eventFamily: "business",
    eventCategory: "call",
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: options.occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || "Recovery Workbench",
      ownerWorkspace: catalogEntry.ownerWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
      promiseId: input.promiseId,
    },
    payload: {
      title: options.title,
      summary: options.summary,
      metadata: {
        workflowAction: "Call.Log",
        customerName: input.customerName,
        customerNumber: input.customerNumber,
        callDirection: input.callDirection || "outbound",
        phoneNumber: input.phoneNumber,
        collectorAssignment: input.collectorAssignment || input.actor.actorName,
        relatedPromiseId: input.promiseId,
        outcome: input.outcome,
        durationMinutes: input.durationMinutes,
        notes: input.notes,
        nextAction: input.nextAction,
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: options.managerUpdateRequired,
      clientPortalProjectionRequired: false,
      notificationRequired: shouldProjectNotification(options.eventType),
      auditRequired: catalogEntry.auditRequired,
      reportingRequired: true,
    },
    visibility: {
      internal: true,
      clientVisible: false,
      managerVisible: options.managerUpdateRequired,
      collectorVisible: true,
      systemVisible: false,
    },
  };
}

function buildNoteWorkflowEvent(
  input: ARWorkflowNoteEventInput,
  options: {
    eventType: ARWorkflowNoteEventType;
    occurredAt: string;
    correlationId: string;
    title: string;
    summary: string;
    managerUpdateRequired: boolean;
    workflowAction: "Note.Create" | "Note.Update";
  },
): ARWorkflowEvent {
  const catalogEntry = AR_NOTE_WORKFLOW_EVENT_CATALOG[options.eventType];
  const clientVisible = input.visibility === "client_visible";
  const managerVisible = input.visibility === "manager_only" || options.managerUpdateRequired;
  return {
    id: createWorkflowId("workflow-event", input.accountId, options.eventType),
    tenantId: input.tenantId,
    correlationId: options.correlationId,
    eventType: options.eventType,
    eventFamily: "business",
    eventCategory: "note",
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: options.occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || "Recovery Workbench",
      ownerWorkspace: catalogEntry.ownerWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
      promiseId: input.promiseId,
    },
    payload: {
      title: options.title,
      summary: options.summary,
      previousValue: input.previousValue,
      newValue: {
        noteType: input.noteType,
        visibility: input.visibility,
        content: input.content,
        outcome: input.outcome,
        nextAction: input.nextAction,
      },
      metadata: {
        workflowAction: options.workflowAction,
        customerName: input.customerName,
        customerNumber: input.customerNumber,
        noteType: input.noteType,
        visibility: input.visibility,
        content: input.content,
        outcome: input.outcome,
        nextAction: input.nextAction,
        relatedRecordType: input.relatedRecordType || "account",
        relatedRecordId: input.relatedRecordId || input.escalationId || input.ticklerId || input.promiseId || input.accountId,
        relatedPromiseId: input.promiseId,
        relatedTicklerId: input.ticklerId,
        relatedEscalationId: input.escalationId,
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: options.managerUpdateRequired,
      clientPortalProjectionRequired: clientVisible,
      notificationRequired: shouldProjectNotification(options.eventType),
      auditRequired: catalogEntry.auditRequired,
      reportingRequired: true,
    },
    visibility: {
      internal: input.visibility !== "client_visible",
      clientVisible,
      managerVisible,
      collectorVisible: input.visibility !== "manager_only",
      systemVisible: false,
    },
  };
}

function buildPromiseWorkflowEvent(
  input: ARWorkflowPromiseEventInput,
  options: {
    eventType: ARWorkflowPromiseEventType;
    occurredAt: string;
    correlationId: string;
    title: string;
    summary: string;
    managerUpdateRequired: boolean;
  },
): ARWorkflowEvent {
  const catalogEntry = AR_PROMISE_WORKFLOW_EVENT_CATALOG[options.eventType];
  const clientVisible = false;
  return {
    id: createWorkflowId("workflow-event", input.promiseId, options.eventType),
    tenantId: input.tenantId,
    correlationId: options.correlationId,
    eventType: options.eventType,
    eventFamily: "business",
    eventCategory: "promise",
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: options.occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || AR_PROMISE_WORKFLOW_ACTION_CATALOG[input.action].sourceWorkspace,
      ownerWorkspace: catalogEntry.ownerWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
      promiseId: input.promiseId,
    },
    payload: {
      title: options.title,
      summary: options.summary,
      previousValue: input.previousValue,
      newValue: input.promiseRecord,
      metadata: {
        workflowAction: input.action,
        promiseId: input.promiseId,
        customerName: input.customerName,
        customerNumber: input.customerNumber,
        promiseStatus: String(input.promiseRecord.status || ""),
        promiseAmountCents: input.promiseRecord.amountCents,
        promiseDate: input.promiseRecord.promiseDate,
        promiseType: input.promiseRecord.promiseType,
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: options.managerUpdateRequired,
      clientPortalProjectionRequired: clientVisible,
      notificationRequired: shouldProjectNotification(options.eventType),
      auditRequired: catalogEntry.auditRequired,
      reportingRequired: true,
    },
    visibility: {
      internal: true,
      clientVisible,
      managerVisible: options.managerUpdateRequired,
      collectorVisible: true,
      systemVisible: false,
    },
  };
}

function buildTicklerWorkflowEvent(
  input: ARWorkflowTicklerEventInput,
  options: {
    eventType: ARWorkflowTicklerEventType;
    occurredAt: string;
    correlationId: string;
    title: string;
    summary: string;
    managerUpdateRequired: boolean;
  },
): ARWorkflowEvent {
  const catalogEntry = AR_TICKLER_WORKFLOW_EVENT_CATALOG[options.eventType];
  const clientVisible = false;
  return {
    id: createWorkflowId("workflow-event", input.ticklerId, options.eventType),
    tenantId: input.tenantId,
    correlationId: options.correlationId,
    eventType: options.eventType,
    eventFamily: "business",
    eventCategory: "tickler",
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: options.occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || AR_TICKLER_WORKFLOW_ACTION_CATALOG[input.action].sourceWorkspace,
      ownerWorkspace: catalogEntry.ownerWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
      ticklerId: input.ticklerId,
    },
    payload: {
      title: options.title,
      summary: options.summary,
      previousValue: input.previousValue,
      newValue: input.ticklerRecord,
      metadata: {
        workflowAction: input.action,
        ticklerId: input.ticklerId,
        customerName: input.customerName,
        customerNumber: input.customerNumber,
        ticklerStatus: String(input.ticklerRecord.status || ""),
        followUpType: input.ticklerRecord.ticklerType,
        dueDate: input.ticklerRecord.dueDate,
        priority: input.ticklerRecord.priority,
        relatedPromise: input.ticklerRecord.relatedPromise,
        relatedPromiseId: input.ticklerRecord.relatedPromiseId || input.ticklerRecord.promiseId || input.ticklerRecord.relatedPromise,
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: options.managerUpdateRequired,
      clientPortalProjectionRequired: clientVisible,
      notificationRequired: shouldProjectNotification(options.eventType),
      auditRequired: catalogEntry.auditRequired,
      reportingRequired: true,
    },
    visibility: {
      internal: true,
      clientVisible,
      managerVisible: options.managerUpdateRequired,
      collectorVisible: true,
      systemVisible: false,
    },
  };
}

function buildDocumentWorkflowEvent(
  input: ARWorkflowDocumentEventInput,
  options: {
    eventType: ARWorkflowDocumentEventType;
    occurredAt: string;
    correlationId: string;
    title: string;
    summary: string;
    managerUpdateRequired: boolean;
  },
): ARWorkflowEvent {
  const catalogEntry = AR_DOCUMENT_WORKFLOW_EVENT_CATALOG[options.eventType];
  const clientVisible = false;
  return {
    id: createWorkflowId("workflow-event", input.documentId, options.eventType),
    tenantId: input.tenantId,
    correlationId: options.correlationId,
    eventType: options.eventType,
    eventFamily: "business",
    eventCategory: "document",
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: options.occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || AR_DOCUMENT_WORKFLOW_ACTION_CATALOG[input.action].sourceWorkspace,
      ownerWorkspace: catalogEntry.ownerWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
      letterId: input.documentType === "letter" ? input.documentId : undefined,
      statementId: input.documentType === "statement" ? input.documentId : undefined,
    },
    payload: {
      title: options.title,
      summary: options.summary,
      previousValue: input.previousValue,
      newValue: input.documentRecord,
      metadata: {
        workflowAction: input.action,
        documentId: input.documentId,
        documentType: input.documentType,
        customerName: input.customerName,
        customerNumber: input.customerNumber,
        documentStatus: String(input.documentRecord.status || ""),
        deliveryStatus: String(input.documentRecord.deliveryStatus || ""),
        template: input.documentRecord.template,
        recipient: input.documentRecord.recipient || input.documentRecord.sentRecipient,
        providerMessageId: input.documentRecord.providerMessageId,
        providerStatus: input.documentRecord.providerStatus,
        deliveryError: input.documentRecord.deliveryError,
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: options.managerUpdateRequired,
      clientPortalProjectionRequired: clientVisible,
      notificationRequired: shouldProjectNotification(options.eventType),
      auditRequired: catalogEntry.auditRequired,
      reportingRequired: true,
    },
    visibility: {
      internal: true,
      clientVisible,
      managerVisible: options.managerUpdateRequired,
      collectorVisible: true,
      systemVisible: false,
    },
  };
}

function buildEscalationWorkflowEvent(
  input: ARWorkflowEscalationEventInput,
  options: {
    eventType: ARWorkflowEscalationEventType;
    occurredAt: string;
    correlationId: string;
    title: string;
    summary: string;
  },
): ARWorkflowEvent {
  const catalogEntry = AR_ESCALATION_WORKFLOW_EVENT_CATALOG[options.eventType];
  const actionEntry = AR_ESCALATION_WORKFLOW_ACTION_CATALOG[input.action];
  return {
    id: createWorkflowId("workflow-event", input.escalationId, options.eventType),
    tenantId: input.tenantId,
    correlationId: options.correlationId,
    eventType: options.eventType,
    eventFamily: "business",
    eventCategory: "escalation",
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: options.occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || actionEntry.sourceWorkspace,
      ownerWorkspace: catalogEntry.ownerWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
    },
    payload: {
      title: options.title,
      summary: options.summary,
      previousValue: input.previousValue,
      newValue: {
        ...input.escalationRecord,
        status: String(input.escalationRecord.status || catalogEntry.lifecycleStatus),
        updatedAt: String(input.escalationRecord.updatedAt || options.occurredAt),
        createdAt: String(input.escalationRecord.createdAt || options.occurredAt),
      },
      metadata: {
        workflowAction: input.action,
        requiredPermission: actionEntry.requiredPermission,
        escalationId: input.escalationId,
        customerName: input.customerName,
        customerNumber: input.customerNumber,
        escalationStatus: String(input.escalationRecord.status || catalogEntry.lifecycleStatus),
        lifecycleStatus: catalogEntry.lifecycleStatus,
        priority: input.escalationRecord.priority,
        reason: input.escalationRecord.reason,
        assignedTo: input.escalationRecord.assignedTo,
        resolution: input.escalationRecord.resolution,
        resolvedAt: input.escalationRecord.resolvedAt,
        relatedPromiseId: input.promiseId || input.escalationRecord.promiseId,
        relatedTicklerId: input.ticklerId || input.escalationRecord.ticklerId,
        sourceTrigger: input.escalationRecord.sourceTrigger,
        sourceEventType: input.escalationRecord.sourceEventType,
        sourceRecordType: input.escalationRecord.sourceRecordType,
        sourceRecordId: input.escalationRecord.sourceRecordId,
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: catalogEntry.managerUpdateRequired,
      clientPortalProjectionRequired: false,
      notificationRequired: shouldProjectNotification(options.eventType),
      auditRequired: catalogEntry.auditRequired,
      reportingRequired: true,
    },
    visibility: {
      internal: true,
      clientVisible: false,
      managerVisible: true,
      collectorVisible: true,
      systemVisible: false,
    },
  };
}

function buildPaymentWorkflowEvent(
  input: ARWorkflowPaymentEventInput,
  options: {
    eventType: ARWorkflowPaymentEventType;
    occurredAt: string;
    correlationId: string;
    title: string;
    summary: string;
  },
): ARWorkflowEvent {
  const catalogEntry = AR_PAYMENT_WORKFLOW_EVENT_CATALOG[options.eventType];
  const actionEntry = AR_PAYMENT_WORKFLOW_ACTION_CATALOG[input.action];
  return {
    id: createWorkflowId("workflow-event", input.paymentId, options.eventType),
    tenantId: input.tenantId,
    correlationId: options.correlationId,
    eventType: options.eventType,
    eventFamily: "business",
    eventCategory: "payment",
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: options.occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || actionEntry.sourceWorkspace,
      ownerWorkspace: catalogEntry.ownerWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
      promiseId: input.promiseId,
      paymentId: input.paymentId,
    },
    payload: {
      title: options.title,
      summary: options.summary,
      previousValue: input.previousValue,
      newValue: {
        ...input.paymentRecord,
        status: String(input.paymentRecord.status || catalogEntry.lifecycleStatus),
        lifecycleStatus: catalogEntry.lifecycleStatus,
        updatedAt: String(input.paymentRecord.updatedAt || options.occurredAt),
        recordedAt: String(input.paymentRecord.recordedAt || input.paymentRecord.createdAt || options.occurredAt),
      },
      metadata: {
        workflowAction: input.action,
        requiredPermission: actionEntry.requiredPermission,
        paymentId: input.paymentId,
        customerName: input.customerName,
        customerNumber: input.customerNumber,
        paymentStatus: String(input.paymentRecord.status || catalogEntry.lifecycleStatus),
        lifecycleStatus: catalogEntry.lifecycleStatus,
        amountCents: normalizePaymentAmountCents(input.paymentRecord.amountCents),
        paymentMethod: input.paymentRecord.paymentMethod,
        paymentDate: input.paymentRecord.paymentDate || input.paymentRecord.recordedAt || input.paymentRecord.createdAt,
        applicationScope: input.paymentRecord.applicationScope,
        appliedToPromiseId: input.promiseId || input.paymentRecord.appliedToPromiseId,
        appliedToAccountId: input.paymentRecord.appliedToAccountId,
        externalPaymentReference: input.paymentRecord.externalPaymentReference,
        accountingProvider: input.paymentRecord.accountingProvider,
        accountingSyncStatus: input.paymentRecord.accountingSyncStatus || "not_configured",
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: catalogEntry.managerUpdateRequired,
      clientPortalProjectionRequired: false,
      notificationRequired: shouldProjectNotification(options.eventType),
      auditRequired: catalogEntry.auditRequired,
      reportingRequired: true,
    },
    visibility: {
      internal: true,
      clientVisible: false,
      managerVisible: true,
      collectorVisible: true,
      systemVisible: false,
    },
  };
}

function buildReportWorkflowEvent(
  input: ARWorkflowReportEventInput,
  options: {
    eventType: ARWorkflowReportEventType;
    occurredAt: string;
    correlationId: string;
    title: string;
    summary: string;
  },
): ARWorkflowEvent {
  const catalogEntry = AR_REPORTING_WORKFLOW_ACTION_CATALOG[input.action];
  return {
    id: createWorkflowId("workflow-event", input.reportId, options.eventType),
    tenantId: input.tenantId,
    correlationId: options.correlationId,
    eventType: options.eventType,
    eventFamily: "platform",
    eventCategory: "reporting",
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: options.occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || catalogEntry.sourceWorkspace,
      ownerWorkspace: "Enterprise Reporting Engine",
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      customerId: input.customerId,
      accountId: input.accountId,
    },
    payload: {
      title: options.title,
      summary: options.summary,
      newValue: {
        reportId: input.reportId,
        reportName: input.reportName,
        action: input.action,
        filters: input.filters || {},
        exportFormat: input.exportFormat,
        drillDownTarget: input.drillDownTarget,
      },
      metadata: {
        workflowAction: input.action,
        reportId: input.reportId,
        reportName: input.reportName,
        filters: input.filters || {},
        exportFormat: input.exportFormat,
        drillDownTarget: input.drillDownTarget,
        drillDownRoute: input.drillDownTarget ? AR_REPORTING_DRILL_DOWN_ROUTES[input.drillDownTarget] : undefined,
        engine: "enterprise_reporting_engine",
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: input.action === "Report.Export",
      clientPortalProjectionRequired: false,
      notificationRequired: false,
      auditRequired: true,
      reportingRequired: true,
    },
    visibility: {
      internal: true,
      clientVisible: false,
      managerVisible: input.action === "Report.Export" || input.action === "Report.DrillDown",
      collectorVisible: true,
      systemVisible: true,
    },
  };
}

function buildPortfolioIntakeWorkflowEvent(
  input: ARWorkflowPortfolioIntakeEventInput,
  options: {
    occurredAt: string;
    correlationId: string;
    title: string;
    summary: string;
  },
): ARWorkflowEvent {
  const catalogEntry = ENTERPRISE_CLIENT_EXCHANGE_EVENT_CATALOG[input.eventType];
  return {
    id: createWorkflowId("workflow-event", input.batchId, input.eventType),
    tenantId: input.tenantId,
    correlationId: options.correlationId,
    eventType: input.eventType,
    eventFamily: "platform",
    eventCategory: "client_exchange",
    actor: {
      actorId: input.actor.actorId || input.actor.actorEmail || input.actor.actorName,
      actorName: input.actor.actorName,
      actorEmail: input.actor.actorEmail || "",
      actorRole: input.actor.actorRole,
      permissionLevel: input.actor.permissionLevel,
    },
    occurredAt: options.occurredAt,
    workspace: {
      sourceWorkspace: input.sourceWorkspace || "Enterprise Client Exchange",
      ownerWorkspace: "Enterprise Client Exchange",
      initiatedFromRoute: input.initiatedFromRoute,
    },
    scope: {
      clientId: input.clientId,
      accountId: input.portfolioId,
    },
    payload: {
      title: options.title,
      summary: options.summary,
      newValue: {
        batch: input.batch,
        stagingRecords: input.stagingRecords || [],
        validationResults: input.validationResults || [],
        mappingProfile: input.mappingProfile,
        approvalRecord: input.approvalRecord,
        reviewSummary: input.reviewSummary,
        importExecution: input.importExecution,
        importHistory: input.importHistory,
        duplicateSummary: input.duplicateSummary,
        rollbackMetadata: input.rollbackMetadata,
        assignmentRecord: input.assignmentRecord,
        assignmentSummary: input.assignmentSummary,
        activationRecord: input.activationRecord,
        activationSummary: input.activationSummary,
        assignmentPlan: input.assignmentPlan,
      },
      metadata: {
        engine: "enterprise_client_exchange",
        batchId: input.batchId,
        portfolioId: input.portfolioId,
        portfolioName: input.portfolioName,
        status: catalogEntry.status,
        projectionTargets: catalogEntry.projectionTargets,
        batch: input.batch,
        stagingRecords: input.stagingRecords || [],
        validationResults: input.validationResults || [],
        mappingProfile: input.mappingProfile,
        approvalRecord: input.approvalRecord,
        reviewSummary: input.reviewSummary,
        importExecution: input.importExecution,
        importHistory: input.importHistory,
        duplicateSummary: input.duplicateSummary,
        rollbackMetadata: input.rollbackMetadata,
        assignmentRecord: input.assignmentRecord,
        assignmentSummary: input.assignmentSummary,
        activationRecord: input.activationRecord,
        activationSummary: input.activationSummary,
        assignmentPlan: input.assignmentPlan,
      },
    },
    routing: {
      recoveryActivityRequired: true,
      timelineRequired: true,
      managerUpdateRequired: catalogEntry.requiredPermission === "manager_override",
      clientPortalProjectionRequired: false,
      notificationRequired: false,
      auditRequired: true,
      reportingRequired: true,
    },
    visibility: {
      internal: true,
      clientVisible: false,
      managerVisible: true,
      collectorVisible: true,
      systemVisible: true,
    },
  };
}

function buildNotificationProjection(
  event: ARWorkflowEvent,
  rule: ARNotificationProjectionRule,
  recipientRole: ARNotificationRecipientRole,
  deliveryChannel: ARNotificationDeliveryChannel,
): ARWorkflowNotificationProjection {
  const recipient = resolveNotificationRecipient(event, recipientRole);
  return {
    notificationId: createNotificationId(event.id, recipientRole, deliveryChannel),
    tenantId: event.tenantId,
    correlationId: event.correlationId,
    workflowEventId: event.id,
    notificationType: rule.notificationType,
    recipient,
    recipientRole,
    deliveryChannel,
    supportedDeliveryChannels: rule.deliveryChannels,
    status: "Created",
    createdAt: event.occurredAt,
    scheduledFor: undefined,
    deliveredAt: undefined,
    readAt: undefined,
    dismissedAt: undefined,
    priority: rule.priority,
    visibility: rule.visibility,
    title: event.payload.title,
    message: `${rule.reason} ${event.payload.summary}`.trim(),
    sourceWorkspace: event.workspace.sourceWorkspace,
    targetWorkspace: rule.targetWorkspace,
    accountId: event.scope.accountId,
    customerName: String(event.payload.metadata?.customerName || ""),
    metadata: {
      workflowEventType: event.eventType,
      workflowEventCategory: event.eventCategory,
      ownerWorkspace: event.workspace.ownerWorkspace,
      initiatedFromRoute: event.workspace.initiatedFromRoute,
      notificationLifecycle: AR_NOTIFICATION_LIFECYCLE,
      channelAbstraction: AR_NOTIFICATION_DELIVERY_CHANNELS,
      sourceMetadata: event.payload.metadata || {},
    },
  };
}

function resolveNotificationRecipient(event: ARWorkflowEvent, recipientRole: ARNotificationRecipientRole) {
  if (recipientRole === "manager") {
    return String(event.payload.metadata?.assignedTo || event.payload.metadata?.manager || "Manager Command");
  }
  if (recipientRole === "administrator") {
    return "AR OS Administrator";
  }
  if (recipientRole === "client_future") {
    return String(event.scope.clientId || "Client Portal");
  }
  return event.actor.actorName || event.actor.actorEmail || "Assigned Collector";
}

function createNotificationId(eventId: string, recipientRole: string, deliveryChannel: string) {
  return `notification-${eventId}-${recipientRole}-${deliveryChannel}`.replace(/[^a-zA-Z0-9_-]+/g, "-");
}

function shouldProjectNotification(eventType: ARWorkflowEventType) {
  return Boolean(AR_NOTIFICATION_PROJECTION_CATALOG[eventType]);
}

function applyReportingFilters(events: ARWorkflowEvent[], filters: ARWorkflowReportingFilter) {
  return events.filter((event) => {
    if (filters.collector && !matchesText(event.actor.actorName, filters.collector)) return false;
    if (filters.manager && !matchesText(String(event.payload.metadata?.manager || event.payload.metadata?.assignedTo || ""), filters.manager)) return false;
    if (filters.clientId && event.scope.clientId !== filters.clientId) return false;
    if (filters.customerId && event.scope.customerId !== filters.customerId) return false;
    if (filters.accountId && event.scope.accountId !== filters.accountId) return false;
    if (filters.portfolioId && String(event.payload.metadata?.portfolioId || "") !== filters.portfolioId) return false;
    if (filters.promiseStatus && String(event.payload.metadata?.promiseStatus || toRecord(event.payload.newValue).status || "") !== filters.promiseStatus) return false;
    if (filters.ticklerStatus && String(event.payload.metadata?.ticklerStatus || toRecord(event.payload.newValue).status || "") !== filters.ticklerStatus) return false;
    if (filters.agingBucket && String(event.payload.metadata?.agingBucket || event.payload.metadata?.aging || "") !== filters.agingBucket) return false;
    if (filters.priority && String(event.payload.metadata?.priority || "") !== filters.priority) return false;
    if (filters.assignment && !matchesText(String(event.payload.metadata?.assignment || event.payload.metadata?.assignedTo || ""), filters.assignment)) return false;
    if (filters.dateRange?.from && Date.parse(event.occurredAt) < Date.parse(filters.dateRange.from)) return false;
    if (filters.dateRange?.to && Date.parse(event.occurredAt) > Date.parse(filters.dateRange.to)) return false;
    const amountCents = normalizePaymentAmountCents(event.payload.metadata?.amountCents || toRecord(event.payload.newValue).amountCents);
    if (filters.balanceRange?.minCents !== undefined && amountCents < filters.balanceRange.minCents) return false;
    if (filters.balanceRange?.maxCents !== undefined && amountCents > filters.balanceRange.maxCents) return false;
    return true;
  });
}

function buildReportingMetric(
  id: string,
  label: string,
  value: number,
  sourceEventTypes: ARWorkflowEventType[],
  drillDownTarget?: ARReportingDrillDownTarget,
  amountCents?: number,
): ARReportingMetricProjection {
  return {
    id,
    label,
    value,
    amountCents,
    trend: value > 0 ? "up" : "flat",
    sourceEventTypes,
    drillDownTarget,
    drillDownRoute: drillDownTarget ? AR_REPORTING_DRILL_DOWN_ROUTES[drillDownTarget] : undefined,
  };
}

function matchesText(value: string, expected: string) {
  return value.trim().toLowerCase().includes(expected.trim().toLowerCase());
}

function buildReportEventTitle(eventType: ARWorkflowReportEventType, reportName: string) {
  if (eventType === "ReportViewed") return `Report viewed: ${reportName}`;
  if (eventType === "ReportFiltered") return `Report filtered: ${reportName}`;
  if (eventType === "ReportExported") return `Report exported: ${reportName}`;
  return `Report drill-down opened: ${reportName}`;
}

function buildReportSummary(input: ARWorkflowReportEventInput) {
  const filterCount = Object.keys(input.filters || {}).length;
  const exportFormat = input.exportFormat ? ` Export format: ${input.exportFormat}.` : "";
  const drillDown = input.drillDownTarget ? ` Drill-down: ${input.drillDownTarget}.` : "";
  return `${input.action} executed for ${input.reportName}. Filters: ${filterCount}.${exportFormat}${drillDown}`;
}

function isPortfolioIntakeWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowPortfolioIntakeEventType {
  return [
    "ImportStarted",
    "PortfolioCreated",
    "ImportValidated",
    "ImportFailed",
    "ImportExecuting",
    "ValidationCompleted",
    "ValidationWarningDetected",
    "ValidationErrorDetected",
    "ImportMapped",
    "MappingProfileCreated",
    "MappingProfileUpdated",
    "MappingWarningDetected",
    "MappingErrorDetected",
    "ApprovalCompleted",
    "ApprovalRequired",
    "RiskLevelCalculated",
    "ImportApproved",
    "ImportRejected",
    "ImportCompleted",
    "ImportRolledBack",
    "PortfolioAssigned",
    "PortfolioAssignmentUpdated",
    "PortfolioAssignmentRemoved",
    "PortfolioReadyForActivation",
    "PortfolioActivationRequested",
    "PortfolioActivationValidated",
    "PortfolioActivated",
    "PortfolioActivationFailed",
    "PortfolioDeactivated",
    "PortfolioActivationBlocked",
    "PortfolioArchived",
  ].includes(eventType);
}

function buildPortfolioIntakeEventTitle(eventType: ARWorkflowPortfolioIntakeEventType, portfolioName: string) {
  return `${ENTERPRISE_CLIENT_EXCHANGE_EVENT_CATALOG[eventType].label}: ${portfolioName}`;
}

function buildPortfolioIntakeSummary(input: ARWorkflowPortfolioIntakeEventInput) {
  const validationSummary = input.batch?.validationSummary
    ? ` Errors: ${input.batch.validationSummary.errorCount}. Warnings: ${input.batch.validationSummary.warningCount}.`
    : "";
  const approval = input.approvalRecord ? ` Approval: ${input.approvalRecord.approvalStatus}.` : "";
  const assignment = input.assignmentPlan ? ` Assignment plan: ${input.assignmentPlan.assignmentPlanId}.` : "";
  return `${input.eventType} for portfolio ${input.portfolioName}. Batch: ${input.batchId}.${validationSummary}${approval}${assignment}`;
}

function normalizeMappingColumnName(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function buildPortfolioMappingIssues(
  mappingProfile: PortfolioMappingProfile,
  missingRequiredFields: string[],
  unmapped: {
    unmappedFields: string[];
    unsupportedSourceColumns: string[];
    duplicateTargetMappings: string[];
    invalidTargetFields: string[];
  },
): PortfolioMappingIssue[] {
  return [
    ...missingRequiredFields.map((targetField) => createPortfolioMappingIssue(mappingProfile, "Error", `Required target field is not mapped: ${targetField}.`, undefined, targetField)),
    ...unmapped.invalidTargetFields.map((targetField) => createPortfolioMappingIssue(mappingProfile, "Error", `Mapped target field is not supported: ${targetField}.`, undefined, targetField)),
    ...unmapped.duplicateTargetMappings.map((targetField) => createPortfolioMappingIssue(mappingProfile, "Error", `Target field has duplicate source mappings: ${targetField}.`, undefined, targetField)),
    ...unmapped.unmappedFields.map((sourceColumn) => createPortfolioMappingIssue(mappingProfile, "Warning", `Source column is not mapped: ${sourceColumn}.`, sourceColumn)),
    ...unmapped.unsupportedSourceColumns.map((sourceColumn) => createPortfolioMappingIssue(mappingProfile, "Information", `No deterministic suggestion exists for source column: ${sourceColumn}.`, sourceColumn)),
  ];
}

function createPortfolioMappingIssue(
  mappingProfile: PortfolioMappingProfile,
  severity: PortfolioMappingIssue["severity"],
  message: string,
  sourceColumn?: string,
  targetField?: string,
): PortfolioMappingIssue {
  return {
    issueId: createWorkflowId("mapping-issue", mappingProfile.profileId, `${severity}-${sourceColumn || targetField || "mapping"}`),
    severity,
    sourceColumn,
    targetField,
    message,
  };
}

function buildMappingSignalEvents(
  input: {
    tenantId: string;
    clientId: string;
    portfolioId: string;
    batchId: string;
    portfolioName: string;
    actor: ARWorkflowActorContext;
    batch?: PortfolioIntakeBatch;
    sourceWorkspace?: string;
    initiatedFromRoute?: string;
  },
  mapping: PortfolioMappingEngineResult,
) {
  const hasWarnings = mapping.issues.some((issue) => issue.severity === "Warning");
  const hasErrors = mapping.issues.some((issue) => issue.severity === "Error");
  const warningEvents = hasWarnings
    ? buildPortfolioIntakeWorkflowEvents({
      tenantId: input.tenantId,
      clientId: input.clientId,
      portfolioId: input.portfolioId,
      batchId: input.batchId,
      portfolioName: input.portfolioName,
      actor: input.actor,
      eventType: "MappingWarningDetected",
      batch: input.batch,
      mappingProfile: mapping.mappingProfile,
      sourceWorkspace: input.sourceWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    })
    : [];
  const errorEvents = hasErrors
    ? buildPortfolioIntakeWorkflowEvents({
      tenantId: input.tenantId,
      clientId: input.clientId,
      portfolioId: input.portfolioId,
      batchId: input.batchId,
      portfolioName: input.portfolioName,
      actor: input.actor,
      eventType: "MappingErrorDetected",
      batch: input.batch,
      mappingProfile: mapping.mappingProfile,
      sourceWorkspace: input.sourceWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    })
    : [];
  return [...warningEvents, ...errorEvents];
}

function buildPortfolioMappingSummary(mapping: PortfolioMappingEngineResult): PortfolioMappingSummary {
  return {
    readiness: mapping.readiness,
    requiredFieldsMapped: mapping.missingRequiredFields.length === 0,
    missingRequiredFields: mapping.missingRequiredFields,
    unmappedFields: mapping.unmappedFields,
    warningCount: mapping.issues.filter((issue) => issue.severity === "Warning").length,
    errorCount: mapping.issues.filter((issue) => issue.severity === "Error").length,
  };
}

function evaluateAssignmentReadiness(assignmentPlan?: PortfolioAssignmentPlan): PortfolioApprovalReadiness {
  if (!assignmentPlan) return "Ready With Warnings";
  return assignmentPlan.workQueueEligibility ? "Ready" : "Ready With Warnings";
}

function calculatePortfolioTotalBalance(records: PortfolioStagingRecord[]) {
  return records.reduce((total, record) => {
    const data = { ...record.rawData, ...record.mappedData };
    const balance = getPortfolioFieldValue(data, "balance", "currentBalance", "openBalance", "Current Balance", "invoice.currentBalance");
    if (!balance || !isFiniteNumber(balance)) return total;
    return total + Number(balance.replace(/[$,]/g, ""));
  }, 0);
}

function buildPortfolioApprovalWorkflowEvents(
  input: {
    batch: PortfolioIntakeBatch;
    validation: PortfolioValidationEngineResult;
    mapping: PortfolioMappingEngineResult;
    actor: ARWorkflowActorContext;
    decision: "Approve" | "Reject" | "Require Approval";
    assignmentPlan?: PortfolioAssignmentPlan;
    stagingRecords?: PortfolioStagingRecord[];
    sourceWorkspace?: string;
    initiatedFromRoute?: string;
  },
  review: PortfolioReviewEngineResult,
) {
  const baseEvent = {
    tenantId: input.batch.tenantId,
    clientId: input.batch.clientId,
    portfolioId: input.batch.portfolioId,
    batchId: input.batch.batchId,
    portfolioName: input.batch.portfolioName,
    actor: input.actor,
    batch: { ...input.batch, approvalStatus: review.approvalRecord?.approvalStatus || input.batch.approvalStatus },
    validationResults: input.validation.validationResults,
    mappingProfile: input.mapping.mappingProfile,
    approvalRecord: review.approvalRecord,
    reviewSummary: review.reviewSummary,
    assignmentPlan: input.assignmentPlan,
    stagingRecords: input.stagingRecords,
    sourceWorkspace: input.sourceWorkspace,
    initiatedFromRoute: input.initiatedFromRoute,
  };
  const decisionEvents: ARWorkflowPortfolioIntakeEventType[] = input.decision === "Approve"
    ? ["ApprovalCompleted", "ImportApproved"]
    : input.decision === "Reject"
      ? ["ApprovalCompleted", "ImportRejected"]
      : ["ApprovalRequired"];
  const eventTypes: ARWorkflowPortfolioIntakeEventType[] = ["RiskLevelCalculated", ...decisionEvents];
  return eventTypes.flatMap((eventType) =>
    buildPortfolioIntakeWorkflowEvents({
      ...baseEvent,
      eventType,
    }),
  );
}

function createAssignmentValidationResult(
  assignment: PortfolioAssignmentRecord,
  severity: PortfolioAssignmentValidationResult["severity"],
  category: string,
  message: string,
): PortfolioAssignmentValidationResult {
  return {
    issueId: createWorkflowId("assignment-issue", assignment.assignmentId, category.replace(/\s+/g, "-")),
    severity,
    category,
    message,
  };
}

function buildPortfolioAssignmentWorkflowEvents(
  input: {
    batch: PortfolioIntakeBatch;
    actor: ARWorkflowActorContext;
    portfolioName: string;
    sourceWorkspace?: string;
    initiatedFromRoute?: string;
  },
  assignmentResult: PortfolioAssignmentEngineResult,
) {
  const readinessEvent: ARWorkflowPortfolioIntakeEventType = assignmentResult.assignmentSummary.activationReadiness === "Blocked"
    ? "PortfolioActivationBlocked"
    : "PortfolioReadyForActivation";
  const eventTypes: ARWorkflowPortfolioIntakeEventType[] = ["PortfolioAssigned", readinessEvent];
  return eventTypes.flatMap((eventType) =>
    buildPortfolioIntakeWorkflowEvents({
      tenantId: input.batch.tenantId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      batchId: input.batch.batchId,
      portfolioName: input.portfolioName,
      actor: input.actor,
      eventType,
      batch: input.batch,
      assignmentRecord: assignmentResult.assignment,
      assignmentSummary: assignmentResult.assignmentSummary,
      sourceWorkspace: input.sourceWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    }),
  );
}

const PORTFOLIO_PIPELINE_EXPECTED_EVENTS: ARWorkflowPortfolioIntakeEventType[] = [
  "ImportStarted",
  "ImportValidated",
  "ImportMapped",
  "ImportApproved",
  "ImportCompleted",
  "PortfolioAssigned",
  "PortfolioReadyForActivation",
  "PortfolioActivationRequested",
  "PortfolioActivationValidated",
  "PortfolioActivated",
];

export function runEnterprisePortfolioIntakeWalkthrough(tenantId = "enterprise-client-exchange-walkthrough"): PortfolioPipelineWalkthroughResult {
  const actor: ARWorkflowActorContext = {
    actorId: "enterprise-client-exchange",
    actorName: "Enterprise Client Exchange",
    actorEmail: "enterprise-client-exchange@truevance.local",
    actorRole: "System",
    permissionLevel: "manager_override",
  };
  const now = new Date().toISOString();
  const batch = createPortfolioWalkthroughBatch(tenantId, now);
  const stagingRecords = createPortfolioWalkthroughStagingRecords(batch, now);
  const intakeEvents = buildPortfolioIntakeWorkflowEvents({
    tenantId,
    clientId: batch.clientId,
    portfolioId: batch.portfolioId,
    batchId: batch.batchId,
    portfolioName: batch.portfolioName,
    actor,
    eventType: "ImportStarted",
    batch,
    stagingRecords,
    sourceWorkspace: "Enterprise Client Exchange",
    initiatedFromRoute: "/admin/ar-recovery/client-exchange",
  });
  const validation = PortfolioValidationEngine.validate({ batch, stagingRecords });
  const validationEvents = buildPortfolioIntakeWorkflowEvents({
    tenantId,
    clientId: batch.clientId,
    portfolioId: batch.portfolioId,
    batchId: batch.batchId,
    portfolioName: batch.portfolioName,
    actor,
    eventType: "ValidationCompleted",
    batch: { ...batch, status: validation.validationSummary.importReadiness === "Blocked" ? "Validation Failed" : "Ready For Review", validationSummary: validation.validationSummary },
    stagingRecords,
    validationResults: validation.validationResults,
    sourceWorkspace: "Enterprise Client Exchange",
    initiatedFromRoute: "/admin/ar-recovery/client-exchange",
  }).concat(buildPortfolioIntakeWorkflowEvents({
    tenantId,
    clientId: batch.clientId,
    portfolioId: batch.portfolioId,
    batchId: batch.batchId,
    portfolioName: batch.portfolioName,
    actor,
    eventType: validation.validationSummary.importReadiness === "Blocked" ? "ImportFailed" : "ImportValidated",
    batch: { ...batch, status: validation.validationSummary.importReadiness === "Blocked" ? "Validation Failed" : "Ready For Review", validationSummary: validation.validationSummary },
    stagingRecords,
    validationResults: validation.validationResults,
    sourceWorkspace: "Enterprise Client Exchange",
    initiatedFromRoute: "/admin/ar-recovery/client-exchange",
  }));
  const mapping = PortfolioMappingEngine.map({
    tenantId,
    clientId: batch.clientId,
    profileName: "Phase 4H Walkthrough Mapping",
    sourceFormat: "Manual",
    sourceColumns: ["Customer Name", "Account Number", "Invoice Number", "Balance", "Due Date"],
    createdBy: actor.actorName,
  });
  const mappingEvents = buildPortfolioIntakeWorkflowEvents({
    tenantId,
    clientId: batch.clientId,
    portfolioId: batch.portfolioId,
    batchId: batch.batchId,
    portfolioName: batch.portfolioName,
    actor,
    eventType: "ImportMapped",
    batch: { ...batch, status: "Ready For Review", validationSummary: validation.validationSummary, mappingProfileId: mapping.mappingProfile.mappingProfileId },
    stagingRecords,
    validationResults: validation.validationResults,
    mappingProfile: mapping.mappingProfile,
    sourceWorkspace: "Enterprise Client Exchange",
    initiatedFromRoute: "/admin/ar-recovery/client-exchange",
  });
  const approvedBatch: PortfolioIntakeBatch = {
    ...batch,
    status: "Approved",
    validationSummary: validation.validationSummary,
    mappingProfileId: mapping.mappingProfile.mappingProfileId,
    approvalStatus: "Approved",
  };
  const review = PortfolioReviewEngine.review({
    batch: approvedBatch,
    validation,
    mapping,
    approvalRecord: {
      approvalId: createWorkflowId("approval", approvedBatch.batchId),
      batchId: approvedBatch.batchId,
      tenantId,
      correlationId: approvedBatch.correlationId,
      clientId: approvedBatch.clientId,
      portfolioId: approvedBatch.portfolioId,
      approvedBy: actor.actorName,
      approvedAt: now,
      approvalStatus: "Approved",
      approvalNotes: "Phase 4H walkthrough approval.",
      riskLevel: "Low",
    },
    clientName: "Phase 4H Pilot Client",
  });
  const approvalEvents = buildPortfolioApprovalWorkflowEvents({
    batch: approvedBatch,
    validation,
    mapping,
    actor,
    decision: "Approve",
    sourceWorkspace: "Enterprise Client Exchange",
    initiatedFromRoute: "/admin/ar-recovery/client-exchange",
  }, review);
  const importResult = PortfolioImportEngine.execute({
    batch: approvedBatch,
    review,
    actor,
    stagingRecords,
  });
  const importEvents = buildPortfolioImportWorkflowEvents({
    batch: approvedBatch,
    review,
    actor,
    stagingRecords,
    sourceWorkspace: "Enterprise Client Exchange",
    initiatedFromRoute: "/admin/ar-recovery/client-exchange",
  }, importResult);
  const assignmentResult = PortfolioAssignmentEngine.assign({
    tenantId,
    clientId: batch.clientId,
    portfolioId: batch.portfolioId,
    correlationId: batch.correlationId,
    collectorId: "collector-phase-4h",
    managerId: "manager-phase-4h",
    teamId: "team-phase-4h",
    priority: "Standard",
    recoveryStage: "Portfolio Intake",
    assignedBy: actor.actorName,
    importResult,
    validation,
    mapping,
    review,
  });
  const assignmentEvents = buildPortfolioAssignmentWorkflowEvents({
    batch: approvedBatch,
    actor,
    portfolioName: batch.portfolioName,
    sourceWorkspace: "Enterprise Client Exchange",
    initiatedFromRoute: "/admin/ar-recovery/client-exchange",
  }, assignmentResult);
  const activationResult = PortfolioActivationEngine.execute({
    action: "Portfolio.Activate",
    actor,
    validation,
    mapping,
    review,
    importResult,
    assignmentResult,
  });
  const activationEvents = buildPortfolioActivationWorkflowEvents({
    batch: approvedBatch,
    actor,
    portfolioName: batch.portfolioName,
    sourceWorkspace: "Enterprise Client Exchange",
    initiatedFromRoute: "/admin/ar-recovery/client-exchange",
  }, activationResult);
  const workflowEvents = [
    ...intakeEvents,
    ...validationEvents,
    ...mappingEvents,
    ...approvalEvents,
    ...importEvents,
    ...assignmentEvents,
    ...activationEvents,
  ];
  const expectedEventsVerified = PORTFOLIO_PIPELINE_EXPECTED_EVENTS.filter((eventType) => workflowEvents.some((event) => event.eventType === eventType));
  const missingEvents = PORTFOLIO_PIPELINE_EXPECTED_EVENTS.filter((eventType) => !expectedEventsVerified.includes(eventType));
  const eventEnvelopeIssues = verifyPortfolioPipelineEventEnvelope(workflowEvents);
  const projectionVerification = verifyPortfolioPipelineProjections(activationResult);
  const failurePaths = verifyPortfolioPipelineFailurePaths({ actor, batch, stagingRecords, validation, mapping, review, importResult });
  const operationalReadinessVerified = importResult.execution.activationStatus === "Pending Activation"
    && assignmentResult.assignmentSummary.activationReadiness === "Ready"
    && activationResult.activation.status === "Activated"
    && failurePaths.every((failurePath) => failurePath.status === "Passed");
  const stages = buildPortfolioPipelineWalkthroughStages({ validation, mapping, review, importResult, assignmentResult, activationResult });
  const ready = missingEvents.length === 0
    && eventEnvelopeIssues.length === 0
    && projectionVerification.every((projection) => projection.projectionStatus === "Prepared")
    && stages.every((stage) => stage.status === "Passed")
    && operationalReadinessVerified;
  return {
    verifier: "enterprise_portfolio_intake_walkthrough",
    tenantId,
    correlationId: batch.correlationId,
    overallStatus: ready ? "READY FOR PILOT CLIENTS" : "READY FOR NEXT IMPLEMENTATION PHASE",
    stages,
    failurePaths,
    expectedEventsVerified,
    missingEvents,
    eventEnvelopeVerified: eventEnvelopeIssues.length === 0,
    eventEnvelopeIssues,
    projectionVerification,
    operationalReadinessVerified,
    notes: [
      "Portfolio intake walkthrough runs in-memory and does not mutate production workspaces.",
      "Workflow Events remain the source of truth for projections.",
      "Activation requires explicit Portfolio.Activate after import and assignment readiness.",
    ],
  };
}

function createPortfolioWalkthroughBatch(tenantId: string, submittedAt: string): PortfolioIntakeBatch {
  return {
    batchId: "phase-4h-walkthrough-batch",
    tenantId,
    correlationId: "phase-4h-walkthrough-correlation",
    clientId: "phase-4h-client",
    portfolioId: "phase-4h-portfolio",
    portfolioName: "Phase 4H Pilot Portfolio",
    sourceType: "Manual",
    sourceFileName: "Phase 4H Walkthrough",
    submittedBy: "Enterprise Client Exchange",
    submittedAt,
    recordCount: 3,
    status: "Received",
    validationSummary: {
      totalRecords: 0,
      validRecords: 0,
      errorCount: 0,
      warningCount: 0,
      informationCount: 0,
      duplicateCount: 0,
      customerCount: 0,
      accountCount: 0,
      invoiceCount: 0,
      importReadiness: "Ready",
      overallValidationStatus: "Passed",
      isImportReady: true,
    },
    approvalStatus: "Pending",
  };
}

function createPortfolioWalkthroughStagingRecords(batch: PortfolioIntakeBatch, createdAt: string): PortfolioStagingRecord[] {
  return [
    {
      stagingRecordId: "phase-4h-customer",
      batchId: batch.batchId,
      tenantId: batch.tenantId,
      clientId: batch.clientId,
      portfolioId: batch.portfolioId,
      rowNumber: 1,
      recordType: "Customer",
      rawData: { "Customer Name": "Phase 4H Customer", email: "billing@phase4h.example", phone: "555-0100" },
      mappedData: { customerName: "Phase 4H Customer", email: "billing@phase4h.example", phone: "555-0100" },
      validationStatus: "Not Validated",
      createdAt,
    },
    {
      stagingRecordId: "phase-4h-account",
      batchId: batch.batchId,
      tenantId: batch.tenantId,
      clientId: batch.clientId,
      portfolioId: batch.portfolioId,
      rowNumber: 2,
      recordType: "Account",
      rawData: { "Customer Name": "Phase 4H Customer", "Account Number": "PH4H-1001", "Aging Bucket": "31-60", Collector: "collector-phase-4h", Manager: "manager-phase-4h" },
      mappedData: { customerName: "Phase 4H Customer", accountNumber: "PH4H-1001", agingBucket: "31-60", collector: "collector-phase-4h", manager: "manager-phase-4h" },
      validationStatus: "Not Validated",
      createdAt,
    },
    {
      stagingRecordId: "phase-4h-invoice",
      batchId: batch.batchId,
      tenantId: batch.tenantId,
      clientId: batch.clientId,
      portfolioId: batch.portfolioId,
      rowNumber: 3,
      recordType: "Invoice",
      rawData: { "Customer Name": "Phase 4H Customer", "Account Number": "PH4H-1001", "Invoice Number": "INV-PH4H-1001", Balance: 2500, "Due Date": "2026-07-31", "Invoice Date": "2026-07-01" },
      mappedData: { customerName: "Phase 4H Customer", accountNumber: "PH4H-1001", invoiceNumber: "INV-PH4H-1001", balance: 2500, dueDate: "2026-07-31", invoiceDate: "2026-07-01" },
      validationStatus: "Not Validated",
      createdAt,
    },
  ];
}

function buildPortfolioPipelineWalkthroughStages(input: {
  validation: PortfolioValidationEngineResult;
  mapping: PortfolioMappingEngineResult;
  review: PortfolioReviewEngineResult;
  importResult: PortfolioImportEngineResult;
  assignmentResult: PortfolioAssignmentEngineResult;
  activationResult: PortfolioActivationEngineResult;
}): PortfolioPipelineWalkthroughStage[] {
  return [
    { stage: "Intake", status: "Passed", detail: "Client, batch, and staged portfolio records prepared." },
    { stage: "Validation", status: input.validation.validationSummary.importReadiness === "Ready" ? "Passed" : "Failed", detail: `Validation readiness: ${input.validation.validationSummary.importReadiness}.` },
    { stage: "Mapping", status: input.mapping.readiness === "Ready" ? "Passed" : "Failed", detail: `Mapping readiness: ${input.mapping.readiness}.` },
    { stage: "Review", status: input.review.reviewSummary.importRiskLevel === "Low" ? "Passed" : "Failed", detail: `Import risk: ${input.review.reviewSummary.importRiskLevel}.` },
    { stage: "Approval", status: input.review.approvalRecord?.approvalStatus === "Approved" ? "Passed" : "Failed", detail: `Approval status: ${input.review.approvalRecord?.approvalStatus || "Missing"}.` },
    { stage: "Import", status: input.importResult.execution.status === "Completed" && input.importResult.execution.activationStatus === "Pending Activation" ? "Passed" : "Failed", detail: `Import status: ${input.importResult.execution.status}; activation status: ${input.importResult.execution.activationStatus}.` },
    { stage: "Assignment", status: input.assignmentResult.assignment.status === "Ready" ? "Passed" : "Failed", detail: `Assignment status: ${input.assignmentResult.assignment.status}.` },
    { stage: "Ready For Activation", status: input.assignmentResult.assignmentSummary.activationReadiness === "Ready" ? "Passed" : "Failed", detail: `Activation readiness: ${input.assignmentResult.assignmentSummary.activationReadiness}.` },
    { stage: "Activation", status: input.activationResult.activation.status === "Activated" ? "Passed" : "Failed", detail: `Activation status: ${input.activationResult.activation.status}.` },
  ];
}

function verifyPortfolioPipelineEventEnvelope(events: ARWorkflowEvent[]) {
  return events.flatMap((event) => {
    const issues: string[] = [];
    if (!event.tenantId) issues.push(`${event.eventType}: missing tenantId`);
    if (!event.correlationId) issues.push(`${event.eventType}: missing correlationId`);
    if (!event.actor?.actorId || !event.actor?.actorName || !event.actor?.permissionLevel) issues.push(`${event.eventType}: missing actor context`);
    if (!event.scope?.clientId) issues.push(`${event.eventType}: missing client scope`);
    if (!event.payload?.title || !event.payload?.summary) issues.push(`${event.eventType}: missing payload`);
    if (!event.routing?.recoveryActivityRequired || !event.routing?.timelineRequired || !event.routing?.auditRequired || !event.routing?.reportingRequired) issues.push(`${event.eventType}: missing routing requirements`);
    if (!event.visibility?.internal || !event.visibility?.managerVisible || !event.visibility?.collectorVisible) issues.push(`${event.eventType}: missing visibility requirements`);
    return issues;
  });
}

function verifyPortfolioPipelineProjections(activationResult: PortfolioActivationEngineResult): PortfolioPipelineProjectionVerification[] {
  const expectedTargets: PortfolioPipelineProjectionVerification["target"][] = ["Recovery Workbench", "Customers & Accounts", "Manager Command", "Recovery Activity", "Timeline", "Reporting"];
  return expectedTargets.map((target) => ({
    target,
    projectionStatus: activationResult.projectionTargets.some((projectionTarget) => projectionTarget.target === target && projectionTarget.projectionStatus === "Prepared") ? "Prepared" : "Missing",
  }));
}

function verifyPortfolioPipelineFailurePaths(input: {
  actor: ARWorkflowActorContext;
  batch: PortfolioIntakeBatch;
  stagingRecords: PortfolioStagingRecord[];
  validation: PortfolioValidationEngineResult;
  mapping: PortfolioMappingEngineResult;
  review: PortfolioReviewEngineResult;
  importResult: PortfolioImportEngineResult;
}): PortfolioPipelineFailurePathResult[] {
  const failedValidation = PortfolioValidationEngine.validate({
    batch: input.batch,
    stagingRecords: [{
      ...input.stagingRecords[0],
      stagingRecordId: "phase-4h-validation-failure",
      rawData: {},
      mappedData: {},
    }],
  });
  const failedMapping = PortfolioMappingEngine.map({
    tenantId: input.batch.tenantId,
    clientId: input.batch.clientId,
    profileName: "Phase 4H Failed Mapping",
    sourceFormat: "Manual",
    sourceColumns: ["Customer Name"],
    createdBy: input.actor.actorName,
  });
  const rejectedReview = PortfolioReviewEngine.review({
    batch: { ...input.batch, approvalStatus: "Rejected" },
    validation: input.validation,
    mapping: input.mapping,
    approvalRecord: {
      approvalId: createWorkflowId("approval", input.batch.batchId, "rejected"),
      batchId: input.batch.batchId,
      tenantId: input.batch.tenantId,
      correlationId: input.batch.correlationId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      rejectedBy: input.actor.actorName,
      rejectedAt: new Date().toISOString(),
      approvalStatus: "Rejected",
      approvalNotes: "Phase 4H rejection path.",
      riskLevel: "Medium",
    },
    clientName: "Phase 4H Pilot Client",
  });
  const failedImport = PortfolioImportEngine.execute({
    batch: { ...input.batch, approvalStatus: "Approved" },
    review: input.review,
    actor: input.actor,
    stagingRecords: input.stagingRecords,
    existingBatchIds: [input.batch.batchId],
  });
  const blockedAssignment = PortfolioAssignmentEngine.assign({
    tenantId: input.batch.tenantId,
    clientId: input.batch.clientId,
    portfolioId: input.batch.portfolioId,
    correlationId: input.batch.correlationId,
    assignedBy: input.actor.actorName,
    importResult: input.importResult,
    validation: input.validation,
    mapping: input.mapping,
    review: input.review,
  });
  const blockedActivation = PortfolioActivationEngine.execute({
    action: "Portfolio.Activate",
    actor: input.actor,
    validation: input.validation,
    mapping: input.mapping,
    review: input.review,
    importResult: input.importResult,
    assignmentResult: blockedAssignment,
  });
  return [
    {
      scenario: "Validation failure",
      status: failedValidation.validationSummary.importReadiness === "Blocked" ? "Passed" : "Failed",
      blockedAt: "Validation",
      eventTypes: ["ValidationErrorDetected", "ImportFailed"],
      detail: `Validation readiness: ${failedValidation.validationSummary.importReadiness}.`,
    },
    {
      scenario: "Mapping failure",
      status: failedMapping.readiness === "Blocked" ? "Passed" : "Failed",
      blockedAt: "Mapping",
      eventTypes: ["MappingErrorDetected"],
      detail: `Mapping readiness: ${failedMapping.readiness}.`,
    },
    {
      scenario: "Approval rejection",
      status: rejectedReview.reviewSummary.importReadiness === "Blocked" && rejectedReview.approvalRecord?.approvalStatus === "Rejected" ? "Passed" : "Failed",
      blockedAt: "Approval",
      eventTypes: ["ImportRejected"],
      detail: `Approval status: ${rejectedReview.approvalRecord?.approvalStatus || "Missing"}.`,
    },
    {
      scenario: "Import failure",
      status: failedImport.execution.status === "Failed" ? "Passed" : "Failed",
      blockedAt: "Import",
      eventTypes: ["ImportFailed"],
      detail: `Import status: ${failedImport.execution.status}.`,
    },
    {
      scenario: "Assignment blocked",
      status: blockedAssignment.assignment.status === "Blocked" ? "Passed" : "Failed",
      blockedAt: "Assignment",
      eventTypes: ["PortfolioActivationBlocked"],
      detail: `Assignment status: ${blockedAssignment.assignment.status}.`,
    },
    {
      scenario: "Activation blocked",
      status: blockedActivation.activation.status === "Activation Failed" ? "Passed" : "Failed",
      blockedAt: "Activation",
      eventTypes: ["PortfolioActivationFailed"],
      detail: `Activation status: ${blockedActivation.activation.status}.`,
    },
  ];
}

function createActivationValidationResult(
  portfolioId: string,
  severity: PortfolioOperationalActivationValidationResult["severity"],
  category: string,
  message: string,
): PortfolioOperationalActivationValidationResult {
  return {
    issueId: createWorkflowId("activation-issue", portfolioId, category.replace(/\s+/g, "-")),
    severity,
    category,
    message,
  };
}

function buildPortfolioActivationWorkflowEvents(
  input: {
    batch: PortfolioIntakeBatch;
    actor: ARWorkflowActorContext;
    portfolioName: string;
    sourceWorkspace?: string;
    initiatedFromRoute?: string;
  },
  activationResult: PortfolioActivationEngineResult,
) {
  const resultEvent: ARWorkflowPortfolioIntakeEventType = activationResult.activation.status === "Activated"
    ? "PortfolioActivated"
    : activationResult.activation.status === "Archived"
      ? "PortfolioArchived"
      : activationResult.action === "Portfolio.Deactivate" ? "PortfolioDeactivated" : "PortfolioActivationFailed";
  const eventTypes: ARWorkflowPortfolioIntakeEventType[] = ["PortfolioActivationRequested", "PortfolioActivationValidated", resultEvent];
  return eventTypes.flatMap((eventType) =>
    buildPortfolioIntakeWorkflowEvents({
      tenantId: input.batch.tenantId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      batchId: input.batch.batchId,
      portfolioName: input.portfolioName,
      actor: input.actor,
      eventType,
      batch: input.batch,
      activationRecord: activationResult.activation,
      activationSummary: activationResult.activationSummary,
      sourceWorkspace: input.sourceWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    }),
  );
}

function buildPortfolioDuplicateKeys(records: PortfolioStagingRecord[], recordType: PortfolioStagingRecord["recordType"], fieldNames: string[]) {
  return records
    .filter((record) => record.recordType === recordType)
    .map((record) => {
      const data = { ...record.rawData, ...record.mappedData };
      return getPortfolioFieldValue(data, ...fieldNames);
    })
    .filter(Boolean)
    .map((value) => value.toLowerCase());
}

function findDuplicatePortfolioKeys(keys: string[], existingKeys: string[] = []) {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  const existing = new Set(existingKeys.map((key) => key.toLowerCase()));
  keys.forEach((key) => {
    if (seen.has(key) || existing.has(key)) duplicates.add(key);
    seen.add(key);
  });
  return Array.from(duplicates);
}

function countDuplicatePortfolioKeys(keys: string[], existingKeys: string[] = []) {
  return findDuplicatePortfolioKeys(keys, existingKeys).length;
}

function buildPortfolioImportWorkflowEvents(
  input: {
    batch: PortfolioIntakeBatch;
    review: PortfolioReviewEngineResult;
    actor: ARWorkflowActorContext;
    stagingRecords?: PortfolioStagingRecord[];
    sourceWorkspace?: string;
    initiatedFromRoute?: string;
  },
  importResult: PortfolioImportEngineResult,
) {
  const completedOrFailedEvent: ARWorkflowPortfolioIntakeEventType = importResult.execution.status === "Completed" ? "ImportCompleted" : "ImportFailed";
  const activationEvent: ARWorkflowPortfolioIntakeEventType | null = importResult.execution.activationStatus === "Activated" ? "PortfolioActivated" : null;
  const eventTypes: ARWorkflowPortfolioIntakeEventType[] = ["ImportExecuting", completedOrFailedEvent, ...(activationEvent ? [activationEvent] : [])];
  return eventTypes.flatMap((eventType) =>
    buildPortfolioIntakeWorkflowEvents({
      tenantId: input.batch.tenantId,
      clientId: input.batch.clientId,
      portfolioId: input.batch.portfolioId,
      batchId: input.batch.batchId,
      portfolioName: input.batch.portfolioName,
      actor: input.actor,
      eventType,
      batch: { ...input.batch, status: eventType === "ImportExecuting" ? "Importing" : importResult.execution.status === "Completed" ? "Completed" : "Failed" },
      stagingRecords: input.stagingRecords,
      approvalRecord: input.review.approvalRecord,
      reviewSummary: input.review.reviewSummary,
      importExecution: importResult.execution,
      importHistory: importResult.history,
      duplicateSummary: importResult.duplicateSummary,
      rollbackMetadata: importResult.rollbackMetadata,
      sourceWorkspace: input.sourceWorkspace,
      initiatedFromRoute: input.initiatedFromRoute,
    }),
  );
}

function validatePortfolioStagingRecord(record: PortfolioStagingRecord, knownCollectors: string[], knownManagers: string[]): PortfolioValidationResult[] {
  const data = { ...record.rawData, ...record.mappedData };
  const results: PortfolioValidationResult[] = [];
  const customerName = getPortfolioFieldValue(data, "customerName", "customer", "Customer Name");
  const accountNumber = getPortfolioFieldValue(data, "accountNumber", "account", "Account Number");
  const invoiceNumber = getPortfolioFieldValue(data, "invoiceNumber", "invoice", "Invoice Number");
  const balance = getPortfolioFieldValue(data, "balance", "currentBalance", "openBalance", "Current Balance");
  const invoiceDate = getPortfolioFieldValue(data, "invoiceDate", "Invoice Date");
  const dueDate = getPortfolioFieldValue(data, "dueDate", "Due Date");
  const agingBucket = getPortfolioFieldValue(data, "agingBucket", "aging", "Aging Bucket");
  const status = getPortfolioFieldValue(data, "status", "Status");
  const email = getPortfolioFieldValue(data, "email", "contactEmail", "Email");
  const phone = getPortfolioFieldValue(data, "phone", "contactPhone", "Phone");
  const collector = getPortfolioFieldValue(data, "collector", "assignedCollector", "Assigned Collector");
  const manager = getPortfolioFieldValue(data, "manager", "assignedManager", "Assigned Manager");
  const portfolioName = getPortfolioFieldValue(data, "portfolioName", "portfolio", "Portfolio");

  if (!customerName && (record.recordType === "Customer" || record.recordType === "Account" || record.recordType === "Invoice")) {
    results.push(createPortfolioValidationResult(record, "Error", "Missing customer names", "Customer name is required for staged customer, account, and invoice records.", "customerName"));
  }
  if (!accountNumber && (record.recordType === "Account" || record.recordType === "Invoice")) {
    results.push(createPortfolioValidationResult(record, "Error", "Missing account numbers", "Account number is required for staged account and invoice records.", "accountNumber"));
  }
  if (!invoiceNumber && record.recordType === "Invoice") {
    results.push(createPortfolioValidationResult(record, "Error", "Required fields", "Invoice number is required for staged invoice records.", "invoiceNumber"));
  }
  if (balance && !isFiniteNumber(balance)) {
    results.push(createPortfolioValidationResult(record, "Error", "Invalid balances", "Balance must be a valid number.", "balance", balance));
  }
  if (balance && isFiniteNumber(balance) && Number(balance.replace(/[$,]/g, "")) < 0) {
    results.push(createPortfolioValidationResult(record, "Error", "Negative balances", "Balance cannot be negative.", "balance", balance));
  }
  if (invoiceDate && !isValidDateValue(invoiceDate)) {
    results.push(createPortfolioValidationResult(record, "Error", "Invalid dates", "Invoice date must be valid.", "invoiceDate", invoiceDate));
  }
  if (dueDate && !isValidDateValue(dueDate)) {
    results.push(createPortfolioValidationResult(record, "Error", "Invalid dates", "Due date must be valid.", "dueDate", dueDate));
  }
  if (agingBucket && !isSupportedAgingBucket(agingBucket)) {
    results.push(createPortfolioValidationResult(record, "Warning", "Invalid aging buckets", "Aging bucket is not recognized.", "agingBucket", agingBucket));
  }
  if (status && !isSupportedPortfolioStatus(status)) {
    results.push(createPortfolioValidationResult(record, "Warning", "Unsupported statuses", "Status is not currently supported by the intake foundation.", "status", status));
  }
  if (email && !isValidEmailValue(email)) {
    results.push(createPortfolioValidationResult(record, "Warning", "Malformed email", "Email address appears malformed.", "email", email));
  }
  if (phone && !isValidPhoneValue(phone)) {
    results.push(createPortfolioValidationResult(record, "Information", "Malformed phone", "Phone number should be reviewed.", "phone", phone));
  }
  if (record.recordType === "Invoice" && !accountNumber) {
    results.push(createPortfolioValidationResult(record, "Error", "Orphaned invoices", "Invoice cannot be validated without a related account number.", "accountNumber"));
  }
  if (record.recordType === "Account" && !customerName) {
    results.push(createPortfolioValidationResult(record, "Error", "Orphaned accounts", "Account cannot be validated without a related customer.", "customerName"));
  }
  if (collector && knownCollectors.length > 0 && !knownCollectors.some((name) => matchesText(name, collector))) {
    results.push(createPortfolioValidationResult(record, "Warning", "Unknown collectors", "Assigned collector was not found in the known collector list.", "collector", collector));
  }
  if (manager && knownManagers.length > 0 && !knownManagers.some((name) => matchesText(name, manager))) {
    results.push(createPortfolioValidationResult(record, "Warning", "Unknown managers", "Assigned manager was not found in the known manager list.", "manager", manager));
  }
  if (record.recordType === "Portfolio Metadata" && !portfolioName) {
    results.push(createPortfolioValidationResult(record, "Warning", "Invalid portfolio metadata", "Portfolio metadata should include a portfolio name.", "portfolioName"));
  }
  if (Object.keys(data).length === 0) {
    results.push(createPortfolioValidationResult(record, "Error", "Required fields", "Staged record has no mapped or raw data."));
  }
  if (results.length === 0) {
    results.push(createPortfolioValidationResult(record, "Information", "Required fields", "Record passed foundation validation."));
  }
  return results;
}

function buildDuplicateResults(records: PortfolioStagingRecord[], recordType: PortfolioStagingRecord["recordType"], fieldName: string, category: PortfolioValidationCategory): PortfolioValidationResult[] {
  const recordsByValue = new Map<string, PortfolioStagingRecord[]>();
  records
    .filter((record) => record.recordType === recordType)
    .forEach((record) => {
      const value = normalizeDuplicateKey(getPortfolioFieldValue({ ...record.rawData, ...record.mappedData }, fieldName));
      if (!value) return;
      recordsByValue.set(value, [...(recordsByValue.get(value) || []), record]);
    });
  return Array.from(recordsByValue.values())
    .filter((duplicates) => duplicates.length > 1)
    .flatMap((duplicates) =>
      duplicates.map((record) => createPortfolioValidationResult(record, "Error", category, `${fieldName} appears more than once in the staged portfolio.`, fieldName)),
    );
}

function buildValidationSignalEvents(
  batch: PortfolioIntakeBatch,
  stagingRecords: PortfolioStagingRecord[],
  validationResults: PortfolioValidationResult[],
  actor: ARWorkflowActorContext,
  sourceWorkspace?: string,
  initiatedFromRoute?: string,
) {
  const warningResults = validationResults.filter((result) => result.severity === "Warning");
  const errorResults = validationResults.filter((result) => result.severity === "Error");
  return [
    ...(warningResults.length > 0 ? buildPortfolioIntakeWorkflowEvents({
      tenantId: batch.tenantId,
      clientId: batch.clientId,
      portfolioId: batch.portfolioId,
      batchId: batch.batchId,
      portfolioName: batch.portfolioName,
      actor,
      eventType: "ValidationWarningDetected",
      batch,
      stagingRecords,
      validationResults: warningResults,
      sourceWorkspace,
      initiatedFromRoute,
    }) : []),
    ...(errorResults.length > 0 ? buildPortfolioIntakeWorkflowEvents({
      tenantId: batch.tenantId,
      clientId: batch.clientId,
      portfolioId: batch.portfolioId,
      batchId: batch.batchId,
      portfolioName: batch.portfolioName,
      actor,
      eventType: "ValidationErrorDetected",
      batch,
      stagingRecords,
      validationResults: errorResults,
      sourceWorkspace,
      initiatedFromRoute,
    }) : []),
  ];
}

function createPortfolioValidationResult(
  record: PortfolioStagingRecord,
  severity: PortfolioValidationSeverity,
  category: PortfolioValidationCategory,
  message: string,
  fieldName?: string,
  sourceValue?: unknown,
): PortfolioValidationResult {
  return {
    validationId: createWorkflowId("validation", record.stagingRecordId, category.replace(/\s+/g, "-")),
    batchId: record.batchId,
    tenantId: record.tenantId,
    clientId: record.clientId,
    portfolioId: record.portfolioId,
    severity,
    category,
    message,
    rowNumber: record.rowNumber,
    fieldName,
    sourceValue,
    createdAt: new Date().toISOString(),
  };
}

function getPortfolioFieldValue(data: Record<string, unknown>, ...fieldNames: string[]) {
  for (const fieldName of fieldNames) {
    const value = data[fieldName];
    if (value !== undefined && value !== null && String(value).trim()) return String(value).trim();
  }
  return "";
}

function normalizeDuplicateKey(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function isFiniteNumber(value: string) {
  return Number.isFinite(Number(value.replace(/[$,]/g, "")));
}

function isValidDateValue(value: string) {
  return !Number.isNaN(Date.parse(value));
}

function isValidEmailValue(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isValidPhoneValue(value: string) {
  return value.replace(/\D/g, "").length >= 10;
}

function isSupportedAgingBucket(value: string) {
  const normalized = value.trim().toLowerCase();
  return ["current", "0-30", "31-60", "61-90", "91+", "91+ days", "over 90", "120+"].includes(normalized);
}

function isSupportedPortfolioStatus(value: string) {
  const normalized = value.trim().toLowerCase();
  return ["active", "open", "current", "past due", "delinquent", "disputed", "closed", "paid", "payment plan"].includes(normalized);
}

function toPortfolioIntakeBatch(value: unknown): PortfolioIntakeBatch | null {
  const record = toRecord(value);
  if (!record.batchId || !record.tenantId || !record.correlationId || !record.clientId || !record.portfolioId) return null;
  return {
    batchId: String(record.batchId),
    tenantId: String(record.tenantId),
    correlationId: String(record.correlationId),
    clientId: String(record.clientId),
    portfolioId: String(record.portfolioId),
    portfolioName: String(record.portfolioName || "Untitled Portfolio"),
    sourceType: normalizePortfolioSourceType(record.sourceType),
    sourceFileName: String(record.sourceFileName || "Not Captured"),
    submittedBy: String(record.submittedBy || "Not Captured"),
    submittedAt: String(record.submittedAt || new Date().toISOString()),
    recordCount: normalizeCount(record.recordCount),
    status: normalizePortfolioImportStatus(record.status),
    validationSummary: normalizePortfolioValidationSummary(record.validationSummary),
    mappingProfileId: record.mappingProfileId ? String(record.mappingProfileId) : undefined,
    approvalStatus: normalizePortfolioApprovalStatus(record.approvalStatus),
  };
}

function toPortfolioMappingProfile(value: unknown): PortfolioMappingProfile | null {
  const record = toRecord(value);
  const profileId = record.profileId || record.mappingProfileId;
  if (!profileId || !record.clientId || !record.profileName) return null;
  return {
    profileId: String(profileId),
    mappingProfileId: String(record.mappingProfileId || profileId),
    tenantId: record.tenantId ? String(record.tenantId) : undefined,
    clientId: String(record.clientId),
    profileName: String(record.profileName),
    sourceFormat: normalizePortfolioSourceType(record.sourceFormat),
    sourceColumns: Array.isArray(record.sourceColumns) ? record.sourceColumns.map(String) : [],
    columnMappings: Array.isArray(record.columnMappings) ? record.columnMappings.map(toPortfolioColumnMapping).filter(Boolean) as PortfolioColumnMapping[] : [],
    requiredFieldsMapped: record.requiredFieldsMapped === undefined ? undefined : Boolean(record.requiredFieldsMapped),
    unmappedFields: Array.isArray(record.unmappedFields) ? record.unmappedFields.map(String) : [],
    createdBy: String(record.createdBy || "Not Captured"),
    updatedBy: String(record.updatedBy || record.createdBy || "Not Captured"),
    activeStatus: record.activeStatus === "Inactive" ? "Inactive" : "Active",
    createdAt: String(record.createdAt || new Date().toISOString()),
    updatedAt: String(record.updatedAt || record.createdAt || new Date().toISOString()),
  };
}

function toPortfolioApprovalRecord(value: unknown): PortfolioApprovalRecord | null {
  const record = toRecord(value);
  if (!record.approvalId || !record.batchId || !record.tenantId || !record.clientId || !record.portfolioId) return null;
  return {
    approvalId: String(record.approvalId),
    batchId: String(record.batchId),
    tenantId: String(record.tenantId),
    correlationId: record.correlationId ? String(record.correlationId) : undefined,
    clientId: String(record.clientId),
    portfolioId: String(record.portfolioId),
    approvedBy: record.approvedBy ? String(record.approvedBy) : undefined,
    approvedAt: record.approvedAt ? String(record.approvedAt) : undefined,
    rejectedBy: record.rejectedBy ? String(record.rejectedBy) : undefined,
    rejectedAt: record.rejectedAt ? String(record.rejectedAt) : undefined,
    approvalNotes: record.approvalNotes ? String(record.approvalNotes) : undefined,
    approvalStatus: normalizePortfolioApprovalStatus(record.approvalStatus),
    riskLevel: normalizePortfolioImportRiskLevel(record.riskLevel),
  };
}

function toPortfolioAssignmentPlan(value: unknown): PortfolioAssignmentPlan | null {
  const record = toRecord(value);
  if (!record.assignmentPlanId || !record.batchId || !record.tenantId || !record.clientId || !record.portfolioId) return null;
  return {
    assignmentPlanId: String(record.assignmentPlanId),
    batchId: String(record.batchId),
    tenantId: String(record.tenantId),
    clientId: String(record.clientId),
    portfolioId: String(record.portfolioId),
    collector: record.collector ? String(record.collector) : undefined,
    manager: record.manager ? String(record.manager) : undefined,
    team: record.team ? String(record.team) : undefined,
    priority: record.priority ? String(record.priority) : undefined,
    portfolio: record.portfolio ? String(record.portfolio) : undefined,
    recoveryStage: record.recoveryStage ? String(record.recoveryStage) : undefined,
    workQueueEligibility: Boolean(record.workQueueEligibility),
    createdBy: String(record.createdBy || "Not Captured"),
    createdAt: String(record.createdAt || new Date().toISOString()),
  };
}

function toPortfolioStagingRecords(value: unknown): PortfolioStagingRecord[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const record = toRecord(item);
    if (!record.stagingRecordId || !record.batchId || !record.tenantId || !record.clientId || !record.portfolioId) return null;
    return {
      stagingRecordId: String(record.stagingRecordId),
      batchId: String(record.batchId),
      tenantId: String(record.tenantId),
      clientId: String(record.clientId),
      portfolioId: String(record.portfolioId),
      rowNumber: normalizeCount(record.rowNumber),
      recordType: normalizePortfolioStagingRecordType(record.recordType),
      rawData: toRecord(record.rawData),
      mappedData: toRecord(record.mappedData),
      validationStatus: normalizePortfolioStagingValidationStatus(record.validationStatus),
      createdAt: String(record.createdAt || new Date().toISOString()),
    };
  }).filter(Boolean) as PortfolioStagingRecord[];
}

function toPortfolioValidationResults(value: unknown): PortfolioValidationResult[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const record = toRecord(item);
    if (!record.validationId || !record.batchId || !record.tenantId || !record.clientId || !record.portfolioId) return null;
    return {
      validationId: String(record.validationId),
      batchId: String(record.batchId),
      tenantId: String(record.tenantId),
      clientId: String(record.clientId),
      portfolioId: String(record.portfolioId),
      severity: normalizePortfolioValidationSeverity(record.severity),
      category: normalizePortfolioValidationCategory(record.category),
      message: String(record.message || "Validation result captured."),
      rowNumber: record.rowNumber === undefined ? undefined : normalizeCount(record.rowNumber),
      fieldName: record.fieldName ? String(record.fieldName) : undefined,
      sourceValue: record.sourceValue,
      createdAt: String(record.createdAt || new Date().toISOString()),
    };
  }).filter(Boolean) as PortfolioValidationResult[];
}

function toPortfolioColumnMapping(value: unknown): PortfolioColumnMapping | null {
  const record = toRecord(value);
  if (!record.sourceColumn || !record.targetField) return null;
  return {
    sourceColumn: String(record.sourceColumn),
    targetField: String(record.targetField),
    required: Boolean(record.required),
    transform: record.transform ? String(record.transform) : undefined,
  };
}

function normalizePortfolioSourceType(value: unknown): PortfolioSourceType {
  if (value === "Excel" || value === "CSV" || value === "Future API" || value === "Future ERP Sync" || value === "Future Secure Upload") return value;
  return "Manual";
}

function normalizePortfolioImportStatus(value: unknown): PortfolioImportStatus {
  return PORTFOLIO_IMPORT_STATUS_LIFECYCLE.includes(value as PortfolioImportStatus) ? value as PortfolioImportStatus : "Draft";
}

function normalizePortfolioApprovalStatus(value: unknown): PortfolioApprovalStatus {
  if (value === "Approved" || value === "Rejected") return value;
  return "Pending";
}

function normalizePortfolioImportRiskLevel(value: unknown): PortfolioImportRiskLevel | undefined {
  if (value === "Low" || value === "Medium" || value === "High" || value === "Critical") return value;
  return undefined;
}

function normalizePortfolioValidationSummary(value: unknown): PortfolioValidationSummary {
  const record = toRecord(value);
  const errorCount = normalizeCount(record.errorCount);
  const warningCount = normalizeCount(record.warningCount);
  const importReadiness = record.importReadiness === "Ready" || record.importReadiness === "Ready With Warnings" || record.importReadiness === "Blocked"
    ? record.importReadiness
    : errorCount > 0 ? "Blocked" : warningCount > 0 ? "Ready With Warnings" : "Ready";
  return {
    totalRecords: normalizeCount(record.totalRecords),
    validRecords: normalizeCount(record.validRecords),
    errorCount,
    warningCount,
    informationCount: normalizeCount(record.informationCount),
    duplicateCount: normalizeCount(record.duplicateCount),
    customerCount: normalizeCount(record.customerCount),
    accountCount: normalizeCount(record.accountCount),
    invoiceCount: normalizeCount(record.invoiceCount),
    importReadiness,
    overallValidationStatus: record.overallValidationStatus === "Passed" || record.overallValidationStatus === "Passed With Warnings" || record.overallValidationStatus === "Failed"
      ? record.overallValidationStatus
      : importReadiness === "Blocked" ? "Failed" : importReadiness === "Ready With Warnings" ? "Passed With Warnings" : "Passed",
    isImportReady: record.isImportReady === undefined ? importReadiness !== "Blocked" : Boolean(record.isImportReady),
  };
}

function normalizePortfolioValidationSeverity(value: unknown): PortfolioValidationSeverity {
  if (value === "Warning" || value === "Information") return value;
  return "Error";
}

function normalizePortfolioValidationCategory(value: unknown): PortfolioValidationCategory {
  return PORTFOLIO_VALIDATION_CATEGORIES.includes(value as PortfolioValidationCategory) ? value as PortfolioValidationCategory : "Required fields";
}

function normalizePortfolioStagingRecordType(value: unknown): PortfolioStagingRecord["recordType"] {
  if (value === "Account" || value === "Invoice" || value === "Balance" || value === "Supporting Document" || value === "Portfolio Metadata") return value;
  return "Customer";
}

function normalizePortfolioStagingValidationStatus(value: unknown): PortfolioStagingRecord["validationStatus"] {
  if (value === "Valid" || value === "Warning" || value === "Error") return value;
  return "Not Validated";
}

function normalizeCount(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
}

function resolveCallOutcomeEventType(outcome: string): ARWorkflowCallEventType {
  if (outcome === "Left Voicemail") return "CallVoicemailLeft";
  if (CALL_FAILED_OUTCOMES.has(outcome)) return "CallFailed";
  if (CALL_COMPLETED_OUTCOMES.has(outcome)) return "CallCompleted";
  return "CallOutcomeUpdated";
}

function resolveNoteCreatedEventType(input: ARWorkflowNoteEventInput): ARWorkflowNoteEventType {
  if (input.noteType === "Customer Note" || input.visibility === "client_visible") return "CustomerNoteCreated";
  if (input.noteType === "Internal Note" || input.noteType === "Escalation Note" || input.visibility === "manager_only") return "ManagerNoteCreated";
  return "CollectorNoteCreated";
}

function shouldRouteNoteToManager(input: ARWorkflowNoteEventInput) {
  return input.noteType === "Internal Note" || input.noteType === "Escalation Note" || input.visibility === "manager_only";
}

function buildSpecializedNoteTitle(eventType: ARWorkflowNoteEventType, input: ARWorkflowNoteEventInput) {
  if (eventType === "CustomerNoteCreated") return `Customer-visible note created for ${input.customerName}`;
  if (eventType === "ManagerNoteCreated") return `Manager note created for ${input.customerName}`;
  return `Collector note created for ${input.customerName}`;
}

function buildNoteSummary(input: ARWorkflowNoteEventInput, verb: "created" | "updated") {
  const outcome = input.outcome ? ` Outcome: ${input.outcome}.` : "";
  const nextAction = input.nextAction ? ` Next action: ${input.nextAction}.` : "";
  return `${input.noteType} ${verb}. Visibility: ${input.visibility}.${outcome}${nextAction}`;
}

function normalizeNoteVisibility(value: unknown): ARWorkflowNoteVisibility {
  if (value === "client_visible" || value === "manager_only" || value === "internal") return value;
  return "internal";
}

function resolvePromiseActionEventType(action: ARWorkflowPromiseAction): ARWorkflowPromiseEventType {
  return AR_PROMISE_WORKFLOW_ACTION_CATALOG[action].primaryEvent;
}

function isPromiseWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowPromiseEventType {
  return [
    "PromiseCreated",
    "PromiseUpdated",
    "PromiseCompleted",
    "PromiseBroken",
    "PromiseCancelled",
    "PromiseEscalated",
    "PromisePaymentReceived",
  ].includes(eventType);
}

function buildPromiseEventTitle(eventType: ARWorkflowPromiseEventType, customerName: string) {
  if (eventType === "PromiseCreated") return `Promise created for ${customerName}`;
  if (eventType === "PromiseUpdated") return `Promise updated for ${customerName}`;
  if (eventType === "PromiseCompleted") return `Promise completed for ${customerName}`;
  if (eventType === "PromiseBroken") return `Promise broken for ${customerName}`;
  if (eventType === "PromiseCancelled") return `Promise cancelled for ${customerName}`;
  if (eventType === "PromiseEscalated") return `Promise escalated for ${customerName}`;
  return `Promise payment received for ${customerName}`;
}

function buildPromiseSummary(eventType: ARWorkflowPromiseEventType, promiseRecord: Record<string, unknown>) {
  const status = String(promiseRecord.status || "Not Captured");
  const amount = typeof promiseRecord.amountCents === "number" ? promiseRecord.amountCents : "Not Captured";
  const date = String(promiseRecord.promiseDate || "Not Captured");
  return `${eventType}. Status: ${status}. Amount cents: ${amount}. Promise date: ${date}.`;
}

function toRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function resolveTicklerActionEventType(action: ARWorkflowTicklerAction): ARWorkflowTicklerEventType {
  return AR_TICKLER_WORKFLOW_ACTION_CATALOG[action].primaryEvent;
}

function isTicklerWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowTicklerEventType {
  return [
    "TicklerCreated",
    "TicklerUpdated",
    "TicklerCompleted",
    "TicklerRescheduled",
    "TicklerCancelled",
    "TicklerSnoozed",
    "TicklerEscalated",
  ].includes(eventType);
}

function resolveDocumentActionEventType(action: ARWorkflowDocumentAction, deliveryStatus?: ARWorkflowDocumentEventInput["deliveryStatus"]): ARWorkflowDocumentEventType {
  if (action === "Letter.Send" && deliveryStatus === "failed") return "LetterFailed";
  if (action === "Statement.Send" && deliveryStatus === "failed") return "StatementFailed";
  return AR_DOCUMENT_WORKFLOW_ACTION_CATALOG[action].primaryEvent;
}

function isDocumentWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowDocumentEventType {
  return [
    "LetterGenerated",
    "LetterPreviewed",
    "LetterPrinted",
    "LetterDownloaded",
    "LetterSent",
    "LetterDelivered",
    "LetterFailed",
    "LetterArchived",
    "StatementGenerated",
    "StatementPreviewed",
    "StatementPrinted",
    "StatementDownloaded",
    "StatementSent",
    "StatementDelivered",
    "StatementFailed",
    "StatementArchived",
  ].includes(eventType);
}

function resolveEscalationActionEventType(action: ARWorkflowEscalationAction): ARWorkflowEscalationEventType {
  return AR_ESCALATION_WORKFLOW_ACTION_CATALOG[action].primaryEvent;
}

function isEscalationWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowEscalationEventType {
  return [
    "EscalationCreated",
    "EscalationUpdated",
    "EscalationAssigned",
    "EscalationResolved",
    "EscalationClosed",
    "EscalationReopened",
  ].includes(eventType);
}

function buildEscalationEventTitle(eventType: ARWorkflowEscalationEventType, customerName: string) {
  if (eventType === "EscalationCreated") return `Escalation created for ${customerName}`;
  if (eventType === "EscalationUpdated") return `Escalation updated for ${customerName}`;
  if (eventType === "EscalationAssigned") return `Escalation assigned for ${customerName}`;
  if (eventType === "EscalationResolved") return `Escalation resolved for ${customerName}`;
  if (eventType === "EscalationClosed") return `Escalation closed for ${customerName}`;
  return `Escalation reopened for ${customerName}`;
}

function buildEscalationSummary(eventType: ARWorkflowEscalationEventType, escalationRecord: Record<string, unknown>) {
  const status = String(escalationRecord.status || AR_ESCALATION_WORKFLOW_EVENT_CATALOG[eventType].lifecycleStatus);
  const priority = String(escalationRecord.priority || "Manager Review");
  const reason = String(escalationRecord.reason || "Not Captured");
  const assignedTo = String(escalationRecord.assignedTo || "Manager Command");
  return `${eventType}. Status: ${status}. Priority: ${priority}. Reason: ${reason}. Assigned to: ${assignedTo}.`;
}

function resolvePaymentActionEventType(action: ARWorkflowPaymentAction): ARWorkflowPaymentEventType {
  return AR_PAYMENT_WORKFLOW_ACTION_CATALOG[action].primaryEvent;
}

function isPaymentWorkflowEventType(eventType: ARWorkflowEventType): eventType is ARWorkflowPaymentEventType {
  return [
    "PaymentRecorded",
    "PaymentUpdated",
    "PaymentVoided",
    "PaymentAppliedToPromise",
    "PaymentAppliedToAccount",
  ].includes(eventType);
}

function buildPaymentEventTitle(eventType: ARWorkflowPaymentEventType, customerName: string) {
  if (eventType === "PaymentRecorded") return `Payment recorded for ${customerName}`;
  if (eventType === "PaymentUpdated") return `Payment updated for ${customerName}`;
  if (eventType === "PaymentVoided") return `Payment voided for ${customerName}`;
  if (eventType === "PaymentAppliedToPromise") return `Payment applied to promise for ${customerName}`;
  return `Payment applied to account for ${customerName}`;
}

function buildPaymentSummary(eventType: ARWorkflowPaymentEventType, paymentRecord: Record<string, unknown>) {
  const status = String(paymentRecord.status || AR_PAYMENT_WORKFLOW_EVENT_CATALOG[eventType].lifecycleStatus);
  const amountCents = normalizePaymentAmountCents(paymentRecord.amountCents);
  const method = String(paymentRecord.paymentMethod || "Not Captured");
  const scope = String(paymentRecord.applicationScope || "Not Applied");
  return `${eventType}. Status: ${status}. Amount cents: ${amountCents}. Method: ${method}. Application: ${scope}.`;
}

function normalizePaymentAmountCents(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return Math.max(0, Math.round(value));
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
}

function buildDocumentEventTitle(eventType: ARWorkflowDocumentEventType, customerName: string) {
  if (eventType === "LetterGenerated") return `Letter generated for ${customerName}`;
  if (eventType === "LetterPreviewed") return `Letter previewed for ${customerName}`;
  if (eventType === "LetterPrinted") return `Letter printed for ${customerName}`;
  if (eventType === "LetterDownloaded") return `Letter downloaded for ${customerName}`;
  if (eventType === "LetterSent") return `Letter sent for ${customerName}`;
  if (eventType === "LetterDelivered") return `Letter delivered for ${customerName}`;
  if (eventType === "LetterFailed") return `Letter delivery failed for ${customerName}`;
  if (eventType === "LetterArchived") return `Letter archived for ${customerName}`;
  if (eventType === "StatementGenerated") return `Statement generated for ${customerName}`;
  if (eventType === "StatementPreviewed") return `Statement previewed for ${customerName}`;
  if (eventType === "StatementPrinted") return `Statement printed for ${customerName}`;
  if (eventType === "StatementDownloaded") return `Statement downloaded for ${customerName}`;
  if (eventType === "StatementSent") return `Statement sent for ${customerName}`;
  if (eventType === "StatementDelivered") return `Statement delivered for ${customerName}`;
  if (eventType === "StatementFailed") return `Statement delivery failed for ${customerName}`;
  return `Statement archived for ${customerName}`;
}

function buildDocumentSummary(eventType: ARWorkflowDocumentEventType, documentRecord: Record<string, unknown>) {
  const template = String(documentRecord.template || "Not Captured");
  const status = String(documentRecord.status || "Not Captured");
  const deliveryStatus = String(documentRecord.deliveryStatus || "Not Captured");
  const recipient = String(documentRecord.recipient || documentRecord.sentRecipient || "Not Captured");
  return `${eventType}. Template: ${template}. Status: ${status}. Delivery: ${deliveryStatus}. Recipient: ${recipient}.`;
}

function buildTicklerEventTitle(eventType: ARWorkflowTicklerEventType, customerName: string) {
  if (eventType === "TicklerCreated") return `Tickler created for ${customerName}`;
  if (eventType === "TicklerUpdated") return `Tickler updated for ${customerName}`;
  if (eventType === "TicklerCompleted") return `Tickler completed for ${customerName}`;
  if (eventType === "TicklerRescheduled") return `Tickler rescheduled for ${customerName}`;
  if (eventType === "TicklerCancelled") return `Tickler cancelled for ${customerName}`;
  if (eventType === "TicklerSnoozed") return `Tickler snoozed for ${customerName}`;
  return `Tickler escalated for ${customerName}`;
}

function buildTicklerSummary(eventType: ARWorkflowTicklerEventType, ticklerRecord: Record<string, unknown>) {
  const status = String(ticklerRecord.status || "Not Captured");
  const dueDate = String(ticklerRecord.dueDate || "Not Captured");
  const priority = String(ticklerRecord.priority || "Not Captured");
  return `${eventType}. Status: ${status}. Due date: ${dueDate}. Priority: ${priority}.`;
}

function buildCallOutcomeTitle(eventType: ARWorkflowCallEventType, customerName: string) {
  if (eventType === "CallVoicemailLeft") return `Voicemail left for ${customerName}`;
  if (eventType === "CallFailed") return `Call failed for ${customerName}`;
  if (eventType === "CallOutcomeUpdated") return `Call outcome updated for ${customerName}`;
  return `Call completed for ${customerName}`;
}

function buildCallOutcomeSummary(input: ARWorkflowCallEventInput) {
  const duration = input.durationMinutes ? `${input.durationMinutes} min` : "duration not captured";
  return `Outcome: ${input.outcome}. Duration: ${duration}. Next action: ${input.nextAction}.`;
}

function createWorkflowId(prefix: string, accountId: string, eventType = "") {
  const parts = [prefix, accountId, eventType, Date.now().toString(36), Math.random().toString(36).slice(2, 8)].filter(Boolean);
  return parts.join("-");
}

function normalizeWorkflowEventContract(event: ARWorkflowEvent): ARWorkflowEvent {
  const entity = event.entity || deriveWorkflowEventEntity(event);
  const metadata = {
    ...event.payload.metadata,
    ...event.metadata,
    entityType: entity.entityType,
    entityId: entity.entityId,
    sourceWorkspace: event.workspace.sourceWorkspace,
    ownerWorkspace: event.workspace.ownerWorkspace,
    clientId: event.scope.clientId,
    customerId: event.scope.customerId,
    accountId: event.scope.accountId,
    promiseId: event.scope.promiseId,
    ticklerId: event.scope.ticklerId,
    letterId: event.scope.letterId,
    statementId: event.scope.statementId,
    paymentId: event.scope.paymentId,
    escalationId: event.scope.escalationId,
    documentRequestId: event.scope.documentRequestId,
    managerReviewId: event.scope.managerReviewId,
  };

  return {
    ...event,
    entity,
    metadata,
    payload: {
      ...event.payload,
      metadata,
    },
  };
}

function deriveWorkflowEventEntity(event: ARWorkflowEvent): ARWorkflowEntityReference {
  const metadata = event.payload.metadata || {};
  const metadataEntityId = String(metadata.entityId || "");
  const fallbackId = metadataEntityId
    || String(metadata.promiseId || metadata.ticklerId || metadata.documentId || metadata.escalationId || metadata.paymentId || metadata.reportId || metadata.batchId || "")
    || event.scope.accountId
    || event.scope.customerId
    || event.scope.clientId
    || event.correlationId
    || event.id;

  switch (event.eventCategory) {
    case "promise":
      return { entityType: "promise", entityId: fallbackId };
    case "tickler":
      return { entityType: "tickler", entityId: fallbackId };
    case "document":
      return {
        entityType: metadata.documentType === "statement" ? "statement" : metadata.documentType === "letter" ? "letter" : "document",
        entityId: fallbackId,
      };
    case "document_request":
      return { entityType: "document_request", entityId: fallbackId };
    case "communication":
      return { entityType: "communication", entityId: fallbackId };
    case "task":
      return { entityType: "task", entityId: fallbackId };
    case "manager":
      return { entityType: "manager_review", entityId: fallbackId };
    case "escalation":
      return { entityType: "escalation", entityId: fallbackId };
    case "payment":
      return { entityType: "payment", entityId: fallbackId };
    case "reporting":
      return { entityType: "report", entityId: fallbackId };
    case "client_exchange":
      if (event.eventType === "PortfolioAssigned" || event.eventType === "PortfolioAssignmentUpdated" || event.eventType === "PortfolioAssignmentRemoved") {
        return { entityType: "portfolio_assignment", entityId: fallbackId };
      }
      if (String(event.eventType).includes("Activation") || event.eventType === "PortfolioActivated" || event.eventType === "PortfolioArchived") {
        return { entityType: "portfolio_activation", entityId: fallbackId };
      }
      return { entityType: "import_batch", entityId: fallbackId };
    case "call":
    case "note":
    default:
      return { entityType: "account", entityId: fallbackId };
  }
}

function isARWorkflowEvent(value: unknown): value is ARWorkflowEvent {
  if (!value || typeof value !== "object") return false;
  const event = value as Partial<ARWorkflowEvent>;
  return Boolean(
    event.id
      && event.tenantId
      && event.correlationId
      && event.eventType
      && event.eventFamily
      && event.eventCategory
      && event.actor
      && event.occurredAt
      && event.workspace
      && event.scope
      && event.payload
      && event.routing
      && event.visibility,
  );
}
